// sessions-active.js — ACTIVE sessions only. Upload this every new session.
// Imported by sessions.js thin aggregator. Never import directly in components.
// Last updated: June 7, 2026 — SECTOR-MAPPING-MODULAR complete · 5-file modular split · F1-F11 fixed · DDL+deploy pending
// Sequence rationale (CONFIRMED May 2026 — agreed with owner):
//   OCI-STORAGE-FOUNDATION: DONE ✅ May 15, 2026 — ATP-2 + OCI NoSQL + Object Storage provisioned
//   OCI-5B-SENATE: easy win · Senate HTML · proves VM-PIPELINE pattern · SAFETY_REVERSAL signal
//   OCI-5B-HOUSE:  easy win · House ZIP · cross-chamber amplifier enabled
//   REFACTOR-EDGAR: two independent pipelines (display + signal) · complex but infra proven
//   OCI-5A: alert service — after all three pipeline sessions confirmed healthy
//   OCI-OUTCOME-DETECTION: nightly outcome labelling — L6 training corpus
//   OCI-5C+: remaining data sources (each independent pipeline)
//   OCI-5H: screener aggregation layer — joins all OCI-5x into screener_metrics in ATP-1
//   OCI-6 (screener UI) after OCI-5H — reads pre-computed screener_metrics
//   OCI-8 (VM-2/D3): VM-2 on private subnet FROM DAY ONE — not after OCI-7
//
// KEY ARCHITECTURAL DECISIONS (permanent — see PRODUCT_STRATEGY.md):
//   Independent pipelines: no shared pipelineEngine.js. Each pipeline self-contained (R34).
//   Three EDGAR consumers: display (OCI NoSQL) · D3 signal (signalWriter+ATP-2) · AI corpus (ATP-2)
//   Five-tier storage: Redis > ATP-1 > OCI NoSQL > ATP-2 > Object Storage > Block (staging only)
//   Broker adapter: brokerAdapter.placeOrder() — never hardcode IBKR directly (R33)
//   ATP password rotation: after OCI-5A (all pipelines confirmed), NOT before each session

export const ACTIVE_SESSIONS = [

  // ─── DONE ─────────────────────────────────────────────────────────────────

  {
    id: 'S27-UX',
    sessionNumber: 'S27-UX',
    name: 'Sessions Tab UX — Collapsible Phases + Done Sub-Groups',
    status: 'done',
    phase: 'Phase 2 — Signal Collection',
    date: 'April 2026',
    commit: '6f091e7',
    items: [
      'Phase headers collapsible — click to collapse entire phase',
      'Done sub-group collapsed by default within each phase',
      'Expand All / Collapse All controls',
      'Phase B+ normalised into Phase B',
    ],
    prereqs: ['S27-ARCH done ✅'],
    doD: ['Sessions tab renders collapsible phases', 'CI=true npm run build clean'],
    smokeChecks: [
      'grep -q "collapsedPhases" src/components/roadmap/SessionsTab.jsx',
      'grep -q "expandedDone" src/components/roadmap/SessionsTab.jsx',
      'grep -q "normalizePhase" src/components/roadmap/SessionsTab.jsx',
    ],
    files: ['src/components/roadmap/SessionsTab.jsx'],
    vendorDeps: [],
  },

  // ─── OCI BUILD SEQUENCE ────────────────────────────────────────────────────

  {
    id: 'oci-1-network',
    sessionNumber: 'OCI-1',
    name: 'OCI-1: VCN + Network Foundation',
    status: 'done',
    phase: 'Phase 3 — OCI Infrastructure',
    date: 'April 2026',
    commit: '128da82',
    items: [
      'Compartment structure: 6 compartments',
      'VCN mp-vcn + subnets + DNS · ATP · VM-1 · Cloud Guard · IAM',
      'NAT Gateway 129.80.17.209 reserved · Service Gateway · Budget $25/mo',
    ],
    prereqs: ['OCI PAYG contract SIGNED ✅'],
    doD: ['All OCI-1 resources confirmed ✅'],
    smokeChecks: [],
    files: ['.claude/project-knowledge/OCI_CONFIG.md'],
    vendorDeps: [
      { name: 'OCI PAYG', plan: 'PAYG', status: 'ACTIVE ✅ — Contract 1224179463.a1' },
    ],
  },

  {
    id: 'oci-2-app-stack',
    sessionNumber: 'OCI-2',
    name: 'OCI-2: VM-1 App Stack — Nginx + Node.js + Redis + Vault + Primary Cutover',
    status: 'done',
    phase: 'Phase 3 — OCI Infrastructure',
    date: 'April 28, 2026',
    commit: '0bec84b',
    items: [
      'Node.js 20 LTS + PM2 · Redis AOF · Nginx + SSL api.marketpulze.com',
      'mp-vault-backend: 28 secrets · AES-256 HSM · instance principal',
      'load-secrets.sh · ecosystem.config.js · GitHub deploy key',
      'App deployed from main · MongoDB connected · crons running',
      'Shadow deploy verified · Cloudflare cutover · VM-1 PRIMARY ✅',
      'Railway → cold standby · 2-vault architecture decided',
    ],
    prereqs: ['OCI-1 complete ✅'],
    doD: ['VM-1 PRIMARY · api.marketpulze.com live · Rule 25 passed ✅'],
    smokeChecks: [],
    files: ['.claude/project-knowledge/OCI_CONFIG.md'],
    vendorDeps: [],
  },

  {
    id: 'oci-3-signalwriter',
    sessionNumber: 'OCI-3',
    name: 'OCI-3: signalWriter + GitHub Actions + Bastion + Gateway + Route Tables',
    status: 'done',
    phase: 'Phase 3 — OCI Infrastructure',
    date: 'April 30, 2026',
    commit: '6f07edf',
    items: [
      '── TASK 1 (OCI-3 session) ──────────────────────────────────────',
      'signalWriter.js (R28): Redis primary + ATP async archive',
      'All 5 D3 signal types: drawing · chart_interaction · screener_signal · signal_log · calculator',
      'signalWriter: redis: true ✅ · atp: false (BL-ATP-1 — deferred OCI-4)',
      '── TASK 2 (OCI-3B session) ─────────────────────────────────────',
      'GitHub Actions deploy pipeline: git push main → SSH → VM-1 → pm2 reload (27s)',
      'ATP wallet → /etc/oci/wallets/atp/ · permissions 600',
      'mp-atpmarketpulze-db-key (HSM AES-256) in mp-databases',
      'mp-atpmarketpulze-passwords vault: 3 secrets (admin pw + wallet pw + mongodb uri)',
      'mp-vm1-databases-vault-policy: VM-1 reads mp-databases vault',
      'load-secrets.sh: ATP_MONGODB_URI + ATP_WALLET_PATH added (31 secrets + 1 path)',
      'start-app.sh: source secrets → exec node (fixes PM2 env var inheritance)',
      'ecosystem.config.js: updated to use start-app.sh wrapper',
      '── OCI CONSOLE (OCI-3B session) ────────────────────────────────',
      'IGW ig-quick-action-mpIGW moved to mp-network compartment',
      'mpbastion created in mp-network · mp-public-subnet · TTL 3hr',
      'mp-developers-policy created: 5 least-privilege statements (no Vault, no ATP)',
      'mp-private-route-table: NAT GW + Service GW rules',
      'mp-private-subnet: associated with mp-private-route-table',
    ],
    prereqs: ['OCI-2 complete ✅'],
    doD: [
      'signalWriter redis: true ✅',
      'GitHub Actions deploy pipeline green ✅ (27s)',
      'ATP wallet on VM-1 ✅',
      'Bastion active ✅',
      'Private subnet route table wired ✅',
    ],
    smokeChecks: [
      'test -f backend/services/signalWriter.js',
      'test -f .github/workflows/deploy.yml',
      'grep -q "signalWriter" backend/services/signalWriter.js',
    ],
    files: [
      'backend/services/signalWriter.js',
      '.github/workflows/deploy.yml',
      '.claude/project-knowledge/OCI_CONFIG.md',
    ],
    vendorDeps: [],
  },

  {
    id: 'mini-backlog-oci3b',
    sessionNumber: 'MINI-BL',
    name: 'Mini Backlog: BL-OCI3-02/03/05/06/07/08 — All closed',
    status: 'done',
    phase: 'Phase 3 — OCI Infrastructure',
    date: 'May 1, 2026',
    commit: '6f07edf',
    items: [
      'BL-OCI3-02 ✅ Advanced Chart button restored in Technicals tab',
      'BL-OCI3-03 ✅ Guest signal_log via signalWriter — D3 L1 crowd demand collecting',
      'BL-OCI3-05 ✅ Duplicate Mongoose indexes removed (ScreenerSignalLog + FundamentalsCache)',
      'BL-OCI3-06 ✅ yahoo-finance2 installed on VM-1 — startup warning gone',
      'BL-OCI3-07 ✅ Yahoo crumb fallback removed — 401 errors gone permanently',
      'BL-OCI3-08 ✅ Not a bug — /analyze requires auth by design, closed',
    ],
    prereqs: ['OCI-3B complete ✅'],
    doD: ['All 6 backlog items closed', 'CI=true npm run build clean', 'GitHub Actions green'],
    smokeChecks: [],
    files: [
      'src/pages/StockPage.jsx',
      'backend/routes/stocks.js',
      'backend/models/ScreenerSignalLog.js',
      'backend/models/FundamentalsCache.js',
      'backend/services/yahoo.js',
    ],
    vendorDeps: [],
  },

  {
    id: 'oci-4-data',
    sessionNumber: 'OCI-4',
    name: 'OCI-4: BL-ATP-1 Fix + Stooq Pipelines + ATP Dual-Write',
    status: 'done',
    phase: 'Phase 3 — OCI Infrastructure',
    date: 'May 1, 2026',
    commit: '559b8ca',
    items: [
      '── BL-ATP-1: ATP MongoDB API TLS — RESOLVED ─────────────────────',
      'Root cause: VM-1 public IP 129.213.147.105 missing from ATP ACL',
      'Fix: added 129.213.147.105 to ATP ACL · removed old VCN OCID',
      'signalWriter: atp: true ✅ — full Redis + ATP dual-write live',
      '── STOOQ BULK LOAD ─────────────────────────────────────────────',
      'stooqBulkLoad.js: streams ZIP · batch upsert · backpressure fix',
      'ATP collections: stooq_us_daily + stooq_macro_daily (admin schema)',
      'Bulk load: US daily (510MB) + macro (946KB) → ATP May 1 2026 ✅',
      '── STOOQ DAILY CRON ────────────────────────────────────────────',
      'stooqPipeline.js: 6:30pm ET Mon-Fri (R27 TZ=America/New_York)',
      'R29: internal D3 backtesting only — zero member endpoints',
    ],
    prereqs: ['OCI-3 complete ✅'],
    doD: [
      'BL-ATP-1 resolved: signalWriter atp: true ✅',
      'stooqBulkLoad.js deployed + bulk load complete ✅',
      'stooqPipeline.js cron scheduled 6:30pm ET ✅',
    ],
    smokeChecks: [
      'test -f backend/jobs/stooqBulkLoad.js',
      'test -f backend/jobs/stooqPipeline.js',
      'grep -q "startStooqPipeline" backend/server.js',
      'grep -q "stooq_us_daily" backend/jobs/stooqBulkLoad.js',
      'grep -q "America/New_York" backend/jobs/stooqPipeline.js',
    ],
    files: [
      'backend/jobs/stooqBulkLoad.js',
      'backend/jobs/stooqPipeline.js',
      'backend/server.js',
      '.claude/project-knowledge/OCI_CONFIG.md',
    ],
    vendorDeps: [
      { name: 'Stooq', plan: 'Free', status: 'INTERNAL ONLY — D3 backtesting (R29) ✅' },
    ],
  },

  {
    id: 'oci-dev-vm',
    sessionNumber: 'OCI-DEV-VM',
    name: 'OCI-DEV-VM: Dev VM Setup + GitHub Actions dev + ENV-SEG + dev.marketpulze.com',
    status: 'done',
    phase: 'Phase 3 — OCI Infrastructure',
    date: 'May 4, 2026',
    commit: '87fb0c0',
    items: [
      '── DEV VM SETUP ────────────────────────────────────────────────',
      'mp-vm1-scanner-dev (150.136.91.57): Node.js 20 · PM2 · Redis AOF · Nginx ✅',
      'OCI CLI 3.81.0 + instance principal (added to mp-vm1-scanner-prod-dynamic-group) ✅',
      'Repo cloned via deploy key (mp-vm1-scanner-dev, read-only) ✅',
      'ATP wallet /etc/oci/wallets/atp/ copied from prod · 700/600 permissions ✅',
      'load-secrets.sh: MONGODB_URI → mp-mongodb-uri-dev (marketpulze-dev Atlas) ✅',
      '/health: redis:true · atp:true · db:connected ✅',
      'PM2 startup: pm2 startup systemd configured ✅',
      'iptables: port 80 + 443 opened + saved to /etc/iptables/rules.v4 ✅',
      '── ATP SCHEMA ARCHITECTURE (permanent) ─────────────────────────',
      '4 new Oracle schemas: MP_REFERENCE · MP_SIGNALS_PROD · MP_SIGNALS_DEV · MP_SIGNALS_TEST',
      '5 new vault secrets in mp-atpmarketpulze-passwords (4 URIs + admin)',
      '1 new vault secret mp-mongodb-uri-dev in mp-vault-backend',
      'load-secrets.sh on VM-1 updated with ATP_MONGODB_URI_REFERENCE/PROD/DEV/TEST',
      'stooqBulkLoad.js: ATP_TARGET_DB env var — migrating stooq_us_daily → MP_REFERENCE',
      'stooq_macro_daily ✅ confirmed in MP_REFERENCE (115,140 records)',
      '── GITHUB ACTIONS ───────────────────────────────────────────────',
      'deploy.yml updated: push dev → SSH → dev VM · push main → VM-1 (unchanged)',
      '3 new GitHub secrets: SCANNER_VM_DEV_HOST · SCANNER_VM_DEV_USER · SCANNER_VM_DEV_PRIVATE_SSH',
      'Health check: https://dev.api.marketpulze.com/health (70s wait) ✅',
      'First green deploy: 1m 25s ✅',
      '── DNS + SSL ────────────────────────────────────────────────────',
      'dev.api.marketpulze.com A → 150.136.91.57 (Cloudflare DNS only) ✅',
      'Let\'s Encrypt SSL for dev.api.marketpulze.com ✅ (expires 2026-08-02)',
      'Nginx: HTTP → HTTPS redirect + proxy to localhost:3000 ✅',
      '── ENV-SEG (BL-OCI3-10 closed) ─────────────────────────────────',
      'Prod VM-1: MONGODB_URI → marketpulze-prod Atlas ✅',
      'Dev VM: MONGODB_URI → marketpulze-dev Atlas ✅',
      '── RAILWAY CANCELLED ────────────────────────────────────────────',
      'Owner cancelled Railway $5/mo subscription May 4, 2026',
    ],
    prereqs: ['OCI-4 complete ✅'],
    doD: [
      '/health on 150.136.91.57: redis:true · atp:true · db:connected ✅',
      'push dev → auto-deploys to dev VM ✅',
      'push main → auto-deploys to VM-1 (unchanged) ✅',
      'dev.marketpulze.com login working ✅',
      'ENV-SEG complete — dev/prod Atlas separated ✅',
    ],
    smokeChecks: [
      'grep -q "dev.marketpulze.com" backend/server.js',
      'grep -q "SCANNER_VM_DEV" .github/workflows/deploy.yml',
      'grep -q "deploy-dev" .github/workflows/deploy.yml',
      'grep -q "dev.api.marketpulze.com" .github/workflows/deploy.yml',
    ],
    files: [
      'backend/server.js',
      '.github/workflows/deploy.yml',
      '.claude/project-knowledge/OCI_CONFIG.md',
    ],
    vendorDeps: [],
  },

  {
    id: 'oci-5-edgar',
    sessionNumber: 'OCI-5',
    name: 'OCI-5: EDGAR + Congressional Data Pipelines → ATP + Block Storage',
    status: 'done',
    phase: 'Phase 3 — OCI Infrastructure',
    date: 'May 4, 2026',
    commit: '1c5a01c',
    items: [
      '── BLOCK STORAGE (Task 0) ───────────────────────────────────────',
      'mp-data-vol1: 200GB OCI block volume · /dev/sdb → /mnt/mp-data ✅',
      'Directories: edgar/raw · edgar/processed · edgar/failed · stooq · xbrl',
      '            pipeline-state · transcripts · backups · reserved',
      'Redis AOF moved to /mnt/mp-data/redis/ → symlinked /var/lib/redis ✅',
      '── EDGAR PIPELINE (Task 1) ──────────────────────────────────────',
      'edgarPipeline.js: Form 4 + SC 13D + SC 13G → edgar_insider (ATP MP_REFERENCE)',
      'edgarPipeline.js: 8-K + S-3 + 424B4 + S-4 + SC TO-T + 10-Q + 10-K → edgar_events',
      '7:00am ET cron → events only · 8:00pm ET cron → full pull',
      'D3 signal: insider_signal → L3 (Form 4 buys ≥ $50k, SC 13D activist)',
      '── CONGRESSIONAL PIPELINE (Task 2) ──────────────────────────────',
      'congressPipeline.js: deployed · source dead (House Stock Watcher S3 → 403)',
      'Fix: OCI-5B-SENATE + OCI-5B-HOUSE (two dedicated sessions)',
      '── SIGNAL WRITER (Task 3) ───────────────────────────────────────',
      'insider_signal + congressional_signal added to VALID_TYPES',
    ],
    prereqs: ['OCI-DEV-VM complete ✅'],
    doD: [
      'Block volume /mnt/mp-data mounted + Redis AOF moved ✅',
      'edgarPipeline.js deployed — 7am + 8pm crons registered ✅',
      'signalWriter: insider_signal + congressional_signal in VALID_TYPES ✅',
    ],
    smokeChecks: [
      'test -f backend/jobs/edgarPipeline.js',
      'test -f backend/jobs/congressPipeline.js',
      'grep -q "edgar_insider" backend/jobs/edgarPipeline.js',
      'grep -q "edgar_events" backend/jobs/edgarPipeline.js',
      'grep -q "America/New_York" backend/jobs/edgarPipeline.js',
      'grep -q "insider_signal" backend/services/signalWriter.js',
      'grep -q "startEdgarPipeline" backend/server.js',
    ],
    files: [
      'backend/jobs/edgarPipeline.js',
      'backend/jobs/congressPipeline.js',
      'backend/services/signalWriter.js',
      'backend/server.js',
    ],
    vendorDeps: [
      { name: 'SEC EDGAR', plan: 'Free public domain', status: 'Form 4 + 8-K + all forms ✅' },
    ],
  },

  {
    id: 'oci-5-harden',
    sessionNumber: 'OCI-5-HARDEN',
    name: 'OCI-5-HARDEN: EDGAR Pipeline Hardening S1-S11 + Congressional Architecture',
    status: 'done',
    phase: 'Phase 3 — OCI Infrastructure',
    date: 'May 6, 2026',
    commit: '0ae9043 + S11-pending',
    items: [
      '── INFRASTRUCTURE ───────────────────────────────────────────────',
      'VM-1 kernel patched: 1049 → 1050-oracle (security patches) ✅',
      'stooq_us_daily + stooq_macro_daily dropped from admin schema ✅',
      'ORA-65114 ATP space pressure: RESOLVED ✅',
      'edgar_events + edgar_insider dropped + restarted clean ✅',
      '225,461 malformed accessionNo txt files deleted ✅',
      'State file reset to 2024-05-05 · bulk re-run started from 2024-05-06 ✅',
      '── EDGAR PIPELINE HARDENING (S1-S11) ───────────────────────────',
      'S1:  HTTP 500 retry 5s/10s/15s backoff (root cause of insider: 0)',
      'S2:  HTTP 429 retry — confirmed working',
      'S3:  Network timeout retry 3s/6s/9s (was immediate reject)',
      'S4:  XML fetch failure → failed/ACCNO.txt with filedAt stored on line 1',
      'S5:  XML 500 → covered by S1',
      'S6:  Parse failure → logged with accessionNo (was invisible)',
      'S7:  ATP upsert failure → NDJSON queue /mnt/mp-data/edgar/failed/',
      '     upsert_queue_insider.ndjson + upsert_queue_events.ndjson',
      '     Max 5 retries · replay at every run start',
      'S8:  ATP connection drop → _ensureATPConn() before every upsert',
      'S9:  Bulk interrupted → resumes from lastRunDate (not day 0)',
      'S10: EFTS total undercount → acceptable, noted',
      'S11: AccessionNo malformed → split(":")[0] strips filename suffix',
      '     Root cause of 225,461 bad txt files + 404 XML fetch failures',
      '── CONGRESSIONAL ARCHITECTURE (OCI-5B design) ───────────────────',
      'Two separate pipelines confirmed:',
      '  senatePipeline.js — Senate PTR (HTML — confirmed efdsearch.senate.gov)',
      '  housePipeline.js  — House PTR (format TBC at OCI-5B-HOUSE session)',
      'Senate source confirmed:',
      '  Search: POST efdsearch.senate.gov/search/',
      '  Filing: GET efdsearch.senate.gov/search/view/ptr/{UUID}/',
      '  Format: HTML table — no PDF, no cheerio — regex sufficient',
      'Filer scope (both chambers):',
      '  Active members: full weight · Former members: recencyWeight 3yr decay',
      '  Candidates: excluded · Cabinet/OGE: future session',
      'Report types: PTR + PTR Amendments + New Filer Report',
      '  New Filer → congressional_holdings (NEW collection) · baseline + sophistication',
      'Owner types: self · spouse · joint · dependent_child (all in PTR HTML)',
      'assetCategory: stock · tbill · bond · option · commodity · fund · other',
      'NEVER filter ticker = "--" — T-Bills ARE the rotation signal',
      'New signals:',
      '  regime_rotation_signal → CONFIRMED in signalWriter VALID_TYPES ✅ (added OCI-5B-SENATE)',
      '  FLIGHT_TO_SAFETY:  stocks → T-Bills (bearish macro)',
      '  SAFETY_REVERSAL:   T-Bills → stocks (2-6wk lead time · highest conviction)',
      '  SECTOR_ROTATION:   sector-specific legislative alpha',
      'Cross-chamber amplifier: Senate + House same direction = HIGHEST CONVICTION D3',
      'Conviction amplifiers: bipartisan · bicameral · <7 day clustering · L6 per-member accuracy',
    ],
    prereqs: ['OCI-5 complete ✅'],
    doD: [
      'edgarPipeline.js S1-S11 hardened + deployed to prod ✅',
      'S11 accessionNo fix committed + deployed ✅',
      'ATP edgar collections clean · bulk re-run in progress ✅',
      'Congressional architecture documented ✅',
      'Senate source confirmed (HTML) ✅',
      'CI=true npm run build clean ✅',
    ],
    smokeChecks: [
      'grep -q "500 — backoff" backend/jobs/edgarPipeline.js',
      'grep -q "Timeout — backoff" backend/jobs/edgarPipeline.js',
      'grep -q "upsert_queue_insider.ndjson" backend/jobs/edgarPipeline.js',
      'grep -q "_ensureATPConn" backend/jobs/edgarPipeline.js',
      'grep -q "not restarting from day 0" backend/jobs/edgarPipeline.js',
      'grep -q "split.*0.*replace" backend/jobs/edgarPipeline.js',
      'grep -q "failedAtOffset" backend/jobs/edgarPipeline.js',
      'grep -q "Form 4 parse failed" backend/jobs/edgarPipeline.js',
    ],
    files: [
      'backend/jobs/edgarPipeline.js',
      '.claude/CONGRESSIONAL_ROTATION_SIGNAL.md',
    ],
    vendorDeps: [],
  },

  // ─── OCI BUILD SEQUENCE (continued) ───────────────────────────────────────

  {
    id: 'doc-align',
    sessionNumber: 'DOC-ALIGN',
    name: 'DOC-ALIGN: Documentation Reconciliation — No Code',
    status: 'done',
    phase: 'Phase 3 — OCI Infrastructure',
    date: 'May 2026',
    commit: '5bf11d0',
    items: [
      '── WHY THIS SESSION EXISTS ───────────────────────────────────',
      'Owner spent 2 weeks of sessions re-aligning Claude instead of building',
      'Root cause: Claude creates new files instead of updating existing ones',
      'Files are fragmented, conflicting, duplicated across two locations',
      'Result: drift, wrong decisions, wasted sessions',
      '── OWNER PROVIDES TWO FILE SETS ─────────────────────────────',
      'Set 1: Project Knowledge files (shorter · 9 files · Claude auto-loads)',
      'Set 2: .claude/ full files (detailed · developer reference)',
      'Claude reads ALL files before writing a single word',
      '── CONFLICTS TO RESOLVE ──────────────────────────────────────',
      'ARCH.md vs MARKETPULZE_ARCHITECTURE.md → one file wins',
      'OCI_CONFIG.md vs OCI_CONFIGURATION.md → shorter for PK · full for .claude/',
      'D3_CORE.md + D3_INTELLIGENCE_FRAMEWORK.md → merge into D3_CORE.md',
      'PRODUCT_BACKLOG.md → reconciliation status',
      'CHANGELOG.md → verify complete history preserved (was wiped this session)',
      '── RULES FOR THIS SESSION ────────────────────────────────────',
      'Never create a new file if an existing one covers the topic',
      'Consolidation only — no new content except what was decided today',
      'Agree naming convention once — never change',
      'Agree what lives in Project Knowledge vs .claude/ — permanent decision',
      '── NEW CONTENT TO MERGE (from OCI-5-HARDEN session) ─────────',
      'D3_INTELLIGENCE_FRAMEWORK.md → merge into D3_CORE.md',
      'MARKETPULZE_ARCHITECTURE.md → merge/replace ARCH.md',
      'CONGRESSIONAL_ROTATION_SIGNAL.md → merge into correct existing file',
      'Pipeline architecture decisions → merge into ARCH.md or OCI_CONFIG.md',
    ],
    prereqs: ['OCI-5-HARDEN complete ✅'],
    doD: [
      'All documentation conflicts resolved ✅',
      'Single authoritative file per topic ✅',
      'Naming convention documented and agreed ✅',
      'Two-set structure (Project Knowledge + .claude/) clean ✅',
      'New developer can read .claude/ and understand full system ✅',
      'Owner no longer holds architecture in head alone ✅',
    ],
    smokeChecks: [],
    files: [
      '.claude/project-knowledge/ (all 9 files)',
      '.claude/ (all reference files)',
    ],
    vendorDeps: [],
  },

  {
    id: 'oci-storage-foundation',
    sessionNumber: 'OCI-STORAGE-FOUNDATION',
    name: 'OCI-STORAGE-FOUNDATION: Provision ATP-2 + OCI NoSQL + Object Storage',
    status: 'done',
    date: 'May 15, 2026',
    commit: '— (OCI Console only · no code)',
    phase: 'Phase 3 — OCI Infrastructure',
    items: [
      '── WHY THIS SESSION EXISTS ───────────────────────────────────────',
      'All pipeline sessions write to OCI NoSQL or ATP-2.',
      'If this session is skipped, every pipeline session retrofits its write target.',
      'OCI Console only — zero code changes. 20-30 minute task.',
      '── TASK 1: ATP Instance 2 (ATPMarketPulze-Archive) ─────────────',
      'Compartment: mp-databases (same as ATP-1)',
      'Name: ATPMarketPulze-Archive · Always Free · mTLS required · MongoDB API enabled',
      'Schema: MP_ARCHIVE',
      'Purpose: AI training corpus · outcome-correlated signals · trade_log · xbrl_financials',
      'Add to ATP ACL: VM-1 (129.213.147.105) + VM-DEV (150.136.91.57) + VM-PIPELINE (129.213.25.103)',
      'Add to dynamic group: mp-vm1-scanner-prod-dynamic-group',
      'Generate wallet: OCI CLI on VM-PIPELINE · install to /etc/oci/wallets/atp-archive/',
      'Add env var: ATP_MONGODB_URI_ARCHIVE to load-secrets.sh on VM-PIPELINE and VM-1',
      '── TASK 2: OCI NoSQL Database ───────────────────────────────────',
      'Name: mp-nosql · Compartment: mp-databases · Always Free tier',
      'Always Free limits: 3 tables · 25GB storage · 133M read+write units/month',
      'Initial tables (create now, schema defined per pipeline session):',
      '  edgar_events (partition key: ticker, sort key: filedAt)',
      '  edgar_insider (partition key: ticker, sort key: filedAt)',
      '  congressional_trades (partition key: ticker, sort key: transactionDate)',
      'Enable TTL on all three tables (TTL field: expiresAt, set per record at write time)',
      'Auth: instance principal (same IAM policy — no new secrets needed)',
      'SDK: oracle-nosqldb npm package (install on VM-PIPELINE in REFACTOR-EDGAR session)',
      '── TASK 3: Object Storage Bucket (mp-data-archive) ─────────────',
      'Compartment: mp-security (consistent with vault)',
      'Name: mp-data-archive · Standard tier · No public access',
      'Purpose: Stooq Parquet archives · EDGAR bulk ZIPs · cold pipeline exports',
      'Lifecycle rule: move to Infrequent Access after 90 days (cost optimisation)',
      'Free tier: 20GB standard · stays within free for years at expected volume',
      '── POST-PROVISIONING VERIFICATION ──────────────────────────────',
      'ATP-2: mongosh $ATP_MONGODB_URI_ARCHIVE --eval "db.runCommand({ping:1})" → ok',
      'NoSQL: OCI CLI list-tables → edgar_events · edgar_insider · congressional_trades visible',
      'Object Storage: upload test file · verify accessible from VM-PIPELINE',
    ],
    prereqs: ['VM-PIPELINE running ✅', 'ATP-1 connected ✅', 'Dynamic group has 3 VMs ✅'],
    doD: [
      'ATP-2 MP_ARCHIVE connected from VM-PIPELINE ✅',
      'OCI NoSQL 3 tables created with TTL enabled ✅',
      'Object Storage bucket mp-data-archive accessible ✅',
      'All new resources added to OCI_CONFIG.md ✅',
      'No code changes — this is infrastructure provisioning only ✅',
    ],
    smokeChecks: [],
    files: [
      '.claude/project-knowledge/OCI_CONFIG.md',
      '.claude/OCI_CONFIGURATION.md',
    ],
    vendorDeps: [],
  },

  {
    id: 'oci-5b-senate',
    sessionNumber: 'OCI-5B-SENATE',
    name: 'OCI-5B-SENATE: Senate Congressional Pipeline — HTML Scraper + Rotation Detection',
    status: 'done',
    phase: 'Phase 4 — Pipeline Data Layer',
    date: 'May 16, 2026',
    commit: '79ab7c6',
    items: [
      '── WHY SENATE FIRST ─────────────────────────────────────────────',
      'Easiest win: Senate source confirmed HTML, no PDF dependency, simple scraping.',
      'SAFETY_REVERSAL = highest conviction D3 signal — get it running first.',
      'Proves VM-PIPELINE deployment pattern before complex EDGAR rebuild.',
      'Independent pipeline: no shared engine dependency. Self-contained file.',
      '── SOURCE (confirmed May 6, 2026) ───────────────────────────────',
      'Search:  POST efdsearch.senate.gov/search/',
      '  Body: reportTypes[]=11 (PTR) · filerType=senator · dateFrom + dateTo',
      'Results: HTML DataTables table — 25 per page · paginated',
      'Filing:  GET efdsearch.senate.gov/search/view/ptr/{UUID}/',
      '  HTML table: transactionDate · owner · ticker · assetName · assetType · type · amount',
      'No cheerio installed — regex HTML parsing sufficient',
      '── WRITE TARGET: OCI NoSQL ──────────────────────────────────────',
      'congressional_trades table in OCI NoSQL (provisioned at OCI-STORAGE-FOUNDATION)',
      'SDK: oracle-nosqldb (install in this session)',
      'NOT ATP MP_REFERENCE — congressional data belongs in OCI NoSQL',
      '── INDEPENDENT PIPELINE RULE (R34) ──────────────────────────────',
      'senatePipeline.js is completely self-contained.',
      'S1-S11 resilience patterns live inside this file.',
      'No shared engine. No pipelineEngine.js. This file runs alone.',
      '── FILER SCOPE ──────────────────────────────────────────────────',
      'Active senators:  full weight (primary signal)',
      'Former senators:  recencyWeight — 1.0 decays to 0.3 over 3 years',
      'Candidates:       EXCLUDED — no committee access',
      '── REPORT TYPES ─────────────────────────────────────────────────',
      'PTR (reportTypes[]=11):       primary trade signal',
      'PTR Amendments:               data accuracy corrections',
      'New Filer Report:             baseline holdings → congressional_holdings',
      '── ASSET CATEGORIZATION ─────────────────────────────────────────',
      'NEVER filter ticker = "--" records (T-Bills/bonds ARE the rotation signal)',
      '_categorizeAsset(assetType, assetName, ticker):',
      '  stock · tbill · bond · option · commodity · fund · other',
      'TTL set at write time by sector classification (R34 TTL rule)',
      '── SIGNAL TIER FIELD (mandatory from day one — agreed OCI-STORAGE-FOUNDATION) ────',
      'Every record MUST include tier: 1 | 2 | 3 at write time — never null, never retrofitted.',
      'congressional_signal tier: 2 (single trade) or 1 (bipartisan cluster + rotation)',
      'regime_rotation_signal tier: 1 (SAFETY_REVERSAL) or 2 (FLIGHT_TO_SAFETY · SECTOR_ROTATION)',
      '── OUTCOME FLAG FIELDS (add from day one) ───────────────────────',
      'outcomeConfirmed: false (filled by OCI-OUTCOME-DETECTION later)',
      'outcomeType: null · outcomeDays: null · outcomeReturn: null',
      'retentionFlag: null (set to PERMANENT when outcomeConfirmed=true)',
      '── ROTATION DETECTION ───────────────────────────────────────────',
      '_detectRotationPattern() alongside _detectClusters()',
      'FLIGHT_TO_SAFETY:  3+ senators · stocks → T-Bills · 14-day window',
      'SAFETY_REVERSAL:   3+ senators · T-Bills → stocks · 2-6wk lead time',
      'SECTOR_ROTATION:   sector-specific · infers policy from rotation direction',
      'Bipartisan (D+R) amplifier · Bicameral amplifier (when House added)',
      '── SIGNALS ──────────────────────────────────────────────────────',
      'regime_rotation_signal → CONFIRMED in signalWriter VALID_TYPES ✅ (added OCI-5B-SENATE)',
      'congressional_signal → existing (large trades + cluster detection)',
      'SAFETY_REVERSAL → Tier 1 alert (when OCI-5A built)',
      'FLIGHT_TO_SAFETY → Tier 1 alert (when OCI-5A built)',
      '── CRON ─────────────────────────────────────────────────────────',
      '9:00pm ET Mon-Fri (R27) — TZ=America/New_York mandatory',
    ],
    prereqs: [
      'OCI-STORAGE-FOUNDATION complete ✅ (OCI NoSQL + ATP-2 + Object Storage provisioned · May 15, 2026)',
      'OCI-5-HARDEN complete ✅',
    ],
    doD: [
      'senatePipeline.js self-contained — no shared engine dependency ✅',
      'Writes to OCI NoSQL congressional_trades (not ATP MP_REFERENCE) ✅',
      'oracle-nosqldb installed on VM-PIPELINE ✅',
      'congressional_trades populating with chamber=senate ✅',
      'FLIGHT_TO_SAFETY detection firing on test data ✅',
      'SAFETY_REVERSAL detection firing on test data ✅',
      'regime_rotation_signal in signalWriter VALID_TYPES ✅',
      'outcomeConfirmed/outcomeType/outcomeDays/outcomeReturn fields present ✅',
      'tier field present on every record (1=bipartisan rotation · 2=single trade) ✅',
      'CI=true npm run build clean ✅',
    ],
    smokeChecks: [
      'test -f backend/jobs/senatePipeline.js',
      'grep -q "efdsearch.senate.gov" backend/jobs/senatePipeline.js',
      'grep -q "oracle-nosqldb\\|nosql" backend/jobs/senatePipeline.js',
      'grep -q "FLIGHT_TO_SAFETY" backend/jobs/senatePipeline.js',
      'grep -q "SAFETY_REVERSAL" backend/jobs/senatePipeline.js',
      'grep -q "SECTOR_ROTATION" backend/jobs/senatePipeline.js',
      'grep -q "regime_rotation_signal" backend/services/signalWriter.js',
      'grep -q "_categorizeAsset" backend/jobs/senatePipeline.js',
      'grep -q "outcomeConfirmed" backend/jobs/senatePipeline.js',
      'grep -q "tier:" backend/jobs/senatePipeline.js',
      'grep -q "America/New_York" backend/jobs/senatePipeline.js',
      '! grep -q "pipelineEngine" backend/jobs/senatePipeline.js',
      '! grep -q "mongoose.model" backend/jobs/senatePipeline.js',
    ],
    files: [
      'backend/jobs/senatePipeline.js',
      'backend/services/signalWriter.js',
      'backend/server.js',
    ],
    vendorDeps: [
      { name: 'efdsearch.senate.gov', plan: 'Free .gov public', status: 'HTML scraping · permanent source' },
      { name: 'oracle-nosqldb', plan: 'Free OCI SDK', status: 'npm install in this session' },
    ],
  },

  {
    id: 'oci-5b-house',
    sessionNumber: 'OCI-5B-HOUSE',
    name: 'OCI-5B-HOUSE: House Congressional Pipeline — ZIP + XML Parser',
    status: 'done',
    phase: 'Phase 4 — Pipeline Data Layer',
    date: 'May 17, 2026',
    commit: 'see HOUSE-PIPELINE-REWRITE (5b26d3f)',
    items: [
      '── SOURCE (confirm format at session open) ───────────────────────',
      'disclosures-clerk.house.gov/FinancialDisclosure',
      'FIRST ACTION: confirm current format — XML in ZIP or PDF',
      '  If XML ZIP: parse directly (preferred — no new packages)',
      '  If PDF: owner approves pdf-parse before any code written',
      '── INDEPENDENT PIPELINE RULE (R34) ──────────────────────────────',
      'housePipeline.js is completely self-contained.',
      'Same S1-S11 resilience inside this file. No shared engine.',
      '── WRITE TARGET: OCI NoSQL ──────────────────────────────────────',
      'Same OCI NoSQL congressional_trades table (chamber=house)',
      'oracle-nosqldb already installed from OCI-5B-SENATE',
      '── SAME ARCHITECTURE AS SENATE ──────────────────────────────────',
      'Same filer scope · report types · owner types · asset categorization',
      'NEVER filter "--" ticker · same TTL rules · same outcome flag fields',
      '── CROSS-CHAMBER AMPLIFIER ──────────────────────────────────────',
      '_detectCrossChambber(): Senate + House same direction same 14-day window',
      '= HIGHEST CONVICTION signal in D3 framework',
      '= Tier 1 alert (when OCI-5A built)',
      '── CRON ─────────────────────────────────────────────────────────',
      '9:05pm ET Mon-Fri (5min after Senate — no concurrent NoSQL writes)',
    ],
    prereqs: ['OCI-5B-SENATE complete ✅'],
    doD: [
      'housePipeline.js self-contained — no shared engine dependency ✅',
      'Writes to OCI NoSQL congressional_trades (chamber=house) ✅',
      'Cross-chamber detection firing when both chambers rotate same direction ✅',
      'outcomeConfirmed/outcomeType/outcomeDays/outcomeReturn fields present ✅',
      'tier field present on every record (1=bipartisan rotation · 2=single trade) ✅',
      'CI=true npm run build clean ✅',
    ],
    smokeChecks: [
      'test -f backend/jobs/housePipeline.js',
      'grep -q "disclosures-clerk.house.gov" backend/jobs/housePipeline.js',
      'grep -q "oracle-nosqldb\\|nosql" backend/jobs/housePipeline.js',
      'grep -q "FLIGHT_TO_SAFETY" backend/jobs/housePipeline.js',
      'grep -q "SAFETY_REVERSAL" backend/jobs/housePipeline.js',
      'grep -q "outcomeConfirmed" backend/jobs/housePipeline.js',
      'grep -q "America/New_York" backend/jobs/housePipeline.js',
      '! grep -q "pipelineEngine" backend/jobs/housePipeline.js',
      '! grep -q "mongoose.model" backend/jobs/housePipeline.js',
    ],
    files: [
      'backend/jobs/housePipeline.js',
      'backend/server.js',
    ],
    vendorDeps: [
      { name: 'disclosures-clerk.house.gov', plan: 'Free .gov public', status: 'Format confirm at session open' },
    ],
  },

  {
    id: 'sector-update-members-committees',
    sessionNumber: 'SECTOR-UPDATE+MEMBERS+COMMITTEES',
    name: 'Sector Mapping · Congress Members · Congress Committees Pipelines',
    status: 'done',
    phase: 'Phase 4 — Pipeline Data Layer',
    date: 'May 18, 2026',
    commit: 'd433fca',
    items: [
      'sectorMappingPipeline.js: 10,353 tickers at session (0 rows now · pre-PIPELINE-RELOAD fix) · 23-col Oracle SQL · SIC→4-level GICS',
      'pipelineErrorHelpers.js Option C: logError · getUnresolvedErrors · markResolved · markRetryFailed',
      'congressMembersPipeline.js: 536 members at session close (0 rows now · PIPELINE-RELOAD pending) · Congress.gov API v3 · tenure conviction proxy',
      'congressCommitteePipeline.js: govinfo.gov CPRT · Rules of Procedure HTML · chair/ranking/member roles',
      'Oracle SQL tables: sector_mapping · congress_members · company_exchange_events · pipeline_errors',
      'fiscalYearEnd VARCHAR2(10) MMDD format · ProPublica confirmed shut down · govinfo.gov replacement',
      'Pipeline chain: senate→house→congressMembers→congressCommittees @ 9pm ET Mon-Fri',
      'CONGRESS_GOV_API_KEY vault + all 3 VMs',
    ],
    prereqs: ['OCI-5B-HOUSE complete ✅'],
    doD: [
      'sector_mapping: 10,353 rows at session close (now 0 · PIPELINE-RELOAD pending)',
      'congress_members: 536 rows at session close (now 0 · PIPELINE-RELOAD pending)',
      'congress_member_committees: committee roles populated ✅',
      'pipeline_errors table operational ✅',
      'CI=true npm run build clean ✅',
    ],
    smokeChecks: [
      'test -f backend/jobs/sectorMappingPipeline.js',
      'test -f backend/jobs/congressMembersPipeline.js',
      'test -f backend/jobs/congressCommitteePipeline.js',
      'grep -q "pipelineErrorHelpers" backend/jobs/sectorMappingPipeline.js',
      'grep -q "api.congress.gov" backend/jobs/congressMembersPipeline.js',
      'grep -q "govinfo.gov" backend/jobs/congressCommitteePipeline.js',
      'grep -q "isCommitteeChair" backend/jobs/congressCommitteePipeline.js',
    ],
    files: [
      'backend/jobs/sectorMappingPipeline.js',
      'backend/jobs/congressMembersPipeline.js',
      'backend/jobs/congressCommitteePipeline.js',
      'backend/jobs/pipelineErrorHelpers.js',
    ],
    vendorDeps: [
      { name: 'api.congress.gov/v3', plan: 'Free .gov API', status: 'CONGRESS_GOV_API_KEY vault ✅' },
      { name: 'govinfo.gov CPRT', plan: 'Free .gov public', status: 'HTML parser ✅' },
    ],
  },

  {
    id: 'pipeline-hardening',
    sessionNumber: 'PIPELINE-HARDENING',
    name: 'Pipeline Hardening — R39 _ensureConn · Senate 302 Fix · govCommitteeXml',
    status: 'done',
    phase: 'Phase 4 — Pipeline Data Layer',
    date: 'May 22, 2026',
    commit: '9cb51f4',
    items: [
      'R39: _ensureConn before every write phase — all 7 pipelines · pipelineHelpers.js updated',
      'senate HTTP 302 fix: plain for loop replaces _withConcurrency (CSRF — R38/R41)',
      'govCommitteeXml.js NEW: .gov XML fallback for committees (clerk.house.gov + senate.gov)',
      'executivePipeline.js NEW: Cabinet nominations · FIRSTNAME/LASTNAME parse fix',
      'congressCommitteePipeline.js: full rewrite → unitedstates JSON + 4-level fallback',
      'congressMembersPipeline.js: senate.gov enrichment (LIS ID + stateRank + phone)',
      'housePipeline.js: docId flat column + _seedStateFromDB',
      'Canonical failure documented: senate 0/3691 = ATP connection dead after 136min idle',
    ],
    prereqs: ['SECTOR-UPDATE+MEMBERS+COMMITTEES complete ✅'],
    doD: [
      '_ensureConn in all 7 pipelines ✅',
      'senate plain for loop confirmed — no _withConcurrency ✅',
      'govCommitteeXml.js deployed ✅',
    ],
    smokeChecks: [
      'test -f backend/jobs/govCommitteeXml.js',
      'grep -q "_ensureConn" backend/jobs/pipelineHelpers.js',
      '! grep -q "_withConcurrency" backend/jobs/senatePipeline.js',
    ],
    files: [
      'backend/jobs/pipelineHelpers.js',
      'backend/jobs/govCommitteeXml.js',
      'backend/jobs/executivePipeline.js',
      'backend/jobs/congressCommitteePipeline.js',
      'backend/jobs/congressMembersPipeline.js',
      'backend/jobs/housePipeline.js',
      'backend/jobs/senatePipeline.js',
    ],
    vendorDeps: [],
  },

  {
    id: 'congress-expansion',
    sessionNumber: 'CONGRESS-EXPANSION',
    name: 'Congress Expansion — Lobbying · FEC · Federal Register · sourceAssetType',
    status: 'done',
    phase: 'Phase 4 — Pipeline Data Layer',
    date: 'May 22, 2026',
    commit: '9911e6c',
    items: [
      'sourceAssetType column on congressional_trades (senate + house) — raw source audit column',
      'lobbyingPipeline.js NEW → lobbying_disclosures (Oracle SQL) · lda.senate.gov · LDA_API_TOKEN',
      'fecPipeline.js NEW → fec_member_funding (Oracle SQL) · api.open.fec.gov · FEC_API_KEY',
      'federalRegisterPipeline.js NEW → federal_register_orders (Oracle SQL) · federalregister.gov · no auth',
      'ExecutiveFinancialPipeline.js → ARCHIVED May 23, 2026 · OGE 278e commercial use prohibited',
      'pipeline-server.js: 11pm evening chain + Sunday weekly chain + federal register 6am',
      'load-secrets.sh: 37 vault secrets + 2 static paths · dynamic count all 3 VMs',
      'LDA_API_TOKEN + FEC_API_KEY vault (mp-vault-backend) + all 3 VMs',
      'DDL run: lobbying_disclosures · fec_member_funding · federal_register_orders',
      'R40: OCI NoSQL 3-table limit hard — all new pipeline data → Oracle SQL MP_REFERENCE',
    ],
    prereqs: ['PIPELINE-HARDENING complete ✅'],
    doD: [
      'lobbyingPipeline.js deployed ✅',
      'fecPipeline.js deployed ✅',
      'federalRegisterPipeline.js deployed ✅',
      'LDA_API_TOKEN + FEC_API_KEY in vault all 3 VMs ✅',
      'DDL tables created ✅',
    ],
    smokeChecks: [
      'test -f backend/jobs/lobbyingPipeline.js',
      'test -f backend/jobs/fecPipeline.js',
      'test -f backend/jobs/federalRegisterPipeline.js',
      'grep -q "LDA_API_TOKEN" backend/jobs/lobbyingPipeline.js',
      'grep -q "FEC_API_KEY" backend/jobs/fecPipeline.js',
      'grep -q "sourceAssetType" backend/jobs/senatePipeline.js',
    ],
    files: [
      'backend/jobs/lobbyingPipeline.js',
      'backend/jobs/fecPipeline.js',
      'backend/jobs/federalRegisterPipeline.js',
      'backend/pipeline-server.js',
      'backend/jobs/senatePipeline.js',
      'backend/jobs/housePipeline.js',
    ],
    vendorDeps: [
      { name: 'lda.senate.gov', plan: 'Free .gov API', status: 'LDA_API_TOKEN vault ✅' },
      { name: 'api.open.fec.gov', plan: 'Free .gov API', status: 'FEC_API_KEY vault ✅' },
      { name: 'federalregister.gov', plan: 'Free .gov API', status: 'No auth required ✅' },
    ],
  },

  {
    id: 'compliance-cleanup',
    sessionNumber: 'COMPLIANCE-CLEANUP',
    name: 'Compliance Cleanup — OGE 278e Archived · NoSQL → Stooq Only',
    status: 'done',
    phase: 'Phase 4 — Pipeline Data Layer',
    date: 'May 23, 2026',
    commit: 'cf5f7da',
    items: [
      'executivePipeline + ExecutiveFinancialPipeline → _archive/ (OGE 278e commercial use prohibited)',
      '5 U.S.C. app § 105(c): annual financial disclosure commercial use prohibited — permanently archived',
      'executive_officials + executive_holdings tables DROPPED from ATP-1 MP_REFERENCE',
      'federalRegisterPipeline double-close bug fixed · 255 records written · Errors:0 ✅',
      'lobbyingPipeline double-close bug fixed + HTTP 400 fix (FR not a valid LDA code)',
      'pipeline-server.js Sunday chain: congressMembers → lobbying (executive steps removed)',
      'All 3 NoSQL tables dropped (edgar_events · edgar_insider · congressional_trades)',
      'NoSQL → stooq OHLCV only (R29 internal/D3) · all 3 slots free for STOOQ-PIPELINE',
      'OGE STOCK Act PTR (oge.gov) = different instrument — EXECUTIVE-PTR-RESTORE (last session)',
      'Hard gate: OGE 278e NEVER flows to D3 or signalWriter — enforced in code',
    ],
    prereqs: ['CONGRESS-EXPANSION complete ✅'],
    doD: [
      'executivePipeline + ExecutiveFinancialPipeline archived in _archive/ ✅',
      'executive_officials + executive_holdings tables dropped ✅',
      'federalRegisterPipeline: 255 records · Errors:0 ✅',
      'lobbyingPipeline: HTTP 400 fix confirmed ✅',
      'NoSQL: all 3 slots free for stooq ✅',
    ],
    smokeChecks: [
      '! test -f backend/jobs/executivePipeline.js',
      '! test -f backend/jobs/ExecutiveFinancialPipeline.js',
      'test -f backend/jobs/_archive/executivePipeline.js',
      'grep -q "federalregister.gov" backend/jobs/federalRegisterPipeline.js',
    ],
    files: [
      'backend/jobs/_archive/executivePipeline.js',
      'backend/jobs/_archive/ExecutiveFinancialPipeline.js',
      'backend/pipeline-server.js',
      'backend/jobs/federalRegisterPipeline.js',
      'backend/jobs/lobbyingPipeline.js',
    ],
    vendorDeps: [],
  },

  {
    id: 'house-pipeline-rewrite',
    sessionNumber: 'HOUSE-PIPELINE-REWRITE',
    name: 'House Pipeline Search Endpoint + Paper Parser + Pipeline Browser UA Fixes',
    status: 'done',
    phase: 'Phase 4 — Pipeline Data Layer',
    date: 'May 25, 2026',
    commit: '5b26d3f',
    items: [
      'housePipeline.js: search endpoint primary (ViewMemberSearchResult + ViewCandidateSearchResult)',
      'parse_house_pdf.py NEW: electronic eFD (pdfplumber) + paper PTR (OpenCV + Tesseract)',
      'memberResolver.js NEW: 4-tier resolution · BROWSER_UA · _bioguideBlocked flag · migrateToInactive',
      'DDL: inactive_congress_members (23 cols) · member_resolution_cache (11 cols)',
      'R42: .gov WAF rule — GET only · BROWSER_UA · 2s jitter · session breaks · IP_BLOCKED stop',
      'R43: USER_AGENT (API key) vs BROWSER_UA (no-key browser-facing .gov) — all pipelines',
      'senatePipeline: memberResolver integrated · BROWSER_UA on efdsearch',
      'congressMembersPipeline: memberResolver + _migrateInactiveMembers · BROWSER_UA',
      'congressCommitteePipeline + govCommitteeXml + federalRegisterPipeline: BROWSER_UA',
      'lobbyingPipeline R37 fix: per-error errConn removed — ORA-65114 connection storm fixed',
      'FEATURE_INVENTORY.md: new Project Knowledge file · shipped feature inventory',
      'Feature Inventory Gate: added to GATES.md',
    ],
    prereqs: ['COMPLIANCE-CLEANUP done ✅', 'CONGRESS-EXPANSION done ✅'],
    doD: [
      'house 2026: Written:1115 Errors:0 ✅',
      'Search endpoint: 1723 filings found (was 764) ✅',
      'memberResolver resolves party + bioguideId at parse time ✅',
      'BROWSER_UA on all no-key .gov endpoints ✅',
      'lobbyingPipeline R37 fix deployed ✅',
      'Smoke: 408/408',
    ],
    smokeChecks: [
      'test -f backend/jobs/memberResolver.js',
      'test -f backend/jobs/parse_house_pdf.py',
      'grep -q "ViewMemberSearchResult" backend/jobs/housePipeline.js',
      'grep -q "BROWSER_UA" backend/jobs/senatePipeline.js',
      'grep -q "_migrateInactiveMembers" backend/jobs/congressMembersPipeline.js',
      '! grep -q "errConn = await _getConn" backend/jobs/lobbyingPipeline.js',
    ],
    files: [
      'backend/jobs/housePipeline.js',
      'backend/jobs/parse_house_pdf.py',
      'backend/jobs/memberResolver.js',
      'backend/jobs/senatePipeline.js',
      'backend/jobs/congressMembersPipeline.js',
      'backend/jobs/lobbyingPipeline.js',
      'scripts/smoke-test.sh',
    ],
    vendorDeps: [],
  },


  {
    id: 'pipeline-validation',
    sessionNumber: 'PIPELINE-VALIDATION',
    name: 'Full Pipeline Data Quality Audit + Bug Fixes',
    status: 'done',
    phase: 'Phase 4 — Pipeline Data Layer',
    date: 'May 27, 2026',
    commit: '9f56a0a',
    items: [
      '── DATA QUALITY AUDIT ────────────────────────────────────────',
      'Full export + analysis of all 20 pipeline tables',
      'Found: PARTY=NULL 100%, BIOGUIDEID=NULL 100% in congressional_trades (34,345 rows)',
      'Found: TRANSACTIONDATE=NULL 100%, ROTATIONSIGNALTYPE=NULL 100%',
      'Found: companyName=ticker for 100% of edgar_events (215K rows)',
      'Found: securityType=other for 88.5% of sector_mapping (9,171/10,365 rows)',
      '── MEMBERRESOLVER FIX ────────────────────────────────────────',
      'Root cause: congress_members.state stores full names ("California")',
      'Senate extracted senator last name prefix as state → never matched',
      'House passed 2-letter code ("CA") → never matched "California"',
      'Fix: CODE_TO_STATE reverse map · _stateFullName() · empty state skips constraint',
      'Senate passes state="" · house 2-letter→full name via CODE_TO_STATE',
      '── SENATEPIPELINE FIXES (4 bugs) ─────────────────────────────',
      'state="" passed to memberResolver (office field never contains state)',
      'transactionType normalised: P→Purchase S→Sale E→Exchange H→Hold',
      'rotationSignalType classified at parse: TOWARD_SAFETY / FROM_SAFETY / GOVERNMENT_DEBT_ACTIVITY',
      'conviction applied unconditionally (not conditional on committeeRole)',
      '── HOUSEPIPELINE FIXES (2 bugs) ──────────────────────────────',
      'cross-chamber SQL used column "member" → ORA-00904 crash in daily incremental run',
      'rotationSignalType classified at parse time',
      '── EDGARDISPLAYPIPELINE FIXES (3 bugs) ───────────────────────',
      '_ensureConn missing import → ReferenceError crash at safety net write phase',
      'tier missing from UPSERT_SQL INSERT (cannot be retrofitted)',
      'companyName: meta.ticker → meta.name||meta.ticker (sector_mapping has real names)',
      'outcomeConfirmed removed: display table only, belongs in edgar_insider',
      '── SECTORMAPPINGPIPELINE EXPANSION ───────────────────────────',
      'Dual-source: company_tickers_exchange.json + company_tickers.json',
      'Expected universe: ~20K-25K rows (was 10,365)',
      'sub.tickers[] expansion: preferred shares + warrants per CIK',
      '12 securityTypes (was 5): stock·etf·etf_leveraged·etf_inverse·etn·fund·reit·bdc·spac·adr·warrant·other',
      'accessTier column: EXCHANGE/OTC/FUND_RETAIL/FUND_INST/UNLISTED',
      'isIraEligible column: 1 for all SEC-registered securities',
      'ENRICH_CONGRESSIONAL_SQL: now enriches sector + securityType + accessTier nightly',
      'congressional_trades: 2 new columns securityType + accessTier',
      '── LEGAL DECISIONS ───────────────────────────────────────────',
      'EXECUTIVE-PTR-RESTORE added as last pipeline session after OCI-6',
      'OGE Form 278e stays archived (5 U.S.C. app §105(c)) — never flows to D3',
      'STOCK Act PTRs = different legal instrument, legal review before session opens',
    ],
    prereqs: ['HOUSE-PIPELINE-REWRITE done ✅'],
    doD: [
      'All 5 fixed pipeline files syntax-checked ✅',
      'DDL_ALTER_ONLY.sql produced (ALTER + TRUNCATE only, no UPDATEs) ✅',
      'All 6 PK files updated (ARCH·CLAUDE·HEAD·ROADMAP·FEATURE_INVENTORY·PRODUCT_COMPASS) ✅',
      'HANDOVER_2026_05_27.md produced ✅',
      'Acceptance criteria defined (8 SQL tests) ✅',
      'DEPLOY PENDING: files not yet on VM-PIPELINE',
    ],
    smokeChecks: [
      'grep -q "CODE_TO_STATE" backend/jobs/memberResolver.js',
      'grep -q "_stateFullName" backend/jobs/memberResolver.js',
      'grep -q "state:      " backend/jobs/senatePipeline.js',
      'grep -q "_TX_NORMALIZE" backend/jobs/senatePipeline.js',
      'grep -q "rotationSignalTypeResolved" backend/jobs/senatePipeline.js',
      'grep -q "senator" backend/jobs/housePipeline.js',
      'grep -q "_ensureConn } = require" backend/jobs/edgarDisplayPipeline.js',
      'grep -q "FORM_TIER" backend/jobs/edgarDisplayPipeline.js',
      'test -f backend/jobs/sectorMappingClassify.js',
      'test -f backend/jobs/sectorMappingHttp.js',
      'test -f backend/jobs/sectorMappingGeo.js',
      'test -f backend/jobs/sectorMappingSQL.js',
      'grep -q "sectorMappingClassify" backend/jobs/sectorMappingPipeline.js',
      'grep -q "etf_leveraged" backend/jobs/sectorMappingPipeline.js',
      'grep -q "_deriveAccessTier" backend/jobs/sectorMappingPipeline.js',
    ],
    files: [
      'backend/jobs/memberResolver.js',
      'backend/jobs/senatePipeline.js',
      'backend/jobs/housePipeline.js',
      'backend/jobs/edgarDisplayPipeline.js',
      'backend/jobs/sectorMappingPipeline.js',
      '.claude/project-knowledge/ARCH.md',
      '.claude/project-knowledge/CLAUDE.md',
      '.claude/project-knowledge/HEAD.md',
      '.claude/project-knowledge/ROADMAP.md',
      '.claude/project-knowledge/FEATURE_INVENTORY.md',
      '.claude/project-knowledge/PRODUCT_COMPASS.md',
      'DDL_ALTER_ONLY.sql',
    ],
    vendorDeps: [],
  },

  {
    id: 'stooq-pipeline',
    sessionNumber: 'STOOQ-PIPELINE',
    name: 'STOOQ-PIPELINE: OCI NoSQL OHLCV Bulk Load + Daily Incremental',
    status: 'planned', // corrected from in-progress — code initial only, never ran
    phase: 'Phase 4 — Pipeline Data Layer',
    date: 'May 23, 2026',
    items: [
      'stooqBulkLoad.js: INITIAL CODE ONLY · bulk load from /mnt/mp-data/stooq/ ZIPs · never run',
      'stooqPipeline.js: INITIAL CODE ONLY · daily incremental cron · never run',
      'OCI NoSQL 3 tables: stooq_daily · stooq_intraday · stooq_macro_daily',
      'Tables created by SDK at first run (OCI Console On Demand capacity bug prevents manual create)',
      'ZIPs ready on VM-PIPELINE: d_us_txt.zip + d_macro_txt.zip at /mnt/mp-data/stooq/',
      'R29: NEVER serve to members — internal D3 backtesting only',
      'R40: These 3 NoSQL slots are reserved exclusively for stooq',
      '⚠️ oracle-nosqldb package may need: npm install oracle-nosqldb on VM-PIPELINE',
      '⚠️ First run has never happened — not validated in production',
    ],
    prereqs: ['PIPELINE-RELOAD acceptance criteria pass (confirms pipeline architecture is stable)'],
    doD: [
      'stooq_daily: > 500,000 rows (US daily OHLCV 30yr history)',
      'stooq_macro_daily: > 10,000 rows (macro symbols)',
      'stooqPipeline.js daily cron confirmed running (6:30pm ET Mon-Fri)',
      'R29 verified: zero member-facing endpoints reading stooq tables',
    ],
    smokeChecks: [
      'test -f backend/jobs/stooqBulkLoad.js',
      'test -f backend/jobs/stooqPipeline.js',
      'grep -q "stooq_daily" backend/jobs/stooqPipeline.js',
    ],
    files: [
      'backend/jobs/stooqBulkLoad.js',
      'backend/jobs/stooqPipeline.js',
    ],
    vendorDeps: [
      { name: 'Stooq.com', plan: 'Free ZIP downloads · personal/research ToS', status: 'R29: internal only — never redistributed' },
    ],
  },


  {
    id: 'doc-sync-rule',
    sessionNumber: 'DOC-SYNC', // SIGNAL-DECOUPLE 4459b30 ✅ · DOC-SYNC-2 0b85ad1 ✅ · both committed May 31 // SIGNAL-DECOUPLE committed 4459b30 May 31 — 17 bugs fixed · moat cols
    name: 'Documentation Sync — Trigger-Based (not a dedicated session)',
    status: 'standing-rule',
    phase: 'Every Phase',
    items: [
      'DOC-SYNC is NOT a scheduled session — it is a trigger-based activity within every session close.',
      'Targeted sync only — update files directly affected by the session, not all 32 files.',
      'Full sweep (all 32 files) only when 5+ triggers have fired without sync.',
    ],
    triggers: [
      'TRIGGER 1: After any pipeline session where data quality acceptance criteria ALL pass.',
      'TRIGGER 2: After any session that introduces a new R-series rule, architectural decision, or new Project Knowledge file.',
      'TRIGGER 3: Claude detects state inconsistency at session start (HEAD vs sessions-active vs other files).',
      'TRIGGER 4: After any session that changes 4-product hierarchy, D3 signal chain, or pipeline architecture.',
      'TRIGGER 5 (hard cap): Maximum 5 feature sessions without any doc sync — force sync regardless.',
    ],
    rule: 'Each trigger requires updating only the directly affected files. Not all 32. Prevents drift without creating dedicated sessions.',
    prereqs: [],
    doD: [],
    smokeChecks: [],
    files: [],
    vendorDeps: [],
  },

  {
    id: 'signal-decouple',
    sessionNumber: 'SIGNAL-DECOUPLE',
    name: 'Signal Architecture Decoupling + Full Pipeline Peer Review — 17 Bugs Fixed',
    status: 'done',
    phase: 'Phase 4 — Pipeline Data Layer',
    date: 'May 31, 2026',
    commit: '4459b30',
    items: [
      '── 17 BUGS FIXED (all 8 pipeline files, syntax-checked ✅) ────',
      'BUG 1: senatePipeline recencyWeight wrong argument',
      'BUG 2: edgarDisplayPipeline safety net companyName = ticker (both paths fixed)',
      'BUG 3: fecPipeline CURRENT_CYCLE hardcoded → _currentFecCycle() dynamic',
      'BUG 4: congressMembersPipeline _ensureConn not imported → ReferenceError crash on first write',
      'BUG 5-6: congressMembersPipeline semicolon + double _fetchAllMembers()',
      'BUG 7: congressMembersPipeline ENRICH_TRADES_SQL LIKE false surname matches',
      'BUG 8: memberResolver _enrichAndReturn missing chamber filter',
      'BUG 9: memberResolver INACTIVE members no recency decay',
      'BUG 10: housePipeline raw.owner from parse_house_pdf.py never read',
      'BUG 11: lobbyingPipeline primarySector = Set insertion order (wrong sector)',
      'BUG 12: sectorMappingPipeline _computeRotationSignals fires signals from data pipeline',
      'BUG 13-14: congressMembersPipeline API key guard + sep undefined → ReferenceError',
      'BUG 15: housePipeline backfill stale 4-arg _processOneFiling → R37 violation on 21k run',
      'BUG 16: senatePipeline holdings missing owner/filingLagDays',
      'BUG 17: senatePipeline label undefined in _fetchFilingList error throw',
      '── SIGNAL ARCHITECTURE DECOUPLED ──────────────────────────────',
      'All 8 data pipelines: ZERO signalWriter.write() code calls',
      'congressionalSignalPipeline.js: NOT YET BUILT — after PIPELINE-RELOAD acceptance criteria',
      'sectorMappingPipeline: exchange signal retained (market structure event — correct)',
      '── MOAT COLUMNS ADDED ──────────────────────────────────────────',
      'congressional_trades.OWNER (SELF/SPOUSE/JOINT/DEPENDENT/OTHER)',
      '  Conviction multiplier: SELF=1.0 · JOINT=0.85 · SPOUSE=0.70 · DEPENDENT=0.60',
      '  No competitor shows ownership type as a signal modifier',
      'congressional_trades.FILINGLAGDAYS (days between trade and STOCK Act disclosure)',
      '  Penalty tiers for congressionalSignalPipeline: ≤45=clean · 46-90=mild · >180=exclude',
      '  No competitor uses filing lag as a conviction modifier',
      '── FOUR-PRODUCT VALUE ──────────────────────────────────────────',
      'Product 1: moat columns visible in screener · ownership type on trade cards',
      'Product 2 (D3): conviction weights include owner multiplier + filingLag penalty from day 1',
      'Product 3 (Inner Circle): rotation direction already computed (rotationSignalType)',
      'Product 4 (Commercial): signal schema complete — brokerAdapter reads conviction weight',
    ],
    doD: [
      'All 8 pipeline files syntax-clean ✅',
      'Zero signalWriter.write() code calls in data pipelines ✅',
      'owner + filingLagDays in congressional_trades ✅',
      'DDL applied by owner in Database Actions ✅',
      'SIGNAL-DECOUPLE committed 4459b30 ✅',
    ],
    smokeChecks: [],
    files: [
      'backend/jobs/senatePipeline.js',
      'backend/jobs/housePipeline.js',
      'backend/jobs/memberResolver.js',
      'backend/jobs/congressMembersPipeline.js',
      'backend/jobs/sectorMappingPipeline.js',
      'backend/jobs/lobbyingPipeline.js',
      'backend/jobs/edgarDisplayPipeline.js',
      'backend/jobs/fecPipeline.js',
    ],
    vendorDeps: [],
  },

  {
    id: 'doc-sync-2',
    sessionNumber: 'DOC-SYNC-2',
    name: 'Documentation Sync 2 — Drift Prevention: Gate 6+7, Q1-Q5, AGREEMENTS, New PK Files',
    status: 'done',
    phase: 'Phase 4 — Pipeline Data Layer',
    date: 'May 31, 2026',
    commit: '0b85ad1',
    items: [
      'Gate 6: Documentation Diff Gate — diff output pasted before every doc file delivery',
      'Gate 7: Pre-Delivery Validation — correct check per file type (.js/.jsx/.sh/.md/.py)',
      'TOP PRIORITY rule: paste validation output before every file link — reject if missing',
      'Q5 (Q5-A through Q5-G): mandatory self-failure check at every session start',
      'AGREEMENTS section in CLAUDE.md — 12 permanent decisions, never remove',
      'R47 Product Cycle Gate (read PRODUCT_CYCLE_CHECKLIST.md before any code)',
      'R48 Agreements Gate (AGREEMENTS section add-only, never remove)',
      'PRODUCT_CYCLE_CHECKLIST.md: new PK file — 4-question check before any session',
      'ACCEPTANCE_CRITERIA.md: new PK file — all 9 SQL acceptance criteria',
      'FileArchSection1Claude.jsx: Layer 0 updated 13→14 files',
      'CLAUDE_SESSION_PROTOCOL.md: Q5 + roadmap position + product identity corrected',
      'Product identity corrected: four-product platform not "personal trading ecosystem"',
    ],
    doD: [
      '15 PK files (was 13) — PRODUCT_CYCLE_CHECKLIST + ACCEPTANCE_CRITERIA ✅',
      'CLAUDE_SESSION_PROTOCOL.md under 8000 chars ✅',
      'DOC-SYNC-2 committed 0b85ad1 ✅',
    ],
    smokeChecks: [],
    files: [
      '.claude/project-knowledge/CLAUDE.md',
      '.claude/project-knowledge/CLAUDE_SESSION_PROTOCOL.md',
      '.claude/project-knowledge/GATES.md',
      '.claude/project-knowledge/RULES.md',
      '.claude/project-knowledge/ACCEPTANCE_CRITERIA.md',
      '.claude/project-knowledge/PRODUCT_CYCLE_CHECKLIST.md',
      'src/components/roadmap/FileArchSection1Claude.jsx',
    ],
    vendorDeps: [],
  },

  {
    id: 'sector-rearch',
    sessionNumber: 'SECTOR-REARCH',
    name: 'sectorMappingPipeline Rewrite — 4 Sources + ISO MIC + 5 New Columns',
    status: 'done',
    phase: 'Phase 4 — Pipeline Data Layer',
    date: 'June 2, 2026',
    commit: '— (see apply-session.sh SECTOR-REARCH)',
    items: [
      '── 4-SOURCE MERGE ───────────────────────────────────────────────',
      'S1: company_tickers_exchange.json (10,365 exchange-listed · has CIK)',
      'S2: company_tickers_mf.json (28,378 mutual fund share classes · field=symbol not ticker)',
      'S3: nasdaqlisted.txt (+1,229 Nasdaq tickers · ETF flag) · BROWSER_UA R43',
      'S4: otherlisted.txt (+4,467 NYSE/AMEX/NYSE_ARCA/CBOE) · BROWSER_UA R43',
      'Merge key: TICKER (not CIK) — no suppression · no tradeability filtering',
      'Total: ~44K-45K tickers (was 10,365 exchange-only)',
      '── ISO 10383 MIC LOOKUP ──────────────────────────────────────────',
      'iso20022.org SWIFT CSV (free · no auth) → EXCHANGEMIC + EXCHANGENAME columns',
      '_fetchMicLookup() + _resolveMic() added',
      '── 5 NEW COLUMNS ────────────────────────────────────────────────',
      '+EXCHANGEMIC +EXCHANGENAME +EMPLOYEES +INVESTORURL +OTCTIER (total 31 cols)',
      'EMPLOYEES = NULL until OCI-5E · INVESTORURL = NULL until OCI-5E',
      'OTCTIER = NULL until FINRA developer.finra.org credentials → vault',
      '── 4-SOURCE VALUE ────────────────────────────────────────────────',
      'S2 adds 28K mutual fund share classes: VTSAX FXAIX PIMCO closed-end funds',
      'S3+S4 upgrade FUND-exchange tickers with correct product names + real exchange',
      'sub.tickers[] expansion: preferred shares + warrants from submissions API',
    ],
    prereqs: ['SIGNAL-DECOUPLE complete ✅', 'DOC-SYNC-2 complete ✅'],
    doD: [
      'sectorMappingPipeline.js node --check CLEAN ✅',
      '4-source merge working: ~44K-45K total tickers',
      'ISO MIC lookup loading ~2400 entries from iso20022.org',
      'S2 field=symbol correctly read (not ticker)',
    ],
    smokeChecks: [
      'grep -q "company_tickers_mf" backend/jobs/sectorMappingPipeline.js',
      'grep -q "nasdaqlisted" backend/jobs/sectorMappingPipeline.js',
      'grep -q "otherlisted" backend/jobs/sectorMappingPipeline.js',
      'grep -q "_fetchMicLookup" backend/jobs/sectorMappingPipeline.js',
      'grep -q "EXCHANGEMIC" backend/jobs/sectorMappingPipeline.js',
    ],
    files: [
      'backend/jobs/sectorMappingPipeline.js',
      '.claude/project-knowledge/PIPELINE_STATUS.md',
      '.claude/project-knowledge/ARCH.md',
      '.claude/project-knowledge/FEATURE_INVENTORY.md',
    ],
    vendorDeps: [
      { name: 'iso20022.org SWIFT MIC CSV', plan: 'Free · ISO 10383 standard', status: 'Monthly updated · no auth ✅' },
      { name: 'OpenFIGI API', plan: 'Free · MIT license · commercial use permitted', status: 'OPENFIGI_API_KEY vault ✅' },
    ],
  },

  {
    id: 'sector-mapping-modular',
    sessionNumber: 'SECTOR-MAPPING-MODULAR',
    name: 'sectorMappingPipeline Modular Rewrite — 5 Files + F1-F11 Bugs Fixed + Phase 3f',
    status: 'done',
    phase: 'Phase 4 — Pipeline Data Layer',
    date: 'June 7, 2026',
    commit: '— pending (DDL + deploy required before PIPELINE-RELOAD)',
    items: [
      '── ROOT CAUSE OF THIS SESSION ───────────────────────────────────',
      '3,355-line monolith: 15 view calls to read, duplicate functions invisible across 260-line gap',
      'F3 (duplicate _parseMicCsv): SyntaxError at require() — pipeline could not start despite node --check passing',
      'str.replace attempt in prior session destroyed 847 lines — full split was the only safe path',
      '── 5-FILE MODULAR SPLIT ─────────────────────────────────────────',
      'sectorMappingClassify.js (403 lines): pure functions · zero I/O · zero DB',
      '  F1: 1 correct _deriveSecurityType (ULTRAPRO SHORT before ULTRAPRO · bondCtx · BEAR NX)',
      '  F2: 1 correct _deriveAccessTier (FUND gate checks both ex=FUND AND st=fund)',
      '  F4: 1 correct _classifyExchange (BATS→CBOE · AMEX→NYSE)',
      'sectorMappingHttp.js (557 lines): HTTP helpers · OpenFIGI · MIC lookup',
      '  F3: exactly 1 _parseMicCsv definition (pipeline was unlaunchable)',
      '  https shadow removed from _fetchOpenFIGI loop',
      'sectorMappingGeo.js (581 lines): country/subdivision lookups · all 229 countries',
      '  F7: country_subdivision (229 countries, dr5hn) replaces us_state (US only)',
      '  F11: stale comments removed',
      'sectorMappingSQL.js (798 lines): all SQL + DB phases',
      '  F5: ipoDate removed from pipeline entirely (OCI-5E owns this)',
      '  F6: businessState uses stateOrCountry (2-char) not stateOrCountryDescription (full name)',
      '  F10: 404 overwrite guard — existingCiks.has check before subCache.set',
      '  Phase 3f _repairWrongValues: targeted UPDATE · zero HTTP · runs every run',
      'sectorMappingPipeline.js (1182 lines): orchestrator',
      '  F8: isSectorStale staleness condition added',
      '  F9: isNameChanged staleness condition added',
      '  isSecTypeStale: etf_leveraged/etf_inverse re-classify with fixed F1 function',
      '── ALL 5 FILES ──────────────────────────────────────────────────',
      'node --check CLEAN · 0 duplicate functions · 0 cross-imports · R34 compliant',
      '── NEW DDL REQUIRED ─────────────────────────────────────────────',
      'country_subdivision (PK: countryCode+subdivisionCode) — replaces us_state',
      'exchange_mic (PK: mic) — ISO 10383 reference',
      'pipeline_meta (PK: metaKey) — ETag + OpenFIGI tracking',
      '── ARCHITECTURE DECISIONS (permanent) ───────────────────────────',
      'ipoDate: removed from pipeline — belongs in OCI-5E (XBRL dei:EffectiveDateOfRegistration)',
      'businessState: stores 2-char code (CA, NY) not full description (California)',
      'country_subdivision: all 229 countries (dr5hn) — zero hardcoded country/state logic',
      'Phase 3f: repairs wrong non-null values every run (accessTier FUND→correct · businessState reset)',
      '── FOUR-PRODUCT VALUE ───────────────────────────────────────────',
      'Product 1: 13 security types on screener · businessState filter works correctly · MIC on exchange cards',
      'Product 2 (D3): FUND tickers correctly classified → congressional trade signal correctly gated',
      'Product 3 (Inner Circle): securityType classification correct for all rotation signals',
      'Product 4 (Commercial): accessTier correct for all 40K+ tickers in signal feed',
    ],
    prereqs: [
      'SECTOR-REARCH complete ✅ (4-source merge base)',
      'SIGNAL-DECOUPLE complete ✅',
    ],
    doD: [
      'All 5 files node --check CLEAN ✅',
      'Zero duplicate functions across all 5 files ✅',
      'Zero cross-imports between module files ✅',
      'DDL run in Database Actions (country_subdivision + exchange_mic + pipeline_meta)',
      'All 5 files deployed to VM-PIPELINE',
      'Full run: runAll({full:true}) completes — ~40K-45K rows',
      'All 10 acceptance criteria pass (Test 10 added for modular rewrite)',
    ],
    smokeChecks: [
      'test -f backend/jobs/sectorMappingClassify.js',
      'test -f backend/jobs/sectorMappingHttp.js',
      'test -f backend/jobs/sectorMappingGeo.js',
      'test -f backend/jobs/sectorMappingSQL.js',
      'grep -q "sectorMappingClassify" backend/jobs/sectorMappingPipeline.js',
      'grep -q "sectorMappingHttp" backend/jobs/sectorMappingPipeline.js',
      'grep -q "sectorMappingGeo" backend/jobs/sectorMappingPipeline.js',
      'grep -q "sectorMappingSQL" backend/jobs/sectorMappingPipeline.js',
      '! grep -q "EDGAR_IPO_SEARCH" backend/jobs/sectorMappingSQL.js',
      '! grep -q "stateOrCountryDescription" backend/jobs/sectorMappingPipeline.js',
      'grep -q "_repairWrongValues" backend/jobs/sectorMappingSQL.js',
      'grep -q "country_subdivision" backend/jobs/sectorMappingGeo.js',
    ],
    files: [
      'backend/jobs/sectorMappingClassify.js',
      'backend/jobs/sectorMappingHttp.js',
      'backend/jobs/sectorMappingGeo.js',
      'backend/jobs/sectorMappingSQL.js',
      'backend/jobs/sectorMappingPipeline.js',
      '.claude/project-knowledge/PIPELINE_STATUS.md',
      '.claude/project-knowledge/ACCEPTANCE_CRITERIA.md',
      '.claude/project-knowledge/DB_SCHEMA.md',
      '.claude/project-knowledge/ARCH.md',
      '.claude/project-knowledge/FEATURE_INVENTORY.md',
      '.claude/project-knowledge/HEAD.md',
      '.claude/HANDOVER.md',
      '.claude/project-knowledge/CLAUDE_SESSION_PROTOCOL.md',
    ],
    vendorDeps: [],
  },

  {
    id: 'congress-members-self-heal',
    sessionNumber: 'CONGRESS-MEMBERS-SELF-HEAL',
    name: 'Source-vs-DB Self-Healing — congressMembersPipeline + congressCommitteePipeline + memberResolver',
    status: 'done',
    phase: 'Phase 4 — Pipeline Data Layer',
    date: 'June 13-14, 2026',
    commit: '— pending (delivered for manual review, not yet pushed)',
    items: [
      '── PERMANENT PATTERN ─────────────────────────────────────────────',
      'Source-vs-DB sync (not timestamp-based) for all 4 congress tables',
      'A row is correct iff it matches what the source says should exist right now',
      'ADD (WHEN NOT MATCHED) + UPDATE (WHEN MATCHED, ALL fields) + DELETE (not in source)',
      'updatedAt<runStart comparison is insufficient — deletes valid rows that failed to refresh',
      '── isCommitteeChair ──────────────────────────────────────────────',
      'congressCommitteePipeline.js is SOLE owner — definitive post-loop 2-UPDATE sync',
      'Order-independent — reads final congress_member_committees state, not per-committee',
      'Fixed real bug: per-committee processing order affected result for multi-committee members',
      '── per-term party ────────────────────────────────────────────────',
      '_resolvePartyForTerm: party is a property of a TERM, not a member',
      'Fixes any party-switcher historical accuracy',
      '── congress_member_terms ─────────────────────────────────────────',
      'Table existed in live DB, was missing from DB_SCHEMA.md entirely — added this session',
      '── RESULTS (first reload) ────────────────────────────────────────',
      '537 congress_members · 2,293 inactive_congress_members',
      '5,566 congress_member_terms · 1,329 congress_member_committees',
      '17-test regression suite (P01-P03, N01-N14b) — 12/15 PASS, fixes applied for 3 failures + 4 new gaps',
    ],
    prereqs: [
      'SECTOR-MAPPING-MODULAR complete ✅',
    ],
    doD: [
      'congressMembersPipeline.js + congressCommitteePipeline.js + memberResolver.js node --check CLEAN',
      '17-test regression suite re-run with all fixes — 17/17 expected, not yet re-confirmed',
      'DB_SCHEMA.md updated: congress_member_terms added, inactive_congress_members PK corrected',
    ],
    smokeChecks: [
      'grep -q "_resolvePartyForTerm" backend/jobs/congressMembersPipeline.js',
      'grep -q "processedCommitteeNames" backend/jobs/congressCommitteePipeline.js',
      '! grep -q "bioguideId)" backend/jobs/memberResolver.js',
    ],
    files: [
      'backend/jobs/congressMembersPipeline.js',
      'backend/jobs/congressCommitteePipeline.js',
      'backend/jobs/memberResolver.js',
      'CONGRESS_PIPELINE_TESTS.sql',
      '.claude/project-knowledge/DB_SCHEMA.md',
      '.claude/project-knowledge/HEAD.md',
    ],
    vendorDeps: [],
  },

  {
    id: 'senate-house-capture-transform-enrich-redesign',
    sessionNumber: 'SENATE-HOUSE-CAPTURE-TRANSFORM-ENRICH-REDESIGN',
    name: 'Congressional Pipeline Architecture Redesign — Capture → Transform → Enrich',
    status: 'done',
    phase: 'Phase 4 — Pipeline Data Layer',
    date: 'June 19-20, 2026',
    commit: '— pending (delivered for manual review, NOT VALIDATED against real data)',
    items: [
      '── ROOT CAUSE OF THIS SESSION ───────────────────────────────────',
      '47,814-row full-enumeration audit found 9 confirmed bugs in senatePipeline.js',
      'housePipeline.js independently re-implemented and re-broke the same 3 bug classes',
      'Every fix required full TRUNCATE + live re-scrape — no recoverable intermediate state',
      '── THREE-STAGE ARCHITECTURE (permanent) ─────────────────────────',
      'CAPTURE: senatePipeline.js + housePipeline.js (rewritten) → raw tables, zero business logic',
      'TRANSFORM: congressionalTransformPipeline.js + congressionalTransformHelpers.js (new)',
      'ENRICH: congressionalDataQualityPipeline.js + tickerResolver.js (new)',
      '── ID REDEFINITION ───────────────────────────────────────────────',
      'congressional_trades.id: SHA-1 hash → deterministic composite string (chamber:sourceRef:rowIndex)',
      'Fixes false-duplicate-on-reprocess flaw in the old SHA-1 approach',
      '── ASSET ALLOWLIST ───────────────────────────────────────────────',
      'stock/option/fund/tbill/bond/crypto/commodity ONLY — not a denylist',
      'Unseen categories default excluded (caught Education Savings Plans, Farm/Ranch mid-session)',
      '── 4-TIER TICKER RESOLUTION ──────────────────────────────────────',
      'Tier 1-2 auto-applied (exact/extracted) · Tier 3-4 never auto-applied (fuzzy/Yahoo)',
      'yahoo-finance2 npm library for Tier 4 — confirmed working live against real network',
      '── VM-PIPELINE INFRASTRUCTURE ────────────────────────────────────',
      'Node.js 20.20.2 → 22.23.0 upgraded (required for yahoo-finance2 v3)',
      '11-day-stuck apt-daily.service found and resolved (unrelated pre-existing issue)',
      '── INCIDENT: D3_INTELLIGENCE_FRAMEWORK.md RECOVERED ─────────────',
      'Permanent Layer 3 doc found accidentally removed from repo, recovered from owner archive',
      '── STANDING RULE ESTABLISHED ─────────────────────────────────────',
      'Full-enumeration validation (every distinct real value, zero sampling) now required for',
      'any full data export — found 5 Senate bugs a targeted forensic pass had missed entirely',
      '── NOT PRODUCTION-CONFIRMED ──────────────────────────────────────',
      'Senate Capture backfill running at session close, completion unconfirmed',
      'Transform/Enrich pipelines never run against real production data — fixtures only',
      'housePipeline.js Capture rewrite not re-validated against real PDFs',
      'New pipeline-server.js deployment to VM-PIPELINE not confirmed',
    ],
    prereqs: [
      'CONGRESS-MEMBERS-SELF-HEAL complete ✅',
    ],
    doD: [
      'All 8 files node --check CLEAN ✅ (functionally tested against constructed fixtures, NOT real data)',
      'architecture_ddl_v1.sql applied and confirmed live ✅',
      'Senate Capture full historical backfill completes — NOT confirmed this session',
      'congressionalTransformPipeline.js runs against real senate_filings_raw — NOT done this session',
      'congressionalDataQualityPipeline.js runs against real data, Tier 4 fires at volume — NOT done this session',
      'housePipeline.js re-validated against real PDFs — NOT done this session',
      'Full-enumeration audit applied to House — NOT done this session',
      'New pipeline-server.js deployed and PM2 resumed — NOT done this session',
    ],
    smokeChecks: [
      'test -f backend/jobs/congressionalTransformPipeline.js',
      'test -f backend/jobs/congressionalTransformHelpers.js',
      'test -f backend/jobs/congressionalDataQualityPipeline.js',
      'test -f backend/jobs/tickerResolver.js',
      'grep -q "senate_filings_raw" backend/jobs/senatePipeline.js',
      'grep -q "house_filings_raw" backend/jobs/housePipeline.js',
      '! grep -q "_detectClusters" backend/jobs/senatePipeline.js',
    ],
    files: [
      'backend/jobs/senatePipeline.js',
      'backend/jobs/housePipeline.js',
      'backend/jobs/congressionalTransformPipeline.js',
      'backend/jobs/congressionalTransformHelpers.js',
      'backend/jobs/congressionalDataQualityPipeline.js',
      'backend/jobs/tickerResolver.js',
      'backend/pipeline-server.js',
      'architecture_ddl_v1.sql',
      '.claude/project-knowledge/DB_SCHEMA.md',
      '.claude/project-knowledge/PIPELINE_STATUS.md',
      '.claude/project-knowledge/OCI_CONFIG.md',
      '.claude/project-knowledge/HEAD.md',
      '.claude/project-knowledge/ARCH.md',
      '.claude/ARCHITECTURE.md',
      '.claude/PIPELINE_REFERENCE.md',
      '.claude/D3_INTELLIGENCE_FRAMEWORK.md',
      '.claude/HANDOVER.md',
    ],
    vendorDeps: ['yahoo-finance2'],
  },

  {
    id: 'house-capture-format-fix',
    sessionNumber: 'HOUSE-CAPTURE-FORMAT-FIX',
    name: 'House Paper-PTR OCR Capture Pipeline Hardening',
    status: 'done',
    phase: 'Phase 4 — Pipeline Data Layer',
    date: 'July 13, 2026',
    commit: 'c621efb',
    items: [
      '── NOT PREVIOUSLY IN THIS FILE ───────────────────────────────────',
      'Backfilled retroactively during OPS-BOUNDARY-DOC-SYNC (July 2026) — this session existed',
      'in HEAD.md and DB_SCHEMA.md already but had zero entry here until now, a real gap',
      '── SCOPE ──────────────────────────────────────────────────────────',
      'House-only by owner instruction, Senate explicitly out of scope this session',
      '14 confirmed bugs found and fixed (BUG-16 through BUG-29), validated against real filings',
      '── MOST CONSEQUENTIAL FIX ─────────────────────────────────────────',
      'OCR fallback trigger required hadContent===true, never true for genuinely scanned',
      'zero-text-layer PDFs — entire Tesseract OCR path was unreachable dead code in production',
      'Confirmed via real production log: 0/459,217 rows had parsedBy=\'tesseract\' before the fix',
      'Fixed: trigger fallback on empty rawTxList instead — covers both real failure cases',
      '── OTHER FIXES THIS SESSION ───────────────────────────────────────',
      'Template A row-boundary: measured 119px fixed grid pitch replaces unreliable Hough detection',
      'Tesseract PSM mode 7→4 (wrapped company names were clipping/dropping)',
      'Owner-column (JT/SP/DC) correction relocated Capture→Transform after 2 design iterations',
      'Page-orientation correction unified across two functions that both needed it',
      'PDFFORMAT field made reliable for skip cases (was silently defaulting incorrectly)',
      '── NOT VALIDATED / STILL OPEN AT SESSION CLOSE ───────────────────',
      'house_filings_raw TRUNCATED, House-only re-capture running (24-48hr expected) — status',
      'not confirmed as of OPS-BOUNDARY-DOC-SYNC (this entry\'s own backfill session)',
      'BUG-30 through BUG-37 (tickerResolver NULL-exclusion, capture_manual_review DDL,',
      'review_queue_v REVIEWID fix) referenced in later handover material but the exact commit',
      'containing them is unconfirmed — see HEAD.md\'s own flagged 9d47777 gap',
    ],
    prereqs: [
      'SENATE-HOUSE-CAPTURE-TRANSFORM-ENRICH-REDESIGN complete ✅',
    ],
    doD: [
      '14 bugs (BUG-16→29) fixed and validated against real filings, not test fixtures ✅',
      'house_filings_raw TRUNCATED and re-capture started — completion NOT confirmed',
      'Senate deliberately NOT touched this session, per explicit owner instruction ✅',
    ],
    smokeChecks: [
      'grep -q "PYTHON_PARSER" backend/jobs/housePipeline.js',
      'grep -q "spawnSync" backend/jobs/housePipeline.js',
      'grep -q "pdfjs-dist" backend/jobs/housePipeline.js',
      'grep -q "pdfplumber" backend/jobs/parse_house_pdf.py',
    ],
    files: [
      'backend/jobs/housePipeline.js',
      'backend/jobs/parse_house_pdf.py',
      'backend/jobs/houseTransformPipeline.js',
      'backend/jobs/congressionalTransformHelpers.js',
    ],
    vendorDeps: [],
  },

  {
    id: 'ops-boundary-doc-sync',
    sessionNumber: 'OPS-BOUNDARY-DOC-SYNC',
    name: 'Marketpulze-ops Decoupling — Documentation Reference (Pulze side)',
    status: 'done',
    phase: 'Phase 4 — Pipeline Data Layer', // documentation-only; filed here since it ran during
                                             // House pipeline testing downtime, not a distinct phase
    date: 'July 2026',
    commit: '1554e93',
    items: [
      '── WHY THIS SESSION EXISTED ───────────────────────────────────────',
      'Marketpulze-ops (staff/admin portal) had ZERO references anywhere in Pulze\'s own docs,',
      'confirmed by direct search — despite being restored to independent architecture June 30,',
      '2026. Run during House pipeline testing downtime, not a distinct pipeline session itself.',
      '── WHAT WAS DOCUMENTED (pointer-only — ops\'s own state tracked in its own project) ──',
      'ARCH.md/ARCHITECTURE.md: corrected stale admin.marketpulze.com line, added OPS PORTAL',
      'BOUNDARY reference section (process/DB/access boundary facts only)',
      'OCI_CONFIG.md/OCI_CONFIGURATION.md: MP_OPERATIONS/MP_BILLING schema rows + admin-api DNS',
      'rows (both ops-owned, same ATP-1/same VMs) + Pulze\'s own new read-only grant into',
      'MP_REFERENCE.PROJECT_PIPELINE_RUNS/PIPELINE_ALERTS (column schema NOT guessed, flagged',
      'unconfirmed in DB_SCHEMA.md pending a live query)',
      'CLAUDE.md/ROADMAP.md/FEATURE_INVENTORY.md/PRODUCT_COMPASS.md: PRODUCT DECOUPLE section,',
      'corrected ADMIN-PORTAL line, admin.js/AdminPage.jsx read-only-mirror entry, ops-fit note',
      '── UNRELATED FIXES FOUND ALONG THE WAY ────────────────────────────',
      'smoke-test.sh: 2 stale checks fixed (admin.js config schema removed during the ADMIN',
      'DECOUPLING; housePipeline.js pdf-parse check tested a library no longer used at all —',
      'real path is pdfjs-dist + Python OCR fallback, confirmed via this session\'s own BUG-31 fix)',
      'smokeCount self-consistency regex bug fixed — was structurally unable to ever pass after',
      'the two-pass auto-sync mechanism ran, would have blocked every future docs:sync commit',
      '3 new architecture diagrams committed to docs/ (overall/pipeline/screener)',
      '── UNRESOLVED GAP, FOUND NOT FIXED ─────────────────────────────────',
      'Actual parent commit of 1554e93 was 9d47777, not c621efb as HEAD.md previously claimed —',
      'at least one commit happened that was never logged in HEAD.md/CHANGELOG.md/this file.',
      'Not closed this session — needs a real git log against the repo to resolve.',
      '── NOT DONE THIS SESSION (flagged, not silently deferred) ─────────',
      'sessions-active.js (this file) was not available until a follow-up upload — these two',
      'entries (this one and house-capture-format-fix above) were added in that follow-up, not',
      'the original OPS-BOUNDARY-DOC-SYNC commit itself',
      'The separate, larger staleness gap (most Layer 0/3 files never updated for the July 13',
      'HOUSE-CAPTURE-FORMAT-FIX session either) remains open — belongs to the parallel',
      'pipeline-validation work, out of scope for this documentation-only thread',
    ],
    prereqs: [
      'HOUSE-CAPTURE-FORMAT-FIX complete ✅',
    ],
    doD: [
      'Zero Marketpulze-ops references in Pulze docs → now documented in 7 files ✅',
      'DB_SCHEMA.md/OCI_CONFIG.md/OCI_CONFIGURATION.md updated with ops-boundary facts, flagged',
      'unconfirmed items explicitly rather than guessed ✅',
      'smoke-test.sh: 516/516 clean, two-pass auto-sync confirmed working ✅',
      '3 architecture diagrams committed ✅',
    ],
    smokeChecks: [
      'grep -q "OPS_PORTAL_URL" src/pages/AdminPage.jsx',
      'test $(grep -cE "^router\\.post\\(.*movedToOps\\(" backend/routes/admin.js) -eq 8',
      'test -f docs/PULZE_OVERALL_ARCHITECTURE.svg',
      'test -f docs/PULZE_PIPELINE_ARCHITECTURE.svg',
      'test -f docs/PULZE_SCREENER_ARCHITECTURE.svg',
    ],
    files: [
      '.claude/project-knowledge/ARCH.md',
      '.claude/ARCHITECTURE.md',
      '.claude/project-knowledge/OCI_CONFIG.md',
      '.claude/OCI_CONFIGURATION.md',
      '.claude/project-knowledge/DB_SCHEMA.md',
      '.claude/project-knowledge/CLAUDE.md',
      '.claude/project-knowledge/ROADMAP.md',
      '.claude/project-knowledge/FEATURE_INVENTORY.md',
      '.claude/project-knowledge/PRODUCT_COMPASS.md',
      '.claude/HEAD.md',
      '.claude/CHANGELOG.md',
      '.claude/SESSION_LOCK.md',
      'scripts/smoke-test.sh',
      'docs/PULZE_OVERALL_ARCHITECTURE.svg',
      'docs/PULZE_PIPELINE_ARCHITECTURE.svg',
      'docs/PULZE_SCREENER_ARCHITECTURE.svg',
    ],
    vendorDeps: [],
  },

    {
    id: 'congressional-signal',
    sessionNumber: 'CONGRESSIONAL-SIGNAL',
    name: 'Congressional Signal Pipeline — congressionalSignalPipeline.js',
    status: 'planned',
    phase: 'Phase 4 — Pipeline Data Layer',
    items: [
      '── WHY THIS COMES BEFORE REFACTOR-EDGAR ────────────────────────',
      'PIPELINE-RELOAD validates the data. This session validates the signals.',
      'Without validated signals, REFACTOR-EDGAR is building on unproven foundation.',
      'Congressional signal IS the moat of this product. Build and validate it first.',
      '── WHAT IT BUILDS ─────────────────────────────────────────────',
      'congressionalSignalPipeline.js — reads validated Oracle SQL data',
      'Three detection functions:',
      '  _detectClusters: 14-day window · 3+ members same ticker/direction',
      '    → writes congressional_signal to signalWriter',
      '  _detectRotationPattern: 30-day window · FLIGHT_TO_SAFETY/SAFETY_REVERSAL',
      '    → writes regime_rotation_signal to signalWriter',
      '  _detectCrossChamberAmplifier: 30-day bicameral · highest conviction',
      '    → writes congressional_signal with tier:1 override to signalWriter',
      '── MOAT COLUMN INPUTS ─────────────────────────────────────────',
      'owner column: filter SELF + JOINT for highest conviction cluster signals',
      'filingLagDays: staleness penalty (>45 days applies decay), >180 exclude from L6',
      '── FIVE-SOURCE CONFLUENCE SCORE ──────────────────────────────',
      'After this session, confluence_score can be computed:',
      '  congressional conviction × owner × lagPenalty',
      '  + committeeRole multiplier (2.0x chair · 1.7x ranking · 1.3x member)',
      '  + fec_member_funding topSector1Pct (% from sector = legislative intent)',
      '  + lobbying_disclosures totalAmount (sector pressure)',
      '  + federal_register_orders signalTier (regulatory action)',
      '── FOUR-PRODUCT VALUE ──────────────────────────────────────────',
      'Product 1: congressional signal clusters displayed on screener + stock pages',
      'Product 2 (D3): Scenario L (Congressional+Insider Convergence) reads this signal',
      'Product 3 (Inner Circle): rotation direction (TOWARD_SAFETY/FROM_SAFETY) displayed',
      'Product 4 (Commercial): signal feed includes congressional conviction tier',
    ],
    prereqs: [
      'PIPELINE-RELOAD all 9 acceptance criteria pass ✅ (owner confirms SQL results)',
      'Owner confirms congressional_trades row count + party_pct in Database Actions',
    ],
    doD: [
      'congressionalSignalPipeline.js deployed on VM-PIPELINE',
      'Cluster signals writing to signalWriter (Redis + ATP) ✅',
      'Rotation signals writing to signalWriter ✅',
      'Cross-chamber amplifier firing ✅',
      'Confluence score computable from all 5 sources ✅',
      'pm2 schedule: nightly after data pipelines complete ✅',
    ],
    smokeChecks: [
      'test -f backend/jobs/congressionalSignalPipeline.js',
      'grep -q "_detectClusters" backend/jobs/congressionalSignalPipeline.js',
      'grep -q "_detectCrossChamberAmplifier" backend/jobs/congressionalSignalPipeline.js',
      'grep -q "signalWriter" backend/jobs/congressionalSignalPipeline.js',
      '! grep -q "signalWriter" backend/jobs/senatePipeline.js',
    ],
    files: [
      'backend/jobs/congressionalSignalPipeline.js',
      'backend/server.js',
    ],
    vendorDeps: [],
  },

  {
    id: 'pipeline-reload',
    sessionNumber: 'PIPELINE-RELOAD',
    name: 'Pipeline Reload — Deploy Fixes + DDL + Full Reload + Validate',
    status: 'next',
    phase: 'Phase 4 — Pipeline Data Layer',
    items: [
      'PRE-RUN: Verify UQ_CONGRESSIONAL dropped in Database Actions (BLOCKER if present)',
      'PRE-RUN: Run DDL for country_subdivision + exchange_mic + pipeline_meta in Database Actions (BLOCKER for sectorMappingPipeline)',
      'PRE-RUN: Deploy 5 sectorMapping module files to VM-PIPELINE (scp to ~/app/backend/jobs/)',
      'PRE-RUN: node --check all 5 sectorMapping files on VM-PIPELINE after deploy',
      'PRE-RUN: git pull origin main on VM-PIPELINE + pm2 restart all',
      '1. sectorMappingPipeline FULL RUN — runAll({full:true}) required on first modular deploy (~90min)',
      '2. congressMembersPipeline (BUG 4+14 fixed — was crashing before SIGNAL-DECOUPLE)',
      '3. congressCommitteePipeline',
      '4. senatePipeline (rm congress_last_run.json first — full backfill)',
      '5. housePipeline overnight (rm house_state.json first — 2013-2026 backfill)',
      '6. lobbyingPipeline',
      '7. federalRegisterPipeline',
      '8. fecPipeline (use options.force=true — cycle skip guard now active from SIGNAL-DECOUPLE)',
      '9. Validate all 10 acceptance criteria SQL — see ACCEPTANCE_CRITERIA.md (Test 10 added SECTOR-MAPPING-MODULAR)',
      '   Paste ALL 10 results before declaring validated. Owner confirms each.',
      '10. Open REFACTOR-EDGAR only after all 10 tests pass',
    ],
    prereqs: [
      'SIGNAL-DECOUPLE committed ✅ (4459b30)',
      'DDL_MOAT_COLUMNS.sql applied ✅ (owner confirmed May 31)',
      'SECTOR-MAPPING-MODULAR committed (June 7, 2026) — 5 modular files + doc updates',
      'DDL for country_subdivision + exchange_mic + pipeline_meta run in Database Actions',
      '5 sectorMapping files deployed to VM-PIPELINE and node --check clean',
      'Verify UQ_CONGRESSIONAL dropped before running any pipeline',
      'OPENFIGI_API_KEY confirmed in vault on VM-PIPELINE',
    ],
    doD: [
      'All 10 acceptance criteria pass (ACCEPTANCE_CRITERIA.md)',
      'senate party_pct > 80% · house party_pct > 80%',
      'owner type distribution: SELF/SPOUSE/JOINT all present (not all SELF)',
      'filingLagDays not_null_pct > 70%',
      'edgar_events tier 100% populated · companyName ≠ ticker',
      'sector_mapping securityType: other < 300 rows · 13 types present · total ~40K-45K rows',
      'sector_mapping FUND rows: zero with accessTier=EXCHANGE (Phase 3f repair verified)',
      'sector_mapping businessState: 2-char codes only (no full descriptions)',
      'country_subdivision: > 5000 rows · 229 countries confirmed',
      'pipeline_errors: 0 unresolved',
    ],
    smokeChecks: [],
    files: [],
    vendorDeps: [],
  },

  {
    id: 'refactor-edgar',
    sessionNumber: 'REFACTOR-EDGAR',
    name: 'REFACTOR-EDGAR: Two Independent EDGAR Pipelines — Display + Signal',
    status: 'planned',
    phase: 'Phase 4 — Pipeline Data Layer',
    items: [
      '── WHY THIRD IN SEQUENCE (not first) ────────────────────────────',
      'Senate + House pipeline sessions prove VM-PIPELINE deployment pattern.',
      'OCI NoSQL is live and tested with congressional data before EDGAR writes to it.',
      'EDGAR is the most complex source — build it on proven infrastructure.',
      '── TWO INDEPENDENT PIPELINES (not one, not a shared engine) ─────',
      'Agreed architecture: each pipeline self-contained. R34 enforced.',
      '',
      'Pipeline 1: edgarDisplayPipeline.js (screener display data)',
      '  Write target: Oracle SQL ATP-1 MP_REFERENCE edgar_events (NOT OCI NoSQL — changed PIPELINE-VALIDATION)',
      '  What it stores: ticker · companyName · filingType · filedAt · accessionNo · SEC URL',
      '  What it does NOT store: raw XML · parsed content · signal scores',
      '  Cron: 7:00am ET Mon-Fri (pre-market 8-K context)',
      '  Retention: 2yr display-only forms · sector-aware signal-relevant forms',
      '',
      'Pipeline 2: edgarSignalPipeline.js (D3 conviction signals)',
      '  Write target: signalWriter → Redis Tier 0 + ATP-2 MP_ARCHIVE',
      '  What it stores: pre-computed conviction payload (NOT raw XML, NOT a link)',
      '  Processing at ingest: transaction value · role · tenure weight · $50K check ·',
      '    cluster detection · tier classification · outcome flag fields',
      '  Cron: 8:00pm ET Mon-Fri (full EDGAR pull)',
      '  Real-time: RSS feed poll every 10min for Tier 1 signals',
      '',
      '── OUTCOME FLAG FIELDS (add from day one — critical for L6) ─────',
      'outcomeConfirmed: false · outcomeType: null',
      'outcomeDays: null · outcomeReturn: null · retentionFlag: null',
      'These fields are null at ingest. OCI-OUTCOME-DETECTION fills them nightly.',
      'Records with outcomeConfirmed=true are PERMANENT in ATP-2 regardless of TTL.',
      '── CORRECT EDGAR SOURCES (never efts.sec.gov) ───────────────────',
      'Real-time:  SEC RSS https://www.sec.gov/cgi-bin/browse-edgar?action=getcurrent&output=atom',
      'Daily:      https://www.sec.gov/Archives/edgar/daily-index/{YEAR}/QTR{N}/crawler.json',
      'Historical: https://www.sec.gov/Archives/edgar/daily-index/bulkdata/submissions.zip',
      '── FORM 4 URL FIX (S11 — permanent) ────────────────────────────',
      'Primary: /edgar/data/{cik}/{accNorm}/form4.xml',
      'Fallback: /edgar/data/{cik}/{accNorm}/{accNorm}-index.json → parse filename',
      'NEVER guess a filename. Primary → fallback → stop.',
      'AccessionNo: always split(":")[0] before replace(/\\//g, "-")',
      '── FORM TYPES ───────────────────────────────────────────────────',
      'Display pipeline: 8-K · 10-Q · 10-K · S-3 · 424B4 · S-4 · SC TO-T · 6-K · 20-F · 40-F',
      'Signal pipeline:  Form 3/4/5/144 · SC 13D · SC 13G',
      '── S1-S11 RESILIENCE (inside each pipeline, not shared) ─────────',
      'S1: HTTP 500 retry 3× · S2: 429 exponential backoff · S3: timeout retry',
      'S4-S6: fetch failure logging · S7: NDJSON upsert queue on block storage',
      'S8: connection check before upsert · S9: resume from lastRunDate',
      'S10: EFTS total undercount acceptable · S11: accessionNo split(":")[0]',
      '── GITHUB ACTIONS DEPLOY JOB ────────────────────────────────────',
      'Add deploy-pipeline job to .github/workflows/deploy.yml in this session',
      'Secrets: SCANNER_VM_PIPELINE_HOST · SCANNER_VM_PIPELINE_USER · SCANNER_VM_PIPELINE_PRIVATE_SSH',
      '── GATE CONDITION ───────────────────────────────────────────────',
      'edgar_insider in OCI NoSQL and ATP-2 > 0 = REFACTOR-EDGAR done',
    ],
    prereqs: [
      'PIPELINE-RELOAD acceptance criteria pass — all 9 tests ✅',
      'CONGRESSIONAL-SIGNAL complete ✅ (congressionalSignalPipeline.js deployed)',
      'edgarDisplayPipeline writes Oracle SQL ATP-1 (not OCI NoSQL — changed PIPELINE-VALIDATION ✅)',
      'SIGNAL-DECOUPLE complete ✅ (BUG 2+15 fixed in edgarDisplayPipeline)',
      'ATP-2 wallet installed on VM-PIPELINE (needed for edgarSignalPipeline)',
      'GitHub Actions deploy-pipeline job: add in this session',
    ],
    doD: [
      'edgarDisplayPipeline.js self-contained · writes to OCI NoSQL edgar_events ✅',
      'edgarSignalPipeline.js self-contained · writes via signalWriter to ATP-2 ✅',
      'No pipelineEngine.js created ✅',
      'crawler.json as daily source (not efts.sec.gov) ✅',
      'form4.xml primary URL (not guessed filename) ✅',
      'outcomeConfirmed/outcomeType/outcomeDays/outcomeReturn in every record ✅',
      'edgar_insider in OCI NoSQL > 0 ✅ ← primary gate',
      'deploy-pipeline job in GitHub Actions ✅',
      'CI=true npm run build clean ✅',
    ],
    smokeChecks: [
      'test -f backend/jobs/edgarDisplayPipeline.js',
      'test -f backend/jobs/edgarSignalPipeline.js',
      '! test -f backend/services/pipelineEngine.js',
      'grep -q "crawler.json" backend/jobs/edgarDisplayPipeline.js',
      'grep -q "form4.xml" backend/jobs/edgarSignalPipeline.js',
      'grep -q "oracle-nosqldb\\|nosql" backend/jobs/edgarDisplayPipeline.js',
      'grep -q "signalWriter" backend/jobs/edgarSignalPipeline.js',
      'grep -q "outcomeConfirmed" backend/jobs/edgarSignalPipeline.js',
      '! grep -q "pipelineEngine" backend/jobs/edgarDisplayPipeline.js',
      '! grep -q "pipelineEngine" backend/jobs/edgarSignalPipeline.js',
      'grep -q "deploy-pipeline" .github/workflows/deploy.yml',
    ],
    files: [
      'backend/jobs/edgarDisplayPipeline.js',
      'backend/jobs/edgarSignalPipeline.js',
      'backend/server.js',
      '.github/workflows/deploy.yml',
    ],
    vendorDeps: [],
  },



  {
    id: 'oci-5a-alerts',
    sessionNumber: 'OCI-5A',
    name: 'OCI-5A: Personal Alert Service — Telegram + WhatsApp + SMS + OCI Email',
    status: 'planned',
    phase: 'Phase 4 — Pipeline Data Layer',
    items: [
      '── WHY AFTER SENATE + HOUSE ─────────────────────────────────────',
      'Alerts consume congressional signal data — data pipelines must be healthy first',
      'SAFETY_REVERSAL + FLIGHT_TO_SAFETY need real data to test end-to-end',
      '── alertService.js ──────────────────────────────────────────────',
      'sendTier1Alert()     → Telegram + WhatsApp + SMS simultaneously (immediate)',
      'sendMorningDigest()  → Telegram + WhatsApp + OCI Email (7:05am ET)',
      'sendEveningSummary() → Telegram + WhatsApp + OCI Email (8:30pm ET)',
      'All via built-in https — no new npm packages',
      'Graceful degradation: if one channel fails, others still fire',
      '── Tier 1 triggers ──────────────────────────────────────────────',
      'SAFETY_REVERSAL detected (congressional rotation — 2-6wk lead time)',
      'FLIGHT_TO_SAFETY detected (congressional rotation — macro bearish)',
      '8-K + ticker in member watchlist cluster',
      '8-K earnings surprise > 10%',
      'CFO/CEO departure (8-K 5.02) + prior insider selling (Form 4)',
      'SC 13D activist filing',
      'Form 4 cluster: 3 insiders same ticker < 7 days',
      'Bipartisan + bicameral congressional cluster (cross-chamber amplifier)',
      '── Delivery channels ────────────────────────────────────────────',
      'Telegram: rich markdown · instant · free',
      'WhatsApp: Twilio sandbox → production after Meta verification',
      'SMS: Twilio · ~$0.01/message',
      'OCI Email: B88523 (in PAYG) · 3,000/mo free · digest + summary only',
      '── Vault secrets (11 new in mp-vault-backend) ───────────────────',
      'mp-telegram-bot-token · mp-telegram-chat-id',
      'mp-twilio-account-sid · mp-twilio-auth-token',
      'mp-twilio-from-number · mp-twilio-whatsapp-from · mp-alert-sms-to · mp-alert-whatsapp-to',
      'mp-alert-email-to · mp-oci-smtp-user · mp-oci-smtp-pass',
    ],
    prereqs: ['OCI-5B-SENATE complete ✅', 'OCI-5B-HOUSE complete ✅'],
    doD: [
      'Telegram bot receives test message ✅',
      'WhatsApp (Twilio sandbox) receives test message ✅',
      'SMS received on personal mobile ✅',
      'OCI Email delivery working ✅',
      'SAFETY_REVERSAL Tier 1 alert fires end-to-end ✅',
      'Morning digest cron: 7:05am ET ✅',
      'Evening summary cron: 8:30pm ET ✅',
    ],
    smokeChecks: [
      'test -f backend/services/alertService.js',
      'grep -q "sendTier1Alert" backend/services/alertService.js',
      'grep -q "sendMorningDigest" backend/services/alertService.js',
      'grep -q "TELEGRAM" backend/services/alertService.js',
      'grep -q "twilio" backend/services/alertService.js',
      'grep -q "alertService" backend/jobs/edgarPipeline.js',
      'grep -q "alertService" backend/jobs/senatePipeline.js',
      'grep -q "alertService" backend/jobs/housePipeline.js',
    ],
    files: [
      'backend/services/alertService.js',
      'backend/jobs/edgarPipeline.js',
      'backend/jobs/senatePipeline.js',
      'backend/jobs/housePipeline.js',
      'backend/server.js',
    ],
    vendorDeps: [
      { name: 'Telegram Bot API', plan: 'Free', status: 'Setup before session: @BotFather' },
      { name: 'Twilio', plan: 'PAYG ~$1/mo', status: 'Setup before session: twilio.com' },
      { name: 'OCI Email Delivery', plan: 'Free (B88523 in PAYG)', status: 'Setup before session: OCI Console' },
    ],
  },

  {
    id: 'oci-5c-gov-data',
    sessionNumber: 'OCI-5C',
    name: 'OCI-5C: Government Money Flow + Macro Regime Data',
    status: 'planned',
    phase: 'Phase 4 — Pipeline Data Layer',
    items: [
      '── WHY ──────────────────────────────────────────────────────────',
      'Government contracts move stocks before press releases — structural edge',
      'Macro regime (rates/CPI) determines sector rotation — L3 context layer',
      'Short interest identifies forced-covering candidates for D3',
      '── USASpending.gov API ───────────────────────────────────────────',
      'govContractsPipeline.js: awards > $10M → gov_contracts (ATP MP_REFERENCE)',
      'Free, no auth, daily updated, public domain',
      'Map recipient company name → ticker (CIK map from OCI-5)',
      'D3 signal: contract_value / annual_revenue = revenue impact %',
      'Impact > 15% + not yet in 8-K → BULLISH pre-announcement signal',
      '── FRED API (Federal Reserve) ───────────────────────────────────',
      'fredPipeline.js: Fed funds rate · CPI · GDP · yield curve → macro_indicators',
      'Free, no auth required (fred.stlouisfed.org/docs/api/fred/)',
      '2yr/10yr spread (recession predictor) · regime classification',
      'D3 regime signal: rising_rates · falling_rates · expansion · contraction',
      '── FINRA Short Interest ─────────────────────────────────────────',
      'shortInterestPipeline.js: bimonthly FINRA data → short_interest (ATP MP_REFERENCE)',
      'High short interest > 20% float + positive catalyst = squeeze candidate',
    ],
    prereqs: ['OCI-5A complete ✅'],
    doD: [
      'gov_contracts pipeline running + data in ATP ✅',
      'macro_indicators pipeline running + data in ATP ✅',
      'short_interest pipeline running + data in ATP ✅',
      'D3 regime signal firing via signalWriter ✅',
    ],
    smokeChecks: [
      'test -f backend/jobs/govContractsPipeline.js',
      'test -f backend/jobs/fredPipeline.js',
      'test -f backend/jobs/shortInterestPipeline.js',
      'grep -q "gov_contracts" backend/jobs/govContractsPipeline.js',
      'grep -q "macro_indicators" backend/jobs/fredPipeline.js',
    ],
    files: [
      'backend/jobs/govContractsPipeline.js',
      'backend/jobs/fredPipeline.js',
      'backend/jobs/shortInterestPipeline.js',
      'backend/server.js',
    ],
    vendorDeps: [
      { name: 'USASpending.gov API', plan: 'Free public domain', status: 'No auth required' },
      { name: 'FRED API', plan: 'Free', status: 'fred.stlouisfed.org/docs/api/fred/' },
      { name: 'FINRA Short Interest', plan: 'Free', status: 'Bimonthly bulk files' },
    ],
  },

  {
    id: 'oci-5d-catalysts',
    sessionNumber: 'OCI-5D',
    name: 'OCI-5D: Sector Catalyst Layer — FDA + ClinicalTrials + Index Events',
    status: 'planned',
    phase: 'Phase 4 — Pipeline Data Layer',
    items: [
      '── WHY ──────────────────────────────────────────────────────────',
      'Pre-catalyst positioning = edge before crowd. PDUFA dates known months in advance.',
      'Index inclusion/exclusion = forced buying/selling — highest predictability.',
      '── FDA PDUFA Calendar ────────────────────────────────────────────',
      'fdaPipeline.js: FDA decision dates → fda_events (ATP MP_REFERENCE)',
      '── ClinicalTrials.gov API ────────────────────────────────────────',
      'clinicalTrialsPipeline.js: Phase 2/3 completions → clinical_trials',
      '── Index Events ─────────────────────────────────────────────────',
      'Russell/S&P reconstitution: forced buying of newly included stocks',
      'indexEventsPipeline.js → index_events',
    ],
    prereqs: ['OCI-5C complete ✅'],
    doD: [
      'FDA PDUFA pipeline running + data in ATP ✅',
      'ClinicalTrials pipeline running + data in ATP ✅',
      'Index events pipeline running + data in ATP ✅',
    ],
    smokeChecks: [
      'test -f backend/jobs/fdaPipeline.js',
      'test -f backend/jobs/clinicalTrialsPipeline.js',
      'grep -q "fda_events" backend/jobs/fdaPipeline.js',
      'grep -q "clinical_trials" backend/jobs/clinicalTrialsPipeline.js',
    ],
    files: [
      'backend/jobs/fdaPipeline.js',
      'backend/jobs/clinicalTrialsPipeline.js',
      'backend/jobs/indexEventsPipeline.js',
      'backend/server.js',
    ],
    vendorDeps: [
      { name: 'FDA.gov PDUFA calendar', plan: 'Free public domain', status: 'No auth required' },
      { name: 'ClinicalTrials.gov API v2', plan: 'Free public domain', status: 'clinicaltrials.gov/api/v2/studies' },
    ],
  },

  {
    id: 'oci-5e-financial-health',
    sessionNumber: 'OCI-5E',
    name: 'OCI-5E: Financial Health Layer — XBRL + Leadership Patterns + Proxy',
    status: 'xbrl-built-leadership-proxy-not-started',
    phase: 'Phase 4 — Pipeline Data Layer',
    items: [
      '── WHY ──────────────────────────────────────────────────────────',
      'XBRL data is Pillar 1 of the 4-pillar D3 conviction score.',
      '10-Q/10-K accessionNos already stored in edgar_events from OCI-5.',
      '── XBRL Extraction — BUILT Aug 2026, real production data ────────',
      'CORRECTED: file is xbrlFinancialsPipeline.js, NOT xbrlPipeline.js (this',
      '  entry itself had the wrong name — same wrong name pipeline-server.js\'s',
      '  own commented-out stub line ~310 still has; fix both together).',
      'CORRECTED: writes to Oracle SQL tables, NOT a MongoDB-style "Collection"',
      '  — this entry predates the R40 Oracle-SQL-only architecture decision.',
      'Real tables: xbrl_financials (raw capture, ~50 GAAP fields) +',
      '  xbrl_financials_computed (EBIT/EBITDA/ratios/growth) +',
      '  xbrl_concept_tag_map (concept->tag mapping, both us-gaap and ifrs-full,',
      '  DB-backed not hardcoded) + xbrl_concept_tag_gaps (self-maintaining',
      '  taxonomy/coverage gap review queue).',
      'Also built, not in original scope: SUPERSEDE amendment architecture',
      '  (10-Q/A, 10-K/A, isSuperseded/supersededById/amendsId), IFRS taxonomy',
      '  support (TSM/SAP/NVO/SONY-class ADRs — confirmed via TSM\'s real filed',
      '  20-F that these report under ifrs-full, not us-gaap).',
      'Real production run: 200,089 rows, 0 compute errors. NOT yet cron-',
      '  scheduled (pipeline-server.js line ~310 still commented out) and NOT',
      '  yet run end-to-end with capture+compute+amendments+taxonomy combined.',
      'Full detail: HANDOVER.md OCI-5E XBRL FINANCIALS arc section.',
      '── Leadership Event Patterns — NOT STARTED, still fully planned ──',
      'Parse 8-K Item 5.02 records · multi-departure detection',
      'CEO/CFO departure patterns → D3 L3 context',
      '── DEF 14A Proxy — NOT STARTED, still fully planned ───────────────',
      'Exec tenure · compensation structure → proxy_summary',
    ],
    prereqs: ['OCI-5D complete ✅'],
    doD: [
      'xbrl_financials populated ✅ (200,089 rows, real production data, Aug 2026)',
      'xbrl_financials_computed populated ✅',
      'leadership_events pattern detection running — NOT STARTED',
      'proxy_summary populated — NOT STARTED',
    ],
    smokeChecks: [
      'test -f backend/jobs/xbrlFinancialsPipeline.js',
      'test -f backend/jobs/xbrlFinancialsSQL.js',
      'test -f backend/jobs/xbrlFinancialsComputePipeline.js',
      'test -f backend/jobs/xbrlFinancialsComputeSQL.js',
      'grep -q "xbrl_financials" backend/jobs/xbrlFinancialsSQL.js',
      '# leadership_events / proxy_summary checks NOT added — those files do not exist yet',
    ],
    files: [
      'backend/jobs/xbrlFinancialsPipeline.js',
      'backend/jobs/xbrlFinancialsSQL.js',
      'backend/jobs/xbrlFinancialsComputePipeline.js',
      'backend/jobs/xbrlFinancialsComputeSQL.js',
      'backend/jobs/xbrlFinancialsHttp.js',
      'backend/jobs/xbrlFinancialsFx.js',
      'backend/jobs/seedXbrlConceptTagMap.js',
      'backend/jobs/proxyPipeline.js — NOT YET BUILT, still planned only',
    ],
    vendorDeps: [
      { name: 'SEC EDGAR XBRL API', plan: 'Free public domain', status: 'data.sec.gov/api/xbrl/companyfacts — confirmed working, real production data' },
      { name: 'FRED (currency conversion)', plan: 'Free public domain', status: 'api.stlouisfed.org/fred — 23 currencies verified against FRED\'s own published series list' },
    ],
  },

  {
    id: 'oci-5f-earnings-intelligence',
    sessionNumber: 'OCI-5F',
    name: 'OCI-5F: Earnings Intelligence — Analyst Revisions + Transcripts',
    status: 'planned',
    phase: 'Phase 4 — Pipeline Data Layer',
    items: [
      '── Analyst Revision Clusters ────────────────────────────────────',
      '3+ analysts raising EPS same direction within 30 days → price follows',
      'Collection: analyst_revisions',
      '── Earnings Transcripts ─────────────────────────────────────────',
      'Source: Finnhub free tier (evaluate coverage first)',
      'Stage raw text: /mnt/mp-data/transcripts/ before ATP ingest',
      'Collection: earnings_transcripts',
      '── L3 Multi-Pillar Prompt Architecture ──────────────────────────',
      'Pillar 1: xbrl_financials · Pillar 2: edgar_events + leadership_events',
      'Pillar 3: edgar_insider + congressional_trades · Pillar 4: fda_events + gov_contracts',
      'Pillar 5: congressional rotation signal (regime_rotation_signal)',
    ],
    prereqs: ['OCI-5E: xbrl_financials populated ✅ (Aug 2026, real data) — Leadership Events/DEF 14A Proxy portions of OCI-5E NOT complete, but not needed for this session\'s own Pillar 1 dependency'],
    doD: [
      'analyst_revisions cluster detection running ✅',
      'earnings_transcripts pipeline running ✅',
      'L3 multi-pillar prompt template documented ✅',
    ],
    smokeChecks: [
      'test -f backend/jobs/analystRevisionsPipeline.js',
      'grep -q "analyst_revisions" backend/jobs/analystRevisionsPipeline.js',
      'grep -q "revision_cluster" backend/jobs/analystRevisionsPipeline.js',
    ],
    files: [
      'backend/jobs/analystRevisionsPipeline.js',
      'backend/jobs/transcriptPipeline.js',
      'backend/server.js',
    ],
    vendorDeps: [
      { name: 'Finnhub', plan: 'Free tier', status: 'Already in stack — evaluate transcript coverage' },
    ],
  },

  {
    id: 'oci-outcome-detection',
    sessionNumber: 'OCI-OUTCOME-DETECTION',
    name: 'OCI-OUTCOME-DETECTION: Nightly Outcome Labelling — L6 Training Corpus',
    status: 'planned',
    phase: 'Phase 4 — Pipeline Data Layer',
    items: [
      'Reads all signals in Oracle SQL ATP-1 + Redis · checks price history · sets outcomeConfirmed=1',
      'Updates congressional_performance + insider_accuracy in ATP-2 MP_ARCHIVE',
      'DuckDB on block storage for analytical queries (never permanent storage)',
      'outcomeDetection.js NEW: nightly 2am ET · runs after sectorMappingPipeline',
      'Without this: L6 has no labelled training data and cannot improve',
      'Gate: REFACTOR-EDGAR + PIPELINE-RELOAD both confirmed healthy before building this',
    ],
    prereqs: ['REFACTOR-EDGAR complete', 'PIPELINE-RELOAD acceptance criteria pass'],
    doD: [
      'outcomeDetection.js deployed and running nightly',
      'ATP-2 congressional_performance has > 100 confirmed outcomes',
      'L6 can read from ATP-2 without errors',
    ],
    smokeChecks: ['test -f backend/jobs/outcomeDetection.js'],
    files: ['backend/jobs/outcomeDetection.js'],
    vendorDeps: [],
  },

  {
    id: 'oci-5g-institutional',
    sessionNumber: 'OCI-5G',
    name: 'OCI-5G: Institutional 13F Holdings — Tier 1 Highest Conviction',
    status: 'planned',
    phase: 'Phase 4 — Pipeline Data Layer',
    items: [
      'SEC EDGAR 13F filings · funds > $100M AUM · free .gov source · quarterly',
      'RESEQUENCED: after OCI-5C (government money flow) — institutional is Tier 1 signal weight',
      'Tables: institutional_holdings · fund_performance → Oracle SQL ATP-1 MP_REFERENCE',
      'Signals: INSTITUTIONAL_ACCUMULATION · SMART_MONEY_EXIT · TIER1_FUND_NEW_POSITION',
      'Berkshire / Renaissance / Citadel new buy = D3 Tier 1 immediate alert',
      'Multiple funds same ticker = strongest signal in entire stack',
    ],
    prereqs: ['OCI-5C complete (gov money flow foundation)', 'PIPELINE-RELOAD acceptance criteria pass'],
    doD: [
      'institutional_holdings: > 10,000 rows',
      'INSTITUTIONAL_ACCUMULATION signal in signalWriter VALID_TYPES',
      'Screener can filter by institutional activity',
    ],
    smokeChecks: ['test -f backend/jobs/institutionalPipeline.js'],
    files: ['backend/jobs/institutionalPipeline.js'],
    vendorDeps: [{ name: 'SEC EDGAR 13F', plan: 'Free .gov', status: 'data.sec.gov/submissions' }],
  },

  {
    id: 'oci-5h-screener-aggregation',
    sessionNumber: 'OCI-5H',
    name: 'OCI-5H: Screener Aggregation Layer — Nightly Pre-Computation',
    status: 'planned',
    phase: 'Phase 4 — Pipeline Data Layer',
    items: [
      'Nightly pre-computation joining all OCI-5x signals into screener_metrics table',
      'OCI-6 screener UI reads ONLY from screener_metrics — never raw pipeline collections',
      'Three decisions required before this session (cannot retrofit):',
      '  (1) screener_metrics schema: flexible document design',
      '  (2) Signal tier field: set in every pipeline from OCI-5B first write ✅ (done)',
      '  (3) Member behaviour aggregation pipeline: design before OCI-6',
      'Government confluence score computed here: 5 sources → single score per ticker/sector',
    ],
    prereqs: ['OCI-5C through OCI-5G all complete', 'screener_metrics schema decision made'],
    doD: [
      'screener_metrics: populated nightly for all tracked tickers',
      'government_confluence_score present per ticker where data exists',
      'OCI-6 screener query time < 200ms using screener_metrics',
    ],
    smokeChecks: ['test -f backend/jobs/screenerAggregationPipeline.js'],
    files: ['backend/jobs/screenerAggregationPipeline.js'],
    vendorDeps: [],
  },

  {
    id: 'signal-collection-rearchitect',
    sessionNumber: 'SIGNAL-COLLECTION-REARCHITECT',
    name: 'Signal Collection Rearchitecture — Quality Gates + Session Aggregation + Outcome Tracking',
    status: 'planned',
    phase: 'Phase 5 — Screener Completion',
    items: [
      '── WHY BEFORE OCI-6 ────────────────────────────────────────────',
      'OCI-6 screener reads signal data. If signal schema is wrong, OCI-6 is wrong.',
      'Phase 2 signal collection = every interaction logged equally — no quality filter.',
      'D3 cannot distinguish a 2-second chart view from a 45-minute drawing session.',
      'Signal quality gate missing = noise floods ATP-2 training corpus.',
      '── DECISION 1: ARCHITECTURE OPEN QUESTION (ARCH.md) ───────────',
      'signalWriter.js uses ATP MongoDB URI (d3_signals SODA). Should Tier 5 move to Oracle SQL?',
      'Recommendation: STAY in MongoDB for Tier 5 crowd signals.',
      '  Rationale: Tier 5 is high-volume lowest-conviction. Does not JOIN to congressional data.',
      '  Oracle SQL ATP-1 is for pipeline data that JOINs. Keep tiers architecturally separate.',
      '── DECISION 2: QUALITY GATE AT COLLECTION TIME ─────────────────',
      'Minimum engagement thresholds before a signal fires:',
      '  chart_view: minimum 15 seconds (suppress bounce events)',
      '  drawing: must be saved — not just started (suppress accidental draws)',
      '  indicator_toggle: stay active >30 seconds before logging',
      '  template_load · alert_price_set · fib_levels: log immediately (explicit intent)',
      '── DECISION 3: SESSION-LEVEL AGGREGATION ──────────────────────',
      'Write ONE composite session_signal per chart session instead of 20 individual events:',
      '  session_duration_seconds · tickers_viewed[] · indicators_used[] · drawings_placed',
      '  alerts_set · scan_source · max_dwell_ticker (most time on one ticker)',
      '  conviction_score: (drawings × 3 + alerts × 5 + dwell/60) / session_duration',
      '── DECISION 4: OUTCOME TRACKING FOR TIER 5 ────────────────────',
      'When a WatchlistLog ticker subsequently moves ≥15%:',
      '  mark WatchlistLog entry as outcomeConfirmed=true (same pattern as pipeline signals)',
      '  Add as second pass in outcomeDetection.js — not a separate pipeline',
      '── FOUR-PRODUCT VALUE ──────────────────────────────────────────',
      'Product 1: member gets better AI analysis (fed by cleaner signals)',
      'Product 2 (D3): higher conviction Tier 5 context — noise filtered before L2',
      'Product 3 (Inner Circle): aggregate member conviction readable from session_signal',
      'Product 4 (Commercial): signal quality improved internally, schema unchanged externally',
    ],
    prereqs: [
      'REFACTOR-1 complete ✅',
      'NAV-RESTRUCTURE complete ✅',
      'ARCH.md open question read: signalWriter MongoDB vs Oracle SQL decision',
    ],
    doD: [
      'Engagement thresholds enforced in ChartInteractionLog (15s view, saved drawing)',
      'session_signal composite record writing after each chart session',
      'WatchlistLog outcome tracking added to outcomeDetection.js',
      'Architecture decision documented in ARCH.md',
      'signalWriter VALID_TYPES includes session_signal',
    ],
    smokeChecks: [
      'grep -q "session_signal" backend/services/signalWriter.js',
      'grep -q "minDwellSeconds" src/components/PriceChart.jsx',
      'grep -q "WatchlistLog.*outcomeConfirmed" backend/jobs/outcomeDetection.js',
    ],
    files: [
      'backend/services/signalWriter.js',
      'src/components/PriceChart.jsx',
      'backend/jobs/outcomeDetection.js',
      'backend/routes/chart-signals.js',
    ],
    vendorDeps: [],
  },

  {
    id: 'extended-hours-scanner',
    sessionNumber: 'EXTENDED-HOURS-SCANNER',
    name: 'Extended Hours Scanner — Pre-Market + Post-Market Gap Scanner on Screener',
    status: 'planned',
    phase: 'Phase 5 — Screener Completion',
    items: [
      '── WHY BOTH WINDOWS MATTER EQUALLY ────────────────────────────',
      'Pre-market (4am-9:30am ET): gap formation, institutional positioning before open.',
      'Post-market (4pm-8pm ET): earnings reactions, FDA decisions, M&A announcements.',
      'Post-market moves ARE the next day\'s pre-market gap. They are the same data window.',
      'The highest-volatility events (earnings beats/misses, FDA approvals) happen post-market.',
      'A member who does not check post-market misses the setup before the next open.',
      '── TWO TABS ON SCREENER ────────────────────────────────────────',
      '"Pre-Market" tab (available 4am-9:30am ET):',
      '  Top 20 tickers by gap % from previous close',
      '  Columns: ticker · signal · gap % · pre-market volume vs 90-day avg · catalyst type',
      '  Filter: gap ≥1% AND pre-market volume ≥2x 90-day average (suppress noise)',
      '"Post-Market" tab (available 4pm-8pm ET):',
      '  Top 20 tickers with largest after-hours price moves',
      '  Columns: ticker · signal · AH move % · AH volume · catalyst type · next-day implication',
      '  Filter: move ≥0.5% AND AH volume ≥1.5x average AH volume',
      '  Catalyst types: earnings beat/miss · FDA decision · M&A · analyst action · no catalyst',
      '── SHARED FEATURES ─────────────────────────────────────────────',
      'Both tabs: show last run timestamp when outside active window (data stays visible)',
      'Both tabs: alert-setting from each result row (sets price alert for next session open)',
      'Both tabs: zero new vendor cost — stooq intraday + edgar_events already in pipeline',
      'Replaces: Benzinga Pro ($40+/mo) for both windows',
      '── D3 VALUE ────────────────────────────────────────────────────',
      'Pre-market gap + congressional buy = Scenario B momentum entry signal',
      'Post-market earnings gap + insider cluster buy = Scenario E (PEAD) setup',
      'Post-market gap + no catalyst = Scenario A pump detection input',
      'AH Institutional Absorption (large AH volume, no price move) = next-day gap predictor',
      '  This same signal is in D3c After-Hours Intelligence Module — we surface it to members here',
      'Member views post-market move + sets next-day alert = highest conviction demand signal',
      '── FOUR-PRODUCT VALUE ──────────────────────────────────────────',
      'Product 1: replaces Benzinga Pro for both extended hours windows ($40+/mo saved)',
      'Product 2 (D3): D3c AH Intelligence Module reads same data — member display is natural byproduct',
      'Product 3 (Inner Circle): post-market catalysts surface in next-day rotation brief',
      'Product 4 (Commercial): extended hours signals = highest-urgency items in signal feed',
    ],
    prereqs: [
      'STOOQ-PIPELINE complete (intraday + after-hours data available in OCI NoSQL)',
      'REFACTOR-EDGAR complete (edgar_events populated with catalyst types)',
      'OCI-6 deployed (adding tabs to existing screener)',
    ],
    doD: [
      'Pre-market tab: top 20 movers 4am-9:30am ET with gap %, volume, catalyst',
      'Post-market tab: top 20 AH movers 4pm-8pm ET with move %, AH volume, catalyst',
      'Both tabs show last run timestamp outside active window',
      'Alert-setting from both result rows works (sets price alert)',
      'Volume filters active for both windows',
      'Signal badge from existing algorithm on each result row',
    ],
    smokeChecks: [
      'grep -q "preMarket\\|postMarket" src/pages/ScreenerPage.jsx',
      'grep -q "extendedHours\\|afterHours" backend/routes/screener.js',
    ],
    files: ['src/pages/ScreenerPage.jsx', 'backend/routes/screener.js'],
    vendorDeps: [
      { name: 'stooq intraday', plan: 'OCI NoSQL — free', status: 'Requires STOOQ-PIPELINE complete' },
    ],
  },

  {
    id: 'short-interest-display',
    sessionNumber: 'SHORT-INTEREST-DISPLAY',
    name: 'Short Interest Display — Short Float + DTC on Screener + Stock Page',
    status: 'planned',
    phase: 'Phase 5 — Screener Completion',
    items: [
      'Screener: "Short Float %" optional column (sortable, filterable)',
      'Screener: "High Short Interest" pre-built scan (short float > 15%)',
      'Stock page: short interest section — float %, DTC, trend (rising/falling)',
      'Legal label required: "Short interest data — informational only. Not a buy/sell signal."',
      'Data: Quiver Quantitative $10/mo (same subscription needed for D3 Scenario F)',
      'Subscribing here means data accumulates months before Scenario F needs it.',
      'Replaces Unusual Whales for short interest display ($48/mo saved).',
      '── D3 VALUE ────────────────────────────────────────────────────',
      'Short float + Scenario G (insider cluster) = Scenario F Mode 1 entry signal',
      'Members viewing high-short-float + setting alerts = squeeze watch crowd signal',
      'Short interest trend feeds D3 Scenario F classifier input at Phase 10',
      '── FOUR-PRODUCT VALUE ──────────────────────────────────────────',
      'Product 1: replaces Unusual Whales for short interest ($48/mo saved)',
      'Product 2 (D3): Scenario F data starts accumulating months early',
      'Product 3 (Inner Circle): short squeeze candidates in sector context',
      'Product 4 (Commercial): short interest in signal feed = squeeze alert context',
    ],
    prereqs: [
      'Quiver Quantitative $10/mo subscribed · QUIVER_API_KEY in vault on all 3 VMs',
      'OCI-6 screener UI deployed (column picker exists)',
      'REFACTOR-EDGAR complete (stock page section layout ready)',
    ],
    doD: [
      'Short float % in screener column picker',
      '"High Short Interest" pre-built scan working',
      'Stock page: short float %, DTC, trend direction',
      'Legal label on every display',
      'Quiver API key in vault',
    ],
    smokeChecks: [
      'grep -q "shortFloat" src/pages/ScreenerPage.jsx',
      'grep -q "QUIVER_API_KEY" backend/.env.example',
    ],
    files: [
      'src/pages/ScreenerPage.jsx',
      'src/pages/StockPage.jsx',
      'backend/routes/screener.js',
      'backend/routes/stocks-seo.js',
    ],
    vendorDeps: [
      { name: 'Quiver Quantitative', plan: '$10/mo', endpoint: '/short-interest/{ticker}', status: 'Subscribe Phase 5 — accumulate for Scenario F' },
    ],
  },

  {
    id: 'refactor-1-fragmentation',
    sessionNumber: 'REFACTOR-1',
    name: 'REFACTOR-1: File Fragmentation Gate (BEFORE OCI-6)',
    status: 'planned',
    phase: 'Phase 5 — Screener Completion',
    items: [
      'Audit all files > 300 lines — PriceChart.jsx was 2498 lines before S28 (completed)',
      'Establish file size limits + feature isolation standards for remaining large files',
      'Zero new features — all existing smoke tests must pass after',
      'Gate: must complete before OCI-6 screener UI session opens',
      'Known large files to audit: StockPage.jsx · screener components · server.js',
    ],
    prereqs: ['All OCI-5x pipeline sessions confirmed healthy'],
    doD: [
      'No file > 300 lines without documented justification',
      'Smoke: 418+ checks all passing',
      'CI=true npm run build clean',
    ],
    smokeChecks: ['grep -rn "." src/components --include="*.jsx" | wc -l'],
    files: [],
    vendorDeps: [],
  },

  {
    id: 'oci-7b-eval',
    sessionNumber: 'OCI-7B-EVAL',
    name: 'OCI-7B-EVAL: gpt-oss-120b Evaluation Before Capital Role',
    status: 'planned',
    phase: 'Phase 8 — D3 Foundation',
    items: [
      'gpt-oss-120b (L4) CONFIRMED on OCI GenAI (SKU B112004/B112005) — evaluate before capital role',
      'L4 role: risk assessment + position sizing context between L3 reasoning and L5 capital gate',
      'A hallucination or overconfidence at L4 = L5 faces plausible-looking wrong input',
      '6 test categories: factual accuracy · hallucination rate · conservative bias · consistency · instruction following · latency',
      'Pass threshold: see OCI_7B_EVAL.md (Project Knowledge)',
      'Fallback if fails: L3 at both L3+L4 · or Meta Llama 3.3 70B · or L5 absorbs L4 role',
    ],
    prereqs: ['OCI-7 complete (WAF + monitoring)', 'D3-PRIVATE-REPO created'],
    doD: [
      'eval_results.json committed to D3 private repo',
      'Pass/fail decision documented in OCI_7B_EVAL.md',
      'If pass: L4 role assigned in D3 engine config',
      'If fail: fallback option chosen and documented',
    ],
    smokeChecks: [],
    files: [],
    vendorDeps: [{ name: 'OCI GenAI gpt-oss-120b', plan: 'SKU B112004/B112005', status: 'CONFIRMED on contract 1224179463.a1' }],
  },

  {
    id: 'executive-ptr-restore',
    sessionNumber: 'EXECUTIVE-PTR-RESTORE',
    name: 'EXECUTIVE-PTR-RESTORE: STOCK Act PTR Pipeline (LAST pipeline session)',
    status: 'planned',
    phase: 'Phase 4 — Pipeline Data Layer',
    items: [
      'INTENTIONALLY LAST pipeline session — after screener site complete + legal review done',
      'LEGAL GATE: confirm 5 U.S.C. app § 105(c) does NOT apply to PTRs (only 278e annual disclosure)',
      'Legal question: Quiver Quantitative operates commercial STOCK Act PTR product — confirm precedent applies',
      'Source: oge.gov PTR system — confirm URL ≠ extapps2.oge.gov (that was 278e — permanently archived)',
      'Scope: executivePtrPipeline.js (NEW — NEVER restore archived executivePipeline.js)',
      'Table: executive_trades Oracle SQL MP_REFERENCE',
      'D3 gate: securityType=stock + accessTier=EXCHANGE only · OGE 278e NEVER to D3',
      'Signal value: tripartisan amplifier — House + Senate + Executive on same ticker = HIGHEST CONVICTION',
      '⚠️ OGE Form 278e (extapps2.oge.gov) is PERMANENTLY archived — do not confuse with PTR',
    ],
    prereqs: [
      'Screener site fully complete (OCI-6 live)',
      'Legal review confirmed in writing: PTR commercial use permitted',
      'All other pipeline sessions confirmed healthy',
    ],
    doD: [
      'Legal review documented in LEGAL_REVIEW.md',
      'executivePtrPipeline.js deployed — never executivePipeline.js',
      'executive_trades: > 1,000 rows',
      'OGE 278e gate in code: hard block on annual 278e data reaching signalWriter',
    ],
    smokeChecks: [
      'test -f backend/jobs/executivePtrPipeline.js',
      '! test -f backend/jobs/executivePipeline.js',
      'grep -q "278e" backend/jobs/executivePtrPipeline.js',
    ],
    files: ['backend/jobs/executivePtrPipeline.js'],
    vendorDeps: [{ name: 'oge.gov PTR system', plan: 'Free .gov — STOCK Act PTRs only', status: 'legal review required first' }],
  },

  {
    id: 'd3-commercial',
    sessionNumber: 'D3-COMMERCIAL',
    name: 'D3-COMMERCIAL: Commercial Signal Product — Broker Adapter · Phase D+',
    status: 'planned',
    phase: 'Phase 12 — Products 3 and 4',
    items: [
      'Product 4 in four-product hierarchy — last product built',
      'Members connect their own broker — D3 provides signals · we never touch their capital',
      'Broker adapter pattern (R33): brokerAdapter.placeOrder() — same interface, their broker',
      'Signal feed only: truncated · no positions/P&L · no L6 weight values · no reasoning text',
      'Legal review required before building: commercial signal product has regulatory implications',
      'Revenue model: subscription tier above Inner Circle',
      'Gate: D3 proven 12+ months profitable + Inner Circle portal live + legal review complete',
    ],
    prereqs: ['D3-INNER-CIRCLE live 12+ months', 'D3 proven profitable', 'Legal review complete'],
    doD: ['Commercial tier members can connect broker', 'Signal feed delivering without revealing D3 positions'],
    smokeChecks: [],
    files: [],
    vendorDeps: [],
  },

  {
    id: 'oci-6-screener',
    sessionNumber: 'OCI-6',
    name: 'OCI-6: Screener Data Architecture + Redis Cache',
    status: 'planned',
    phase: 'Phase 5 — Screener Completion',
    items: [
      'Rewrite screener.js: MongoDB + ATP fundamentals + EDGAR insider + congressional JOIN',
      'Add filters: P/E · revenue growth · insider buying · congressional activity · catalysts',
      'Replace NodeCache → Redis (already on VM-1)',
      'Pre-computed screener results in Redis (5min TTL market hours)',
    ],
    prereqs: ['OCI-5F complete ✅', 'Redis on VM-1 ✅'],
    doD: [
      'Screener queries use ATP + EDGAR + congressional data',
      'NodeCache removed · Redis cache active',
      'CI=true npm run build clean',
    ],
    smokeChecks: [
      'grep -v "NodeCache" backend/routes/screener.js',
      'grep -q "redis" backend/routes/screener.js',
    ],
    files: ['backend/routes/screener.js', 'backend/services/redisClient.js'],
    vendorDeps: [],
  },

  {
    id: 'oci-7-monitoring',
    sessionNumber: 'OCI-7',
    name: 'OCI-7: WAF + Monitoring + Alarms + Vulnerability Scanning',
    status: 'planned',
    phase: 'Phase 8 — D3 Foundation',
    items: [
      'OCI WAF: protect api.marketpulze.com',
      'OCI Monitoring + Alarms: CPU, memory, Redis, ATP',
      'Add IBKR whitelist IP 129.80.17.209',
      'Restrict mpbastion CIDR from 0.0.0.0/0 to team IPs',
      'OCI-7B: evaluate Grok 4 (L1 upgrade) · Gemini 2.5 Pro (L2) · Cohere Rerank 4 Pro (L2.5)',
    ],
    prereqs: ['OCI-6 complete ✅'],
    doD: ['WAF active', 'Alarms configured', 'IBKR whitelist added', 'Bastion restricted'],
    smokeChecks: [],
    files: ['.claude/project-knowledge/OCI_CONFIG.md'],
    vendorDeps: [],
  },

  {
    id: 'oci-8-vm2',
    sessionNumber: 'OCI-8',
    name: 'OCI-8: VM-2 D3 Stack — IB Gateway + D3 Engine + mp-vault-d3',
    status: 'planned',
    phase: 'Phase 8 — D3 Foundation',
    items: [
      'CRITICAL R26: VM-2 MUST be x86_64 — never ARM',
      'Shape: VM.Standard.E5.Flex (x86_64) · 2 OCPU · 16GB · ~$30-50/mo',
      'IB Gateway + D3 engine skeleton + Redis subscriber',
      'mp-vault-d3: IBKR credentials ONLY · VM-2 instance principal only',
    ],
    prereqs: ['OCI-7 complete ✅', 'IBKR whitelist confirmed ✅'],
    doD: ['VM-2 x86_64 running', 'IB Gateway installed', 'D3 reads Redis', 'IBKR creds in vault'],
    smokeChecks: [],
    files: ['.claude/project-knowledge/OCI_CONFIG.md'],
    vendorDeps: [
      { name: 'IBKR', plan: 'Live account', status: 'Whitelist 129.80.17.209 at OCI-7' },
    ],
  },

  {
    id: 'oci-9-shadow',
    sessionNumber: 'OCI-9',
    name: 'OCI-9: D3 Engine Shadow Mode — All Layers Logging, No Execution',
    status: 'planned',
    phase: 'Phase 9 — D3 Shadow',
    items: [
      'All 7 D3 layers active but EXECUTE=false',
      'Every signal logged: what D3 would trade, why, confidence',
      'L1-L5 consensus tracking + performance baseline',
    ],
    prereqs: ['OCI-8 complete ✅'],
    doD: ['All layers logging', 'No trades executed', 'Signal log flowing to ATP'],
    smokeChecks: [],
    files: [],
    vendorDeps: [],
  },

  {
    id: 'oci-10-shadow-run',
    sessionNumber: 'OCI-10',
    name: 'OCI-10: D3a — 30-Day Shadow Mode Run',
    status: 'planned',
    phase: 'Phase 9 — D3 Shadow',
    items: [
      '30 consecutive trading days shadow run',
      'Daily review: accuracy, layer agreement, false positives',
      'Weight adjustments — owner approval required',
    ],
    prereqs: ['OCI-9 complete ✅'],
    doD: ['30 days complete', 'Accuracy report', 'Owner approves D3b gate'],
    smokeChecks: [],
    files: [],
    vendorDeps: [],
  },

  {
    id: 'oci-dr',
    sessionNumber: 'OCI-DR',
    name: 'OCI-DR: Backup Automation + Frankfurt DR (Before D3c Live Capital)',
    status: 'planned',
    phase: 'Phase 11 — D3 Capital Deploy',
    items: [
      'Object Storage: Redis + ATP + MongoDB backups automated',
      'Frankfurt region: VM images cross-region · ATP data guard',
      'Cloudflare DNS failover automation · DR runbook tested',
      'MUST complete before D3c live capital trading',
    ],
    prereqs: ['OCI-10 complete ✅'],
    doD: ['Backups automated', 'Frankfurt DR tested', 'RTO < 1hr validated'],
    smokeChecks: [],
    files: [],
    vendorDeps: [],
  },

  {
    id: 'oci-11-railway',
    sessionNumber: 'OCI-11',
    name: 'OCI-11: Railway Cancelled — mp-vm-test activation',
    status: 'planned',
    phase: 'Phase 3 — OCI Infrastructure',
    items: [
      'Railway cancelled May 4 2026 by owner ✅',
      'mp-vm-test: activate when needed for staging layer',
    ],
    prereqs: ['OCI-DR complete ✅'],
    doD: ['DR plan updated', 'mp-vm-test activated if needed'],
    smokeChecks: [],
    files: [],
    vendorDeps: [],
  },

  {
    id: 'oci-12-perf',
    sessionNumber: 'OCI-12',
    name: 'OCI-12: Performance Baseline + Optimisation',
    status: 'planned',
    phase: 'Phase 11 — D3 Capital Deploy',
    items: [
      'Lighthouse + Core Web Vitals baseline',
      'API P95 response time baseline',
      'Redis cache hit rate analysis · ATP slow query review',
    ],
    prereqs: ['OCI-11 complete ✅'],
    doD: ['Baseline documented', 'P95 API < 200ms', 'Cache hit rate > 80%'],
    smokeChecks: [],
    files: [],
    vendorDeps: [],
  },

  // ─── PHASE B FEATURES (after OCI-6) ───────────────────────────────────────


  // ─── PAGE EVALUATION + ENHANCEMENT + FRAGMENTATION ──────────────────────
  // Purpose: evaluate all initially developed pages to competitor level
  // BEFORE screener becomes primary product with government signal data.
  // Sequence: PIPELINE-RELOAD confirmed → evaluate each page → enhance → REFACTOR-1 → OCI-5C+
  // Rule: evaluation must answer "would a Finviz/Unusual Whales/TradingView subscriber switch?"

  {
    id: 'nav-restructure',
    sessionNumber: 'NAV-RESTRUCTURE',
    name: 'Navigation Restructure — Admin + Roadmap out of User Dropdown',
    status: 'planned',
    phase: 'Phase 5 — Screener Completion',
    items: [
      '── WHY THIS SESSION ─────────────────────────────────────────────',
      'Current: Admin Panel + Roadmap live inside user dropdown (member menu)',
      'Problem 1: Admin Panel in member dropdown = every member sees it (visually)',
      '  Only admins can access it but all members see the menu item — looks unfinished',
      'Problem 2: Roadmap in member dropdown = product owner tool mixed with member features',
      '  Members see Roadmap — not appropriate for a production product',
      'Problem 3: When Admin Panel moves to admin.marketpulze.com (Phase C ADMIN-PORTAL)',
      '  the nav restructure must already be done — cannot do it mid-Phase C',
      '',
      '── NAVIGATION CHANGES ───────────────────────────────────────────',
      'User dropdown (member-facing — ALL members see):',
      '  KEEP: My Profile · Analysis History · Account Settings · Upgrade Plan · Sign Out',
      '  REMOVE: Admin Panel (move to admin-only nav)',
      '  REMOVE: Roadmap (move to owner-only access)',
      '',
      'Admin nav (requireAdmin only — separate top-level route /admin):',
      '  Admin Panel: visible in top nav ONLY for admin users',
      '  OR: admin badge/button in top nav bar replacing dropdown item',
      '',
      'Roadmap access:',
      '  Option A: superAdmin only — /roadmap accessible by SUPER_ADMIN_EMAILS only',
      '  Option B: public but not in any nav — direct URL only (owner uses it, members who find it can read)',
      '  Option C: Keep in nav but only for superAdmin (hidden from all other users)',
      '  DECISION REQUIRED at session open — confirm Option A/B/C with owner',
      '',
      '── UPGRADE PLAN BUTTON ──────────────────────────────────────────',
      'Current: "Free Plan" badge in header + "Upgrade Plan" in dropdown',
      'Review: is this the right placement for the subscription CTA?',
      'TradingView model: persistent "Upgrade" in top nav bar (not dropdown)',
      'Consider: move Upgrade CTA to persistent top nav position for better conversion',
      '',
      '── D3 SIGNAL ────────────────────────────────────────────────────',
      'No D3 signal — navigation change only. Zero feature code.',
      'All smoke tests must pass after — no regression.',
    ],
    prereqs: ['PIPELINE-RELOAD acceptance criteria pass'],
    doD: [
      'Admin Panel not visible in user dropdown for non-admin users',
      'Roadmap: access decision implemented (owner confirms Option A/B/C)',
      'User dropdown clean: My Profile · Analysis History · Account Settings · Upgrade Plan · Sign Out only',
      'Admin users: Admin Panel accessible via separate nav mechanism',
      'Upgrade Plan CTA placement reviewed and confirmed',
      'Smoke: 418+ checks all passing',
      'CI=true npm run build clean',
    ],
    smokeChecks: [
      'grep -q "requireAdmin" src/components/UserDropdown.jsx || grep -q "isSuperAdmin" src/components/UserDropdown.jsx',
      '! grep -q "Admin Panel" src/components/UserDropdown.jsx || grep -q "isAdmin.*Admin Panel" src/components/UserDropdown.jsx',
    ],
    files: [
      'src/components/UserDropdown.jsx',
      'src/components/NavBar.jsx',
    ],
    vendorDeps: [],
    note: 'Phase C ADMIN-PORTAL session (sessions-pipeline.js) handles the full admin.marketpulze.com subdomain with Stripe. This session is the pre-requisite nav cleanup only.',
    designFlag: [
      '⚠️ DESIGN DECISION FOR ADMIN-PORTAL SESSION (not this session):',
      'Admin panel already has two access tiers built: regular admin + Super Admin (SUPER badge).',
      'Current tabs: Users · Requests · Testers · Configuration · Settings',
      'Regular admin scope: member management · invite requests · tester management · rate config',
      'Super Admin scope: everything above PLUS system config · pipeline status · D3 signal monitoring',
      'Question at ADMIN-PORTAL time: does regular admin get a separate login flow / subdomain?',
      'Or does admin.marketpulze.com serve both tiers with role-based tab visibility?',
      'Super Admin trading suite (trading.marketpulze.com) is always separate — never shares with admin portal.',
      'This flag is for awareness only — design at ADMIN-PORTAL session open.',
    ],
  },

  {
    id: 'eval-home',
    sessionNumber: 'EVAL-HOME',
    name: 'Home Page Evaluation + Enhancement — Competitor Level',
    status: 'planned',
    phase: 'Phase 5 — Screener Completion',
    items: [
      'Evaluate vs TradingView (market overview), Unusual Whales (gov signal home), Benzinga (news-first)',
      'Gap: no market overview on home — first impression is weak vs competitors',
      'Gap: government signal thesis not visible — what are senators doing this week? not on home',
      'Gap: no sector rotation summary on home — Unusual Whales has this at $50/mo',
      'Enhancement: Market snapshot strip (SPY/QQQ/VIX/DXY price + direction) — existing cache, zero API cost',
      'Enhancement: "This week in Congress" widget — top 3 conviction trades from congressional_trades',
      'Enhancement: Signal strength banner — if FLIGHT_TO_SAFETY active, show it on home prominently',
      'Enhancement: Member hook — guest sees blurred government signal widget with "Request Invite" CTA',
      'D3: home_government_signal_view · home_market_strip_view → L1 engagement signal',
      'Fragmentation check: review Home page component size — split if > 300 lines',
    ],
    prereqs: ['PIPELINE-RELOAD acceptance criteria pass (congressional_trades > 4000 rows)'],
    doD: [
      'Home page passes: "would an Unusual Whales subscriber see unique value here?"',
      'Market snapshot strip live',
      '"This week in Congress" widget live for members',
      'Guest sees locked government signal widget as hook',
      'CI=true npm run build clean',
    ],
    smokeChecks: [
      'grep -q "home_government_signal_view" src/pages/HomePage.jsx',
      'grep -q "ThisWeekInCongress" src/pages/HomePage.jsx',
    ],
    files: ['src/pages/HomePage.jsx', 'backend/routes/home.js'],
    vendorDeps: [],
  },

  {
    id: 'responsive-layout',
    sessionNumber: 'RESPONSIVE-LAYOUT',
    name: 'Site-wide Responsive Container Fix — 60% Screen Utilization Bug',
    status: 'done',
    phase: 'Phase 5 — Screener Completion',
    date: 'July 2026',
    commit: 'UNCONFIRMED — owner reported successful deploy via screenshot (wider Home page render confirmed working) but the actual terminal output with real commit hash was not pasted for this specific successful run (an earlier run failed with "nothing to commit," traced to stale files in _staging — a fresh full re-run was requested, its result not yet confirmed back). DO NOT assume a commit hash — get git log --oneline -3 before trusting this line.',
    items: [
      '── ROOT CAUSE ──────────────────────────────────────────────────',
      'shared.js container.maxWidth was a fixed 1400px — every page (via App.jsx wrapper) and',
      'Header.jsx froze at 1400px regardless of screen size, wasting ~40%+ of a large/ultra-wide',
      'monitor (owner-reported: only ~60% screen utilization)',
      '── FIX ─────────────────────────────────────────────────────────',
      'shared.js: container.maxWidth → clamp(1400px, 96vw, 2000px) — floor preserves existing',
      'laptop/tablet behavior exactly, scales fluidly above ~1460px viewport, capped at 2000px',
      'so ultra-wide monitors do not stretch hero/text copy edge-to-edge (readability)',
      'shared.js: added FULL_BLEED_PAGES registry (replaces hardcoded page !== "chart" check',
      'in App.jsx) — single source of truth for which pages skip s.container entirely',
      '17 files had their OWN redundant/competing maxWidth removed so they inherit the shared',
      'value instead of double-capping: MarketPage, PricingPage, WaitlistPage, ProfilePage,',
      'RoadmapPage, AdminPage, NewsPage, DashboardPage, ScreenerPage, CalendarPage,',
      'CalculatorPage, TradingSignalsPage, StockPage, HeroSection (the actual Home content —',
      'found via corrected regex survey after first pass incorrectly said "no cap"), Footer.jsx',
      '(had its OWN independent 1200px cap never tied to s.container at all — found via owner',
      'screenshot review, not code search)',
      '── DELIBERATELY NOT CHANGED ────────────────────────────────────',
      'AuthPage/ResetPasswordPage/VerifyEmailPage — narrow centered forms, stretching these to',
      '98vw would be a UX regression not improvement (owner explicitly confirmed this)',
      'ChartPage — already correctly full-bleed via FULL_BLEED_PAGES, no change needed',
      'SharePage — renders inside s.container but has no competing maxWidth of its own; only',
      'child ShareCard has a fixed 420px width (deliberate, same reasoning as narrow forms)',
      'Several small nested-element maxWidths left alone (table-cell truncation, text label',
      'wrap widths) — confirmed via context check to not be page containers',
    ],
    prereqs: [],
    doD: [
      'Owner screenshot confirms Home page content fills width correctly at large viewport ✅',
      'Header/content/footer widths visually consistent ✅',
      'Smoke 516/516 confirmed clean before commit (per script run) ✅',
      'Actual commit hash confirmed — NOT YET DONE, see commit field above',
    ],
    smokeChecks: [],
    files: [
      'src/styles/shared.js', 'src/App.jsx', 'src/components/Header.jsx', 'src/components/Footer.jsx',
      'src/components/HeroSection.jsx', 'src/pages/MarketPage.jsx', 'src/pages/PricingPage.jsx',
      'src/pages/WaitlistPage.jsx', 'src/pages/ProfilePage.jsx', 'src/pages/RoadmapPage.jsx',
      'src/pages/AdminPage.jsx', 'src/pages/NewsPage.jsx', 'src/pages/DashboardPage.jsx',
      'src/pages/ScreenerPage.jsx', 'src/pages/CalendarPage.jsx', 'src/pages/CalculatorPage.jsx',
      'src/pages/TradingSignalsPage.jsx', 'src/pages/StockPage.jsx',
    ],
    vendorDeps: [],
  },

  {
    id: 'ux-layout-polish',
    sessionNumber: 'UX-LAYOUT-POLISH',
    name: 'UX Realignment — Look & Feel Pass Following Responsive Container Fix',
    status: 'planned',
    phase: 'Phase 5 — Screener Completion',
    items: [
      '── WHY THIS SESSION EXISTS ─────────────────────────────────────',
      'RESPONSIVE-LAYOUT fixed the container-width bug (site was using ~60% of large-monitor',
      'screen width) but was deliberately scoped to ONLY the width fix — not a design pass.',
      'Owner reviewed the deployed result at full width and flagged follow-up UX work needed',
      'across pages — this session captures that, scoped separately, not bundled reactively.',
      '── SPECIFIC FINDINGS FROM OWNER SCREENSHOT REVIEW (Home page, post-fix) ────',
      '"How it Works" 4-card row: cards are fixed-size, so the wider container just adds large',
      'gaps between them rather than the cards growing — reads as unfinished, not spacious',
      '"Everything you need" 6-card feature grid: same fixed-size-card-in-wider-box problem',
      'Stats row (500+ / 50+ / 100% / 15-min): reads sparse at the new width — floating numbers',
      'with large gaps; consider a bordered/card treatment or larger type instead of just more gap',
      'Top Gainers/Losers/Most Active 3-column table block: this one DID scale well — more',
      'breathing room reads as intentional, not broken — use as the reference for "good" here',
      '── SCOPE (confirm with owner before starting — this is a placeholder, not a spec) ──',
      'Review EVERY page (not just Home) at the new container width for the same fixed-size-',
      'element-in-wider-box problem — Dashboard, Market, Screener, Calendar, Calculator,',
      'Pricing, Profile, Admin, News, StockPage, TradingSignals all changed width this session',
      'and none have been individually design-reviewed at their new width yet',
      'Likely fix pattern: replace fixed-size grids with auto-fit/minmax responsive grids (already',
      'used correctly in the PricingPage/WaitlistPage plan-card grids — that pattern works, extend it)',
      'Owner also mentioned wanting to consider "additional features" and broader look-and-feel',
      'changes in the same pass — needs a scoping conversation before code starts, per CS1',
      '(Challenge Before Build) — this is deliberately left open/broad in this entry, not narrowed',
      'to a fixed feature list, since the owner has not yet specified which features',
    ],
    prereqs: ['RESPONSIVE-LAYOUT commit hash confirmed (see that entry — currently unconfirmed)'],
    doD: [
      'Not yet defined — first step of this session is a scoping conversation with the owner,',
      'not code, per CS1. This entry exists so the request is not lost between chats/sessions,',
      'not as a committed spec.',
    ],
    smokeChecks: [],
    files: [],
    vendorDeps: [],
  },

  {
    id: 'eval-dashboard',
    sessionNumber: 'EVAL-DASHBOARD',
    name: 'Dashboard Page Evaluation + Enhancement — Competitor Level',
    status: 'planned',
    phase: 'Phase 5 — Screener Completion',
    items: [
      'Evaluate vs TradingView (watchlist + alerts), Finviz (portfolio tracking)',
      'Gap: no signal summary for watchlist tickers — member sees prices but not which have active gov signals',
      'Gap: no conviction score per watchlist ticker — Unusual Whales has congressional flag per ticker',
      'Gap: no alert history view — member set alerts in S27K but no history panel',
      'Enhancement: Watchlist signal overlay — for each watched ticker show: gov signal present (Y/N), insider cluster (Y/N), conviction score',
      'Enhancement: Alert history panel — when price hit, when signal fired, outcome if known',
      'Enhancement: Portfolio P&L if tickers tracked with entry price (Phase B simple version)',
      'D3: watchlist_signal_check · alert_history_view → L2 conviction_speed, portfolio_intent',
      'Fragmentation check: review Dashboard component size — split if > 300 lines',
    ],
    prereqs: ['PIPELINE-RELOAD acceptance criteria pass', 'EVAL-HOME complete'],
    doD: [
      'Dashboard passes: "does a TradingView watchlist user get more value here?"',
      'Signal overlay on watchlist live',
      'Alert history panel live',
      'CI=true npm run build clean',
    ],
    smokeChecks: [
      'grep -q "watchlist_signal_check" src/pages/DashboardPage.jsx',
    ],
    files: ['src/pages/DashboardPage.jsx', 'backend/routes/dashboard.js'],
    vendorDeps: [],
  },

  {
    id: 'screener-nl-translate',
    sessionNumber: 'SCREENER-NL-TRANSLATE',
    name: 'Natural Language Screener Search — Translation Logic',
    status: 'planned',
    phase: 'Phase 5 — Screener Completion',
    items: [
      'SHELL ALREADY BUILT (SCREENER-FRAG follow-up, see HEAD.md): NLSearchBar.jsx (full-width',
      '  search box, honest "coming soon" messaging), POST /api/screener/nl-search (captures',
      '  query as nl_search_query signal, no translation yet), PROVEN_SCREENS constant (10',
      '  documented methodologies — Magic Formula, Piotroski F-Score, CANSLIM, Minervini Trend',
      '  Template, Golden Cross, Volume Momentum, Quality at a Discount, Dividend Growth, Tiny',
      '  Titans, Relative Strength Leaders — each marked partial/pending against real data gaps),',
      '  ProvenScreensDropdown.jsx (shows exact missing criteria per methodology, never silently',
      '  no-ops). Adding this entry specifically because the owner flagged that previously',
      '  discussed features have been silently lost despite being "added" to session files —',
      '  this is the fix: a real tracked entry, not just a conversation.',
      'REMAINING — the actual translation logic, a real architecture decision not yet made:',
      '  structured-intent extraction (timeframe, exclusions, sort direction mapped onto',
      '  existing registry fields — cheaper, safer, buildable now) vs. full semantic',
      '  interpretation (flexible but costs an LLM call per search, and cannot honestly answer',
      '  theme-based queries like "AI stocks" without a Theme data source, which is stub today).',
      'REMAINING — Proven Screens: only 2 of 10 (Minervini partial, Volume Momentum partial) have',
      '  ANY live criteria today. The other 8 are 100% pending fundamentals (XBRL-EARNINGS-INTEL)',
      '  or technical history (Stooq) — this session does not change that, only documents it.',
      'D3: nl_search_query -> L2 raw member intent, likely the richest Tier 3 signal in the',
      '  product once translation logic exists to act on it.',
    ],
    prereqs: ['owner decision: structured-intent vs. full semantic interpretation', 'XBRL-EARNINGS-INTEL (for most Proven Screens to go live)', 'Stooq pipeline (for Minervini/Golden Cross/CANSLIM technical criteria)'],
    doD: [
      'Owner has chosen structured-intent vs. full semantic approach, documented here',
      'If structured-intent: timeframe/exclusion/sort extraction working for the confirmed-',
      '  achievable example ("best return last month, excluding OTC")',
      'If semantic: cost-per-query and rate-limiting confirmed acceptable before shipping',
      'Theme data source decision made (needed for genuinely open-ended queries)',
    ],
    smokeChecks: [
      'grep -q "nl_search_query" backend/services/signalWriter.js',
      'test -f src/components/screener/NLSearchBar.jsx',
    ],
    files: ['src/components/screener/NLSearchBar.jsx', 'src/components/screener/ProvenScreensDropdown.jsx', 'src/constants/screenerConfig.js', 'backend/routes/screener.js'],
    vendorDeps: ['Anthropic API (if semantic approach chosen) — reuse existing intelligence/AI Brief integration, confirm with owner before adding a second Claude-calling code path'],
  },

  {
    id: 'eval-screener',
    sessionNumber: 'EVAL-SCREENER',
    name: 'Screener Page Evaluation + Enhancement — Pre-Government-Data Pass',
    status: 'planned', // NOT 'done' — substantial real progress landed via SCREENER-FRAG (see below),
                        // but algorithm-explanation-per-scan and the two UI polish items were not
                        // touched. Left as 'planned' rather than fabricated as 'done' or a new status
                        // value this schema doesn't define (only done/next/planned exist here).
    phase: 'Phase 5 — Screener Completion',
    items: [
      'COMPLETED (SCREENER-FRAG, ran ahead of stated prereqs below — owner-confirmed, House pipeline',
      '  downtime utilization, no live members yet so no conflict): fragmentation (12-file split,',
      '  ScreenerPage.jsx now a shell), field registry + FilterPanel.jsx (Finviz-equivalent tabbed',
      '  manual filter UI — screenshot-matched, ready/stub field distinction), deepened Tier 3 signal',
      '  capture (fieldFilterChanges), live ticker validation (sector_mapping → fuzzy suggestion →',
      '  Yahoo fallback, data_quality_review coverage-gap logging).',
      'NOT done — genuine gaps, not silently claimed: algorithm explanation per scan (scan_explanation_click',
      '  — PIECE 4 was scoped but not built this session), no result count badge on scan buttons, no mini',
      '  sparkline on result rows.',
      'Evaluate vs Finviz Elite ($25/mo), Scanz ($197/mo) — UI and feature gap analysis',
      'NOTE: this is the PRE-government-data evaluation — government signal columns come at OCI-6',
      'D3: scan_explanation_click (not yet built) · filter_saved → L2 strategy_fingerprint',
    ],
    prereqs: ['PIPELINE-RELOAD acceptance criteria pass', 'EVAL-DASHBOARD complete'], // SCREENER-FRAG
      // deliberately ran ahead of both — owner decision, logged here per Gate 4 rather than left as
      // an undocumented sequencing jump. Remaining scope (algorithm explanation, badges, sparklines)
      // still gated behind these prereqs as originally intended.
    doD: [
      'Screener passes: "does a Finviz Elite subscriber get more value here?"',
      'Algorithm explanation tooltips on all scans — REMAINING',
      'Result count badges on scan buttons — REMAINING',
      'Mini sparklines on result rows — REMAINING',
      'Manual filter mode (descriptive + technical filters) — DONE via FilterPanel.jsx (SCREENER-FRAG)',
      'Guest: top 10 + hook visible — unchanged, already present pre-SCREENER-FRAG',
      'No screener component > 300 lines — DONE, 12-file split (SCREENER-FRAG)',
      'CI=true npm run build clean',
    ],
    smokeChecks: [
      // Updated to real file names (SCREENER-FRAG) — the originals referenced a single
      // ScreenerPage.jsx and a component name ("ManualFilterMode") that was never built under
      // that name; FilterPanel.jsx is the actual manual-filter-mode implementation.
      'grep -q "scan_explanation_click" src/pages/ScreenerPage.jsx  // NOT YET TRUE — algorithm explanation remains scoped, not built',
      'test -f src/components/screener/FilterPanel.jsx',
    ],
    files: ['src/pages/ScreenerPage.jsx', 'src/components/screener/'],
    vendorDeps: [],
  },

  {
    id: 'eval-calendar',
    sessionNumber: 'EVAL-CALENDAR',
    name: 'Calendar Page Evaluation + Enhancement — Competitor Level',
    status: 'planned',
    phase: 'Phase 5 — Screener Completion',
    items: [
      'Evaluate vs Benzinga Pro (earnings calendar), Market Chameleon (PDUFA calendar)',
      'Gap: no PDUFA (FDA drug approval) calendar — Market Chameleon charges $50/mo for this',
      'Gap: no earnings signal annotation — which earnings have gov signal alignment?',
      'Gap: no "my watchlist" filter on calendar — member sees all events, not just ones they care about',
      'Gap: no pre-earnings signal summary — "3 insiders bought NVDA in the 30 days before earnings"',
      'Enhancement: PDUFA calendar from FDA.gov (free, no auth) — integrate with OCI-5D or standalone',
      'Enhancement: Watchlist filter toggle — show only events for watched tickers',
      'Enhancement: Earnings signal badge — show gov signal presence on earnings row',
      'Enhancement: Economic calendar signal annotation — if federal_register_orders relates to upcoming event, flag it',
      'D3: calendar_watchlist_filter · earnings_signal_badge_click → L2 event_driven_profile',
      'Fragmentation check: Calendar component size',
    ],
    prereqs: ['PIPELINE-RELOAD acceptance criteria pass', 'EVAL-SCREENER complete'],
    doD: [
      'Calendar passes: "does a Benzinga Pro or Market Chameleon subscriber see unique value?"',
      'PDUFA calendar integrated (FDA.gov)',
      'My Watchlist filter working',
      'Earnings signal badge live',
      'CI=true npm run build clean',
    ],
    smokeChecks: [
      'grep -q "calendar_watchlist_filter" src/pages/CalendarPage.jsx',
      'grep -q "pdufa" backend/routes/calendar.js',
    ],
    files: ['src/pages/CalendarPage.jsx', 'backend/routes/calendar.js'],
    vendorDeps: [
      { name: 'FDA.gov PDUFA calendar', plan: 'Free public domain', status: 'No auth required' },
    ],
  },

  {
    id: 'eval-news',
    sessionNumber: 'EVAL-NEWS',
    name: 'News Page Evaluation + Enhancement — Competitor Level',
    status: 'planned',
    phase: 'Phase 5 — Screener Completion',
    items: [
      'Evaluate vs Benzinga Pro (fast news + AI analysis), Unusual Whales (news with signal tags)',
      'Gap: no signal tag on news items — Unusual Whales tags each article with congressional/insider relevance',
      'Gap: no AI signal detection in news — Benzinga Pro detects M&A/FDA/earnings in headline',
      'Gap: no watchlist filter on news — member sees all news not just their tickers',
      'Gap: no "related government signal" annotation on news — if senator traded ticker in news, show it',
      'Enhancement: Watchlist news filter toggle',
      'Enhancement: AI signal tag per article — uses existing Claude API (L3 signal classification)',
      'Enhancement: Related congressional trade badge — if ticker in news has recent congressional trade, show conviction score',
      'Enhancement: Federal Register link — if news article relates to regulatory order in federal_register_orders, cross-reference',
      'D3: news_signal_tag_click · news_watchlist_filter → L2 information_sensitivity',
      'Fragmentation check: News component size',
    ],
    prereqs: ['PIPELINE-RELOAD acceptance criteria pass', 'EVAL-CALENDAR complete'],
    doD: [
      'News passes: "does a Benzinga Pro subscriber get more signal intelligence here?"',
      'Watchlist filter live',
      'AI signal tag per article live',
      'Congressional trade badge on relevant tickers',
      'CI=true npm run build clean',
    ],
    smokeChecks: [
      'grep -q "news_signal_tag_click" src/pages/NewsPage.jsx',
    ],
    files: ['src/pages/NewsPage.jsx', 'backend/routes/news.js'],
    vendorDeps: [],
  },

  {
    id: 'eval-calculator',
    sessionNumber: 'EVAL-CALCULATOR',
    name: 'Calculator Page Evaluation + Enhancement — Competitor Level',
    status: 'planned',
    phase: 'Phase 5 — Screener Completion',
    items: [
      'Evaluate vs Scanz position sizing, standalone calculators',
      'Gap: no D3 signal integration — calculator should pre-fill from active signals',
      'Gap: no conviction score input — Kelly fraction should factor in signal conviction weight',
      'Gap: no congressional signal context — if ticker has active congressional signal, show conviction at top',
      'Enhancement: Signal pre-fill — if coming from screener result, pre-fill ticker + signal details',
      'Enhancement: Conviction-weighted Kelly — incorporate D3 conviction score into Kelly fraction calculation',
      'Enhancement: Government signal context panel — "This ticker has active senator cluster (2.1x conviction)" shown above calculator',
      'Enhancement: Risk scenario matrix — show outcome distribution for different position sizes',
      'D3: calculator_signal_prefill · conviction_kelly_used → L2 valuation_conviction, risk_profile',
      'Fragmentation check: Calculator component size',
    ],
    prereqs: ['PIPELINE-RELOAD acceptance criteria pass', 'EVAL-NEWS complete'],
    doD: [
      'Calculator passes: "does it add D3 signal intelligence that standalone calculators cannot?"',
      'Signal pre-fill from screener working',
      'Conviction Kelly implemented',
      'Government signal context panel live',
      'CI=true npm run build clean',
    ],
    smokeChecks: [
      'grep -q "conviction_kelly_used" src/pages/CalculatorPage.jsx',
      'grep -q "signal_prefill" src/pages/CalculatorPage.jsx',
    ],
    files: ['src/pages/CalculatorPage.jsx', 'backend/routes/calculator.js'],
    vendorDeps: [],
  },

  {
    id: 'eval-stockpage',
    sessionNumber: 'EVAL-STOCKPAGE',
    name: 'Stock Page Evaluation + Enhancement + Fragmentation',
    status: 'planned',
    phase: 'Phase 5 — Screener Completion',
    items: [
      'Evaluate vs TradingView (full chart), Unusual Whales (congressional tab), Morningstar (fundamentals)',
      'Gap: congressional trades section exists in spec but needs government signal data integration',
      'Gap: insider activity tab needs EDGAR signal display (after REFACTOR-EDGAR)',
      'Gap: government confluence score not visible per ticker — the core moat is invisible',
      'Gap: peer comparison panel (S27N) needs scoping upgrade — competitor chart overlay not just table',
      'Enhancement: Government Confluence Score panel — show 5 sources + combined score per ticker',
      'Enhancement: Congressional activity mini-feed — last 5 trades for this ticker + conviction weight',
      'Enhancement: Insider cluster flag — if edgar_insider has recent cluster, show tier + conviction',
      'Enhancement: S27N peer comparison — upgrade to include chart overlay option (TradingView-level)',
      'Enhancement: Stock page tab shell fragmentation — if StockPage.jsx > 300 lines, split by tab',
      'Fragmentation: StockPage.jsx — likely largest component · split into tab components if not done',
      'D3: government_score_view · congressional_feed_click · insider_cluster_view → L3 signal engagement',
    ],
    prereqs: ['PIPELINE-RELOAD acceptance criteria pass', 'EVAL-CALCULATOR complete'],
    doD: [
      'Stock page passes: "is this the only place showing congressional conviction + insider cluster + gov score in one view?"',
      'Government Confluence Score panel live',
      'Congressional activity mini-feed live',
      'StockPage.jsx: no single file > 300 lines',
      'CI=true npm run build clean',
    ],
    smokeChecks: [
      'grep -q "government_score_view" src/pages/StockPage.jsx',
      'grep -q "CongressionalFeed" src/pages/StockPage.jsx',
    ],
    files: ['src/pages/StockPage.jsx', 'src/components/stock/'],
    vendorDeps: [],
  },

  {
    id: 'eval-chart',
    sessionNumber: 'EVAL-CHART',
    name: 'Chart Page Evaluation + Enhancement + Competitor Comparison Feature',
    status: 'planned',
    phase: 'Phase 5 — Screener Completion',
    items: [
      'Evaluate vs TradingView (full chart · multi-ticker overlay · compare feature)',
      'Current: pure D3.js chart · 16 indicators · 6 chart types · drawing tools · all done ✅',
      'Gap: no competitor/peer chart overlay — TradingView has Compare feature (add symbol)',
      'Gap: no government signal markers on chart — when senators bought/sold should be visible on timeline',
      'Gap: S27-CHART-TAB layout — dedicated tab within stock page not yet built',
      'Enhancement: Peer chart overlay — add 1-2 comparison symbols on same chart (% return normalized)',
      'Enhancement: Congressional trade markers — vertical lines on chart showing senator buy/sell dates',
      'Enhancement: Insider trade markers — S27M scope (vertical lines for Form 4 buys > $50K)',
      'Enhancement: S27-CHART-TAB — activate the tab layout in stock page (mode=tab in MODE_CONFIG)',
      'D3: chart_compare_add · congressional_marker_click · insider_marker_click → L2 strategy_fingerprint',
      'Fragmentation check: PriceChart.jsx was split in S28 — verify all subfiles still < 300 lines',
    ],
    prereqs: ['EVAL-STOCKPAGE complete', 'PIPELINE-RELOAD acceptance criteria pass'],
    doD: [
      'Chart passes: "does the compare feature match TradingView? do government markers add unique value?"',
      'Peer chart overlay (compare feature) live',
      'Congressional trade markers on chart',
      'S27-CHART-TAB layout active',
      'All PriceChart subfiles < 300 lines',
      'CI=true npm run build clean',
    ],
    smokeChecks: [
      'grep -q "chart_compare_add" src/components/PriceChart.jsx',
      'grep -q "CongressionalMarker" src/components/PriceChart.jsx',
      'grep -q "mode=tab" src/components/PriceChart.jsx',
    ],
    files: ['src/components/PriceChart.jsx', 'src/pages/ChartPage.jsx', 'src/pages/StockPage.jsx'],
    vendorDeps: [],
  },

  {
    id: 'eval-market-hub',
    sessionNumber: 'EVAL-MARKET-HUB',
    name: 'Market Hub Build + Evaluation — S27-MARKET-A/B Combined',
    status: 'planned',
    phase: 'Phase 5 — Screener Completion',
    items: [
      'Market tab currently shows placeholder — needs to be built to competitor level',
      'Evaluate vs TradingView (market overview), Unusual Whales (government signal hub)',
      'S27-MARKET-A scope: sector performance table · 12-tab signal table · hover popup',
      'S27-MARKET-B scope: congressional rotation panel · insider transactions · sector heatmap',
      'Government Signal Hub: FLIGHT_TO_SAFETY / SAFETY_REVERSAL / SECTOR_ROTATION active signals display',
      'This is MarketPulze unique moat — real-time government signal confluence — no competitor has it',
      'Conviction-weighted rotation display: which sectors are committee chairs rotating into',
      'Bipartisan signal highlight: Republican + Democrat same direction = highest conviction',
      'Cross-chamber amplifier display: House + Senate same ticker = bicameral confirmation',
      'D3: rotation_signal_view · sector_heatmap_click · congressional_rotation_view → L3 HIGHEST VALUE',
    ],
    prereqs: ['PIPELINE-RELOAD acceptance criteria pass', 'EVAL-CHART complete'],
    doD: [
      'Market Hub passes: "is this the only place showing bipartisan cross-chamber government signal rotation?"',
      'Sector performance live',
      'Congressional rotation panel live (FLIGHT_TO_SAFETY / SAFETY_REVERSAL active if present)',
      'Conviction-weighted sector display live',
      'CI=true npm run build clean',
    ],
    smokeChecks: [
      'grep -q "congressional_rotation_view" src/components/MarketDashboard.jsx',
      'grep -q "RotationSignalPanel" src/components/MarketDashboard.jsx',
    ],
    files: ['src/components/MarketDashboard.jsx', 'backend/routes/market.js'],
    vendorDeps: [],
  },

  {
    id: 'page-fragmentation-audit',
    sessionNumber: 'PAGE-FRAG-AUDIT',
    name: 'Page Fragmentation Audit — All Pages < 300 Lines Before OCI-6',
    status: 'planned',
    phase: 'Phase 5 — Screener Completion',
    items: [
      'Audit all page components and route files for > 300 line violations',
      'Known risks: StockPage.jsx · App.jsx · server.js · MarketDashboard.jsx · ScreenerPage.jsx',
      'PriceChart.jsx was split in S28 — verify all 11 subfiles still < 300 lines',
      'Standard: split by feature/tab/section — never by arbitrary line count',
      'Gate: ALL page files < 300 lines before OCI-6 screener data columns are added',
      'Reason: OCI-6 adds government signal columns to screener — if ScreenerPage.jsx is 800 lines already, adding more breaks Gate 2',
      'All smoke tests must pass after every split — zero functional changes',
    ],
    prereqs: ['EVAL-MARKET-HUB complete'],
    doD: [
      'All src/pages/*.jsx files: < 300 lines',
      'All src/components/*.jsx files: < 300 lines',
      'backend/server.js: < 300 lines (routes in separate files)',
      'Smoke: 418+ checks all passing',
      'CI=true npm run build clean',
    ],
    smokeChecks: [
      'find src/pages -name "*.jsx" | xargs wc -l | awk \'$1 > 300 {print}\' | wc -l | grep -q "^0$"',
    ],
    files: [],
    vendorDeps: [],
  },

  {
    id: 'S27N-peer-compare',
    sessionNumber: 'S27N',
    name: 'Peer Comparison Panel — Sector Stock Comparison + Chart Overlay',
    status: 'planned',
    phase: 'Phase 5 — Screener Completion',
    items: [
      '3-4 sector peers on StockPage: signal, score, P/E, revenue growth, analyst target',
      'All data from FundamentalsCache — zero new API calls',
      'Chart overlay option: compare this ticker vs peers on same chart (% return normalized)',
      'Government signal comparison: does this ticker have stronger gov signal than sector peers?',
      'Conviction differential: show if this ticker outranks peers in congressional conviction',
      'D3: peer_comparison_viewed · peer_chart_overlay_used · peer_signal_compared → L2 sector_preference',
    ],
    prereqs: ['PAGE-FRAG-AUDIT complete', 'EVAL-STOCKPAGE complete'],
    doD: [
      'Peer table renders with signal + score + fundamentals',
      'Chart overlay option works',
      'Government signal comparison panel live',
      'D3 logs fire',
      'CI=true npm run build clean',
    ],
    smokeChecks: [
      'grep -q "peer_comparison_viewed" src/pages/StockPage.jsx',
      'grep -q "PeerChartOverlay" src/components/stock/',
    ],
    files: ['src/pages/StockPage.jsx', 'src/components/stock/'],
    vendorDeps: [],
  },



  {
    id: 'S27L',
    sessionNumber: 'S27L',
    name: 'Earnings Annotations on Chart',
    status: 'planned',
    phase: 'Phase 5 — Screener Completion',
    items: [
      'Earnings date vertical markers + EPS beat/miss dots',
      'Hover tooltip: reported EPS vs estimate, surprise %, revenue',
      'D3: earnings_marker_click → L2',
    ],
    prereqs: ['S27N deployed (id: S27N-peer-compare)', 'REFACTOR-EDGAR complete (edgar_insider data confirmed)'],
    doD: ['Markers visible', 'D3 log fires', 'CI=true npm run build clean'],
    smokeChecks: ['grep -q "earnings_marker_click" src/components/PriceChart.jsx'],
    files: ['src/components/PriceChart.jsx'],
    vendorDeps: [],
  },

  {
    id: 'S27-MARKET-A',
    sessionNumber: 'S27-MARKET-A',
    name: 'Market Intelligence Hub — Phase A',
    status: 'planned',
    phase: 'Phase 5 — Screener Completion',
    items: [
      'Fix Sector Performance spinner (P0)',
      '12-tab signal table + hover popup with D3 chart',
      'D3: market_session_start, signal_table_click, ticker_hover',
    ],
    prereqs: ['S27I deployed'],
    doD: ['12-tab table', 'Hover popup', 'D3 logs', 'CI=true npm run build clean'],
    smokeChecks: ['grep -q "signal_table_click" src/components/MarketDashboard.jsx'],
    files: ['src/components/MarketDashboard.jsx', 'backend/routes/market.js'],
    vendorDeps: [],
  },

  {
    id: 'S27-MARKET-B',
    sessionNumber: 'S27-MARKET-B',
    name: 'Market Intelligence Hub — Phase B',
    status: 'planned',
    phase: 'Phase 5 — Screener Completion',
    items: [
      'Intraday charts · Sector heatmap · Breadth panel',
      'Insider transactions table (from OCI-5 EDGAR data)',
      'Congressional trades table + rotation signal display',
      'D3: sector_click, breadth_view, insider_view, rotation_signal_view',
    ],
    prereqs: ['S27-MARKET-A deployed', 'PIPELINE-RELOAD acceptance criteria pass (congressional_trades > 4000 rows)'],
    doD: ['Intraday charts', 'Sector heatmap', 'Insider table', 'Congressional rotation panel', 'CI=true npm run build clean'],
    smokeChecks: ['grep -q "sector_click" src/components/MarketDashboard.jsx'],
    files: ['src/components/MarketDashboard.jsx', 'backend/routes/market.js'],
    vendorDeps: [],
  },

  {
    id: 'S27-MARKET-C',
    sessionNumber: 'S27-MARKET-C',
    name: 'Market Intelligence Hub — Phase C',
    status: 'planned',
    phase: 'Phase 5 — Screener Completion',
    items: [
      'World geographic SVG heatmap · Pattern recognition',
      'D3 Engine Status Panel (after OCI-9)',
      'D3: world_map_click, pattern_click, d3_status_read',
    ],
    prereqs: ['S27-MARKET-B deployed', 'OCI-9 shadow mode active'],
    doD: ['World map', 'Pattern signals', 'CI=true npm run build clean'],
    smokeChecks: ['grep -q "pattern_click" src/components/MarketDashboard.jsx'],
    files: ['src/components/MarketDashboard.jsx'],
    vendorDeps: [],
  },

  {
    id: 'sec-filings-page',
    sessionNumber: 'SEC-FILINGS-PAGE',
    name: 'SEC Filings \u2014 Standalone Page + StockPage Tab',
    status: 'done', // update commit hash below once apply-session.sh confirms the real push
    phase: 'Phase 5 \u2014 Screener Completion',
    date: 'July 2026',
    commit: 'PENDING \u2014 fill in after apply-session.sh confirms the real push output',
    items: [
      'GET /api/filings/categories \u2014 category grid (9 categories, counts, high-conviction teaser count, safe for guests)',
      'GET /api/filings \u2014 paginated feed, ticker/category search open to guests',
      'POST /api/filings/interaction \u2014 click-level D3 signal (view-level logged automatically on every GET)',
      'Two independent gates: content (tier 1/2/3) + capability (form-type filter, direct form search, significance sort, high-conviction preset) \u2014 member-only, server-enforced not client-trusted',
      'Tier 1 (Inner Circle) ALWAYS locked regardless of auth \u2014 Product 3 does not exist yet, distinct INNER_CIRCLE_UNAVAILABLE lockReason vs SIGN_IN_REQUIRED',
      'StockPage.jsx Filings tab: full guest lock unchanged (Pattern A); standalone /filings page: partial guest gate (Pattern B)',
      'oracleReadPool.js reused, no second pool created (explicit owner architecture direction)',
      'Real isSuperAdmin resolution wired into filings.js \u2014 mirrors admin.js exactly, documented duplication (admin.js does not export it)',
      'D3: reused existing screener_signal type, zero signalWriter.js schema changes \u2014 deliberate, D3 product itself not yet built',
    ],
    prereqs: ['SCREENER-FRAG (oracleReadPool.js) done \u2705', 'tickerValidator.js shared pattern established \u2705'],
    doD: [
      'Both endpoints return real edgar_events data (727,516+ rows)',
      'Tier gating verified: guest sees tier-3 full, tier-2 locked, tier-1 always locked regardless of auth',
      'Capability gating verified: formType/sort/preset params ignored server-side for unauthenticated requests',
      'D3 signals confirmed firing: screener_signal source:filings, action feed_view + click',
      'CI=true npm run build clean',
    ],
    smokeChecks: [
      'test -f backend/routes/filings.js',
      'test -f backend/services/filingsQueryService.js',
      'grep -c "api/filings" backend/server.js',
    ],
    files: [
      'backend/routes/filings.js', 'backend/services/filingsQueryService.js', 'backend/services/oracleReadPool.js',
      'src/pages/FilingsPage.jsx', 'src/hooks/useFilingsData.js', 'src/data/filingFieldRegistry.js', 'src/data/filingFormTypes.js',
      'src/components/filings/CategoryGrid.jsx', 'src/components/filings/FilingFilters.jsx',
      'src/components/filings/FilingRow.jsx', 'src/components/filings/FilingLockedRow.jsx', 'src/components/filings/FilingsFeed.jsx',
      'src/App.jsx', 'src/components/Header.jsx', 'src/pages/StockPage.jsx',
    ],
    vendorDeps: [],
  },

  {
    id: 'filing-alert-notification',
    sessionNumber: 'FILING-ALERT-NOTIFICATION',
    name: 'Filing Alert Notification \u2014 In-App Only, Watchlist/Active-View Triggered',
    status: 'planned',
    phase: 'Phase 5 \u2014 Screener Completion',
    items: [
      'Trigger: edgar_events write with significance >= 8 (already real-time via RSS poll \u2014 no new detection logic needed)',
      'Condition: ticker is in member watchlist OR member currently viewing that stock page',
      'Delivery: IN-APP ONLY \u2014 no email/SMS. Owner decision (confirmed explicitly this session): full alert build blocked on pipeline completion + real-time data subscription, NOT on missing infrastructure',
      'priceAlertCron.js / scanAlertCron.js pattern already exists and is the intended extension point once real-time pipeline data is live \u2014 this is NOT a build-from-scratch item',
      'Known pre-existing bug found in both cron files during SEC-FILINGS-PAGE research, unrelated to this item and NOT fixed here: both call signalWriter.log(...) but signalWriter.js only exports write() \u2014 alert_fired/alert_price_cluster signals currently silently fail to log, caught in try/catch, console-only fallback fires instead',
    ],
    prereqs: ['Congressional/EDGAR pipeline completion + validation', 'Real-time data subscription active'],
    doD: ['Not started \u2014 blocked per explicit owner decision, tracked here so it is not silently lost'],
    smokeChecks: [],
    files: ['backend/jobs/priceAlertCron.js', 'backend/jobs/scanAlertCron.js', 'backend/services/signalWriter.js'],
    vendorDeps: [],
  },

  {
    id: 'economic-calendar-fred',
    sessionNumber: 'ECONOMIC-CALENDAR-FRED',
    name: 'Economic Calendar \u2014 Replace Finnhub with FRED (separate pipeline)',
    status: 'planned',
    phase: 'Phase 5 \u2014 Screener Completion',
    items: [
      'Root cause confirmed this session: Calendar page (CalendarPage.jsx) Earnings + IPO tabs called wrong Finnhub URLs (/stock/earnings-calendar, /stock/ipo instead of the real /calendar/earnings, /calendar/ipo) \u2014 fixed as a same-session hotfix in stocks-seo.js, should work on free tier once verified live',
      'Economic tab (/calendar/economic) already used the correct Finnhub URL but still 500s \u2014 suspected (not confirmed) premium-tier gating; Finnhub has a documented history (GitHub issue) of moving previously-free endpoints behind paid plans',
      'Owner does not have a Finnhub subscription \u2014 decision: build a SEPARATE pipeline using FRED (Federal Reserve Economic Data) instead of paying for Finnhub premium',
      'FRED API key already provisioned in OCI Vault (OCID: ocid1.vaultsecret.oc1.iad.amaaaaaaskhpm4aaj2vabic4bctmuwi3vctdmwhqahx3f3qf3k3voodpn5fa) \u2014 env var name it loads as still needs to be confirmed before wiring',
      'CONFIRMED via FRED official docs: fred/releases/dates returns release name + scheduled date across ~290 tracked US releases (CPI, Employment Situation, GDP, Retail Sales, PPI, etc.) \u2014 free, no premium tier, FRED\u2019s own site calls this the "Economic Release Calendar"',
      'SCOPE TRADEOFFS vs what Finnhub/Yahoo show \u2014 must be decided explicitly, not assumed, before building: (1) US-only, no international events (FI/GB/SE etc.) \u2014 likely acceptable given this product is already US-equities-scoped; (2) no consensus "Market Expectation" \u2014 FRED gives actual released values + revision history only, not analyst forecasts, so an Estimate column would always be empty and should not be built; (3) no built-in impact rating (High/Med/Low) \u2014 would need a hand-built importance classification per release name, same pattern as edgarFormConfig.js\u2019s per-form significance scoring',
      'This is PIPELINE-TRACK work (new data source), not Screener-track \u2014 belongs in its own session with its own HANDOVER.md/TESTING.md/PRODUCT_BACKLOG.md updates, unlike SEC-FILINGS-PAGE',
      'SUPERSEDED IN SCOPE by CALENDAR-PIPELINE below (same session, expanded July 2026) \u2014 FRED-for-economic-events is now one data source within a broader batch calendar pipeline covering earnings + IPO + economic, not a standalone live-call replacement',
    ],
    prereqs: ['Confirm FRED Vault secret env var name', 'Decide US-only scope is acceptable', 'Decide impact-rating classification approach'],
    doD: ['Not started \u2014 flagged and tracked per owner instruction, not built inline this session'],
    smokeChecks: [],
    files: ['backend/routes/stocks-seo.js (calendar routes to be replaced or supplemented)', 'src/pages/CalendarPage.jsx'],
    vendorDeps: ['FRED (Federal Reserve Economic Data) \u2014 free, OCI Vault secret already provisioned'],
  },

  {
    id: 'calendar-pipeline',
    sessionNumber: 'CALENDAR-PIPELINE',
    name: 'Market Calendar \u2014 Batch Pipeline (Earnings + Economic + IPO), Replace Live Finnhub Calls',
    status: 'planned',
    phase: 'Phase 4 \u2014 Pipeline',
    items: [
      'Root cause, this session: CalendarPage.jsx\u2019s three tabs call stocks-seo.js live per page load \u2014 Earnings/IPO used wrong Finnhub URLs (fixed as hotfix), Economic uses the right URL but still fails (suspected premium gating, unconfirmed)',
      'NEW finding, this session: even after the URL fix, IPO now returns real data but Earnings (same fix, same pattern) still returns empty \u2014 the codebase\u2019s OWN existing per-ticker earnings route (stocks-seo.js, GET /:ticker/earnings) already calls /calendar/earnings WITH a symbol= filter and works in production; the bulk all-companies earnings-calendar (no symbol filter) may be gated differently on Finnhub free tier \u2014 not confirmed, worth checking Finnhub\u2019s response directly rather than assuming',
      'DECISION (owner, this session): stop relying on live third-party calendar calls per page load entirely \u2014 build a real batch pipeline, same architectural pattern as edgarDisplayPipeline.js/senatePipeline.js (Capture on a schedule \u2192 store in our own DB \u2192 serve frontend from indexed data), not another live-API patch',
      'Economic events specifically: use FRED (see superseded ECONOMIC-CALENDAR-FRED entry above for full detail \u2014 confirmed free fred/releases/dates endpoint, US-only scope, no consensus estimate, needs hand-built impact classification)',
      'Earnings + IPO: re-evaluate whether Finnhub free tier can supply bulk (non-symbol-scoped) calendar data at all, or whether a different free source is needed \u2014 do not assume Finnhub without checking the actual response first',
      'FUTURE ENHANCEMENTS \u2014 explicitly flagged by owner, NOT in scope for the initial pipeline build, to be added only after Earnings/Economic/IPO are solid: stock splits calendar; biotech FDA trial/approval phase calendar (PDUFA dates etc.) \u2014 Finnhub\u2019s own alternative-data offering includes an FDA approval calendar, worth checking free-tier access to that specifically when this phase starts, same caution as everything else Finnhub-related this session (verify before assuming)',
    ],
    prereqs: ['Confirm FRED Vault secret env var name (see ECONOMIC-CALENDAR-FRED)', 'Confirm real cause of Earnings bulk-query gap', 'Decide Earnings/IPO data source if Finnhub free tier cannot supply bulk data'],
    doD: ['Not started \u2014 flagged and tracked per owner instruction, not built inline this session'],
    smokeChecks: [],
    files: ['backend/routes/stocks-seo.js (calendar routes, to be replaced by pipeline-fed routes)', 'src/pages/CalendarPage.jsx', 'backend/jobs/ (new calendarPipeline.js, pattern TBD)'],
    vendorDeps: ['FRED (economic \u2014 free, provisioned)', 'Finnhub or alternative (earnings/IPO \u2014 source TBD)', 'Finnhub alternative data (FDA calendar \u2014 future, unverified)'],
  },

  {
    id: 'vm-production-issues-investigation',
    sessionNumber: 'VM-PRODUCTION-ISSUES-INVESTIGATION',
    name: 'Investigate real production issues found via pm2 logs (July 2026 doc-sync session)',
    status: 'planned',
    phase: 'Phase 4 \u2014 Pipeline',
    items: [
      'Found via real pm2 logs pulled on both VM-1 prod and dev during the NEWS-MULTI-SOURCE rss-parser install this session \u2014 NOT caused by anything built this session (clean boot sequence both times, no new errors from rss-parser or newsAggregatorService)',
      '"Aborted!" crashes tied to [signalWriter] ATP error: Socket \u2018secureConnect\u2019 timed out after ~10000ms (connectTimeoutMS: 10000) \u2014 confirmed on BOTH VM-1 prod and dev VMs, suggesting a real, recurring ATP connectivity issue, not a one-off',
      'Repeating Python ModuleNotFoundError: No module named \u2018oci_cli\u2019 traceback specifically on the dev VM (mp-vm1-scanner-dev) \u2014 some script attempting to invoke the oci CLI tool without the oci_cli Python module installed; not seen on prod',
      'Neither investigated further this session \u2014 flagged via real log evidence, not guessed',
    ],
    prereqs: ['Pull fresh pm2 logs on both VMs to confirm these are still occurring, not already resolved', 'Identify what is actually calling oci CLI on the dev VM and why prod does not hit the same path'],
    doD: ['Not started \u2014 flagged and tracked, not investigated this session'],
    smokeChecks: [],
    files: ['backend/services/signalWriter.js (ATP connection handling)', 'unknown script invoking oci CLI on dev VM \u2014 needs identification'],
    vendorDeps: ['Oracle ATP (connection timeout \u2014 real infra issue, not this session\u2019s code)'],
  },

  {
    id: 'review-console-p1p2',
    sessionNumber: 'REVIEW-CONSOLE-P1P2',
    name: 'Data Quality Review Console — Phase 1 & 2 (Pulze-repo backend, Ops-repo work NOT started)',
    status: 'done', // update commit hash below once apply-REVIEW-CONSOLE-P1P2.sh confirms the real push — PENDING as of this entry
    phase: 'Phase 5 — Screener Completion',
    date: 'July 2026',
    commit: 'PENDING — fill in after apply-REVIEW-CONSOLE-P1P2.sh confirms the real push output',
    items: [
      'New isolated Oracle schema MP_REVIEW_CONSOLE (CONNECT+RESOURCE only) — narrow-privileged, SEPARATE from oracleReadPool.js\u2019s pipeline credential. Confirmed live: negative test SELECT against MP_REFERENCE.SECTOR_MAPPING fails ORA-00942 as this user, proving isolation is real not assumed',
      'review_console_audit_log — owned directly by MP_REVIEW_CONSOLE (not MP_REFERENCE), confirmed live via real INSERT+ROLLBACK test',
      'review_queue_v FIXED (owned by MP_REFERENCE, pre-existing bug): AMENDMENT branch was ROWIDTOCHAR(ROWID) as ID — unstable placeholder, real PK confirmed live as REVIEWID (NUMBER NOT NULL, consistent across all 3 source tables) via all_tab_columns. Faked CAST(NULL) RESOLVEDAT replaced with the real column. RESOLVEDBY confirmed to genuinely not exist on AMENDMENT_REVIEW (no actor-identity column at all) \u2014 left NULL deliberately, not a remaining bug',
      'BONUS FIX, same session: RESOLVEDNOTE added as a 10th view column \u2014 exists on all 3 source tables (confirmed live) but was silently dropped by the pre-existing view entirely, caught before any Review Console code was built around the old 9-column shape',
      'linkedStaffId (Number, nullable) added to backend/models/User.js \u2014 links a Pulze test account to Marketpulze-ops\u2019s ADMIN_USERS.USER_ID, per PULZE-OPS-INTEGRATION-BRIDGE.md Section 9. Additive only, zero existing-account impact',
      'oracleReviewPool.js (NEW) \u2014 separate pool object AND separate Oracle credential from oracleReadPool.js, deliberately, so a bug in any member-facing route can never reach write access to congressional_trades',
      'internalAuth.js (NEW) \u2014 X-Internal-Key + X-Acting-User required on every /api/review-console/* route. No guest/member tier. Never added to server.js CORS allowlist \u2014 only ever called server-to-server by Marketpulze-ops\u2019s backend',
      'reviewConsoleService.js (NEW) \u2014 getQueue()/confirmOrReject() fully grounded against confirmed live schemas. updateProductionTrade()/insertManualTranscription() DELIBERATELY INCOMPLETE \u2014 refuse to run until CONGRESSIONAL_TRADES\u2019 full column list + valid STATUS values + remaining CONGRESSIONAL_TRADES_STAGING columns are confirmed live, not guessed',
      'review-console.js (NEW) route file, server.js updated (oracleReviewPool.init() as its own step, /api/review-console mounted, confirmed NOT in CORS array)',
      'Real bug found in apply-session-template.sh itself: SMOKE_ADDITIONS used a blind >> append, landing AFTER smoke-test.sh\u2019s own Final Results summary block \u2014 same bug class HEAD.md already recorded as fixed once during SEC-FILINGS-PAGE, but the checked-in template still had the naive version. Fixed via python3 insert-before-anchor (Rule 8 compliant) in this session\u2019s apply script \u2014 template itself still needs the permanent fix, tracked as a pending action below',
      'Real discrepancy found and flagged, not resolved: this file\u2019s smokeCount claims vs HEAD.md\u2019s vs a real sandbox-measured count all differed \u2014 see sessions.js PROJECT_SCOPE.smokeCount comment and HEAD.md for full detail',
      'Ops-side proxy route + PROJECT:REVIEW_* permission seeding + frontend tab NOT built this session \u2014 separate phase (3 & 4), pending owner confirmation of Ops-hosted frontend (PULZE-OPS-INTEGRATION-BRIDGE.md Section 8, open item #2)',
    ],
    prereqs: ['SCREENER-FRAG (oracleReadPool.js pattern) done \u2705', 'MP_REVIEW_CONSOLE Oracle schema created + grants confirmed live \u2705'],
    doD: [
      'All 6 changed/new JS files node --check clean (confirmed in sandbox this session)',
      'MP_REVIEW_CONSOLE negative-privilege test confirmed live (ORA-00942 on SECTOR_MAPPING)',
      'review_queue_v fix confirmed live (SELECT * FROM MP_REFERENCE.REVIEW_QUEUE_V returns real rows, RESOLVEDNOTE present)',
      'review_console_audit_log confirmed live (COUNT + real INSERT/ROLLBACK test)',
      'NOT YET DONE: real bash scripts/smoke-test.sh run against the actual repo (only a sandbox dry-run performed, missing most real files)',
      'NOT YET DONE: CI=true npm run build on the real repo',
      'NOT YET DONE: PULZE_INTERNAL_API_KEY + ATP_REVIEW_CONSOLE_CREDENTIALS generated and placed in real .env (vault migration deliberately deferred, tracked)',
    ],
    smokeChecks: [
      'test -f backend/services/oracleReviewPool.js',
      'test -f backend/middleware/internalAuth.js',
      'test -f backend/services/reviewConsoleService.js',
      'test -f backend/routes/review-console.js',
      'grep -q "MP_REVIEW_CONSOLE" backend/services/oracleReviewPool.js',
      'grep -q "x-internal-key" backend/middleware/internalAuth.js',
      'oracleReviewPool never imported by filings.js/search.js/screener.js/calculator.js (isolation-boundary regression guard)',
    ],
    files: [
      'backend/models/User.js', 'backend/services/oracleReviewPool.js', 'backend/middleware/internalAuth.js',
      'backend/services/reviewConsoleService.js', 'backend/routes/review-console.js', 'backend/server.js',
    ],
    vendorDeps: [],
  },

  {
    id: 'congress-page-redesign',
    sessionNumber: 'CONGRESS-PAGE-REDESIGN',
    name: 'Congress Page Redesign \u2014 Insights Tab, Member Detail Subpage, congress.js Backend/Frontend Split',
    status: 'done',
    phase: 'Phase 5 \u2014 Screener Completion',
    date: 'July 2026',
    commit: '41bce4c \u2014 confirmed via real git log -1 (dev, main, origin/HEAD all point here)',
    items: [
      'Formalized CONGRESSIONAL-PAGE-REDESIGN-DESIGN.md \u2014 peer comparison (Kadoa, Capitol Trades, TraderCongress, Quiver Quantitative, confirmed via live search not memory), phased feature scoping (buildable now vs. blocked on Stooq vs. blocked on congressionalSignalPipeline.js)',
      'Insights tab (NEW, 4th tab): top traded tickers (30/90/120d/YTD/all), top buy/sell volume, filing lag (overall + a Leaderboard ranking members by worst average lag, floored at 3+ trades to avoid a single-data-point false leader), and Bipartisan Convergence \u2014 same-ticker/opposite-party trades within a configurable day window',
      'Bipartisan Convergence explicitly labeled CLUSTER CONTEXT, never a trading signal \u2014 the real congressional signal still gates on congressionalSignalPipeline.js (not yet built, per PIPELINE-RELOAD sequencing); this distinction is stated in both the backend route comments and the frontend badge copy, not just one or the other',
      'Member Detail Subpage (CongressMemberPage.jsx, route /congress/member/:bioguideId?chamber=) \u2014 routed page, NOT a modal, matching the existing /stocks/:ticker per-entity detail pattern. Shows photo (bioguide-keyed unitedstates/images URL, zero new pipeline/column), district/state/party/tenure/committee-chair/website/phone/office, trade activity summary, full trade history (exact composite-key match, not the fragile name-LIKE /trades?member= filter), committee assignments, term history, and an honest \u201ccoming soon\u201d placeholder for Phase 2 stock-performance (gated on the Stooq pipeline)',
      'Wired in from all three original tabs (Trade Feed member name, Member Profiles row, Committee View roster) \u2014 previously a member name appeared in three places on this page and led nowhere in any of them; a real gap (App.jsx never passed onNavigate to CongressPage.jsx at all) was also found and fixed during this wiring',
      'congress.js RETIRED (709 lines, grown too large to safely edit under Gate 2) and split into congressTrades.js (Trade Feed), congressMembers.js (Member Profiles + Committee View \u2014 kept together, genuinely coupled by the same composite-PK discipline), congressInsights.js, plus a new congressShared.js helpers module (constants + soft-auth) \u2014 explicit owner decision to use a shared module here rather than the admin.js/requireReviewConsoleAccess.js duplication precedent, since three-plus files on a still-growing feature surface is a different risk shape than that precedent\u2019s narrow one-off case',
      'Same shared-module decision applied to the frontend: CongressPage.jsx reduced to a thin shell (header + tab nav only), its 3 original tabs extracted into src/components/congressional/{TradeFeedTab,MemberProfilesTab,CommitteeViewTab}.jsx plus the new InsightsTab.jsx, with a matching congressShared.jsx for shared constants/helpers \u2014 matches this codebase\u2019s own existing ReviewConsoleTab.jsx/TradeBrowserTab.jsx (Ops repo) multi-tab precedent rather than inventing a new pattern',
      'Committee View\u2019s real, confirmed gap closed: /committees now returns a per-member roster (name, party, role) alongside its existing rollup counts, not just aggregate numbers with no names reachable at all',
      'Trade Feed: member name is now a link (BIOGUIDEID added to the /trades SELECT for this), OWNER shown with its real conviction-multiplier explanation on hover, and a Bipartisan Convergence badge (coarse ticker-level approximation, flagged as such, not a per-row exact match)',
      'TWO REAL BUGS found only on this session\u2019s first actual live run against the real repo, neither caught in any sandbox dry-run: (1) smoke-test.sh\u2019s own count-consistency self-check sat BEFORE the 7 oracleReviewPool checks at the file\u2019s end, so ACTUAL_TOTAL structurally could never include them \u2014 undercounted by exactly 7 every run; confirmed via a real run (662 claimed vs. 655 actual), fixed by moving the self-check to the true end of the file. (2) CI=true npm run build caught two real ESLint no-unused-vars errors (guestLimit in TradeFeedTab.jsx and CongressMemberPage.jsx) \u2014 dead state that existed in the ORIGINAL pre-split CongressPage.jsx too, never caught before because that code path had never been through a real CI build until this session',
      'smoke-test.sh: 12 stale congress.js-referencing checks retired (file no longer exists at that path) and replaced with 44 checks against the real current shape; oracleReviewPool isolation guard updated to list the 3 new route files. Net +32 checks, mechanically recounted via grep, not hand-tracked. sessions.js smokeCount updated 630/630 \u2192 662/662 to match',
      'D3: none this session \u2014 Product 1 UI/data-quality work only, zero signalWriter calls',
    ],
    prereqs: ['CONGRESSIONAL-REVIEW-AND-MEMBER-PAGE-ARC (original 3-tab build) done \u2705', 'REVIEW-CONSOLE-P1P2 pattern (oracleReadPool-only, isolated pool) established \u2705'],
    doD: [
      'bash scripts/smoke-test.sh run for real against the actual repo: 662 passed, 0 failed, SAFE TO DEPLOY \u2014 confirmed via real owner-pasted terminal output, not a sandbox dry-run',
      'CI=true npm run build clean \u2014 confirmed after fixing the 2 real ESLint no-unused-vars errors found on the first real build attempt',
      'git log -1 confirmed: commit 41bce4c on dev, main, and origin/HEAD',
      'All new/changed JS files node --check clean; all new/changed JSX files structurally validated via @babel/parser before delivery',
    ],
    smokeChecks: [
      'test -f backend/routes/congressTrades.js',
      'test -f backend/routes/congressMembers.js',
      'test -f backend/routes/congressInsights.js',
      'test -f backend/utils/congressShared.js',
      '! test -f backend/routes/congress.js',
      'test -f src/components/congressional/TradeFeedTab.jsx',
      'test -f src/components/congressional/MemberProfilesTab.jsx',
      'test -f src/components/congressional/CommitteeViewTab.jsx',
      'test -f src/components/congressional/InsightsTab.jsx',
      'test -f src/pages/CongressMemberPage.jsx',
    ],
    files: [
      'backend/routes/congressTrades.js', 'backend/routes/congressMembers.js', 'backend/routes/congressInsights.js',
      'backend/utils/congressShared.js', 'backend/server.js',
      'src/components/congressional/congressShared.jsx', 'src/components/congressional/MemberPhoto.jsx',
      'src/components/congressional/TradeFeedTab.jsx', 'src/components/congressional/MemberProfilesTab.jsx',
      'src/components/congressional/CommitteeViewTab.jsx', 'src/components/congressional/InsightsTab.jsx',
      'src/pages/CongressPage.jsx', 'src/pages/CongressMemberPage.jsx', 'src/App.jsx',
      'scripts/smoke-test.sh', 'src/data/sessions.js',
    ],
    vendorDeps: [],
  },

  {
    id: 'congress-page-controls',
    sessionNumber: 'CONGRESS-PAGE-CONTROLS + CONGRESS-PAGE-POLISH',
    name: 'Congress Page \\u2014 Shared ConFilterBar, Window/Status Filters, Smoke-Test Self-Check Permanent Fix',
    status: 'done',
    phase: 'Phase 5 \\u2014 Screener Completion',
    date: 'July 2026',
    commit: '2b191d7 (3 commits: 98bb6f2, 1e7f6aa, 2b191d7) \\u2014 confirmed via real owner-pasted git output',
    items: [
      'New shared ConFilterBar.jsx/ConPaginationFooter component \\u2014 owner request: one consistent filter+pagination UX across every tab, injectable into any future one, rather than each tab hand-building its own filter row. Every control opt-in via explicit show* boolean props.',
      'Window (30/90/120/ytd/all) and status (active/inactive/all) filters added to Trade Feed (congressTrades.js) and Insights (congressInsights.js, all 4 endpoints) \\u2014 REAL, default-changing behavior: both now default to status=active, window=ytd instead of showing everything since 2020 regardless of member status. Flagged explicitly in code comments, not a silent change.',
      'Member Profiles\\u2019 own window filter \\u2014 REVERSED an earlier judgment call from this same arc (first built as deliberately excluded, reasoning \\u201cno clean meaning for a member list\\u201d). Owner corrected this directly (\\u201cthat needed\\u201d) \\u2014 real interpretation built: an EXISTS subquery against congressional_trades correlated on the composite key, showing only members who traded within the window.',
      'CongressMemberPage.jsx width fix \\u2014 removed an invented, one-off maxWidth: 900px cap in favor of the site\\u2019s own established s.container policy (shared.js, clamp(1400px, 96vw, 2000px)), after the owner reported the page using under 60% of screen width.',
      'Committee View deliberately left without window/status \\u2014 owner-confirmed acceptable this session, not an oversight.',
      'REAL BUG #1: a cross-batch dependency miss. The work was first split into a \\u201cfeat\\u201d batch (new ConFilterBar.jsx + its 3 dependent tab files) and a \\u201chotfix\\u201d batch (independent bug fixes) to match the right template to the right risk level \\u2014 but congressShared.jsx\\u2019s DEFAULT_PAGE_SIZE export was assigned to the wrong batch, breaking the feat batch\\u2019s real CI=true npm run build (Attempted import error: \\u2018DEFAULT_PAGE_SIZE\\u2019 is not exported). Fixed by mechanically checking every import against every actual export across all 5 files \\u2014 not re-reading the dependency graph by eye a second time, which is exactly what missed it the first time.',
      'REAL BUG #2, RECURRING: smoke-test.sh\\u2019s count-consistency self-check had the SAME bug class fixed once already (for a different trailing block, last session) \\u2014 every insertion mechanism anchored new checks to \\u201cbefore Final Results,\\u201d but the self-check itself sits right there too, so anything newly inserted landed AFTER the self-check ran and could never be counted. Confirmed on a real run: 662 claimed vs. 667 actual after 5 checks were added by the owner\\u2019s own apply-script run. Fixed PERMANENTLY this time, not patched around again: a dedicated anchor comment (\\u201cNEW CHECKS INSERT ABOVE THIS LINE\\u201d) now sits directly before the self-check block in smoke-test.sh itself, and apply-session-template.sh\\u2019s insertion logic targets that anchor instead of \\u201cFinal Results\\u201d \\u2014 verified by actually running the insertion mechanism against a real copy of the file and checking line numbers, not just re-reading the code.',
      'Related smaller mistake, caught the same way: a new smoke check asserted CongressMemberPage.jsx no longer contains maxWidth: \\u2018900px\\u2019 \\u2014 but the fix\\u2019s own explanatory comment restates that exact string when describing what was removed, so the check FALSE-FAILED against its own documentation. Fixed by checking positively for the corrected line instead of negatively for the old one.',
      'D3: none this session \\u2014 Product 1 UI/data-quality work only, zero signalWriter calls',
    ],
    prereqs: ['CONGRESS-PAGE-REDESIGN (Insights tab, Member Detail Subpage, congress.js split) done \\u2705'],
    doD: [
      'bash scripts/smoke-test.sh run for real against the actual repo: 675 passed, 0 failed, SAFE TO DEPLOY \\u2014 confirmed via real owner-pasted terminal output, matching exactly what was predicted before the run',
      'CI=true npm run build clean \\u2014 confirmed after fixing the cross-batch DEFAULT_PAGE_SIZE export miss found on the first real build attempt',
      'git log -1 confirmed: commit 2b191d7 on dev and main (3 commits total this arc: 98bb6f2, 1e7f6aa, 2b191d7)',
      'Every import in every changed file mechanically checked against every actual export \\u2014 not just node --check/babel-parse, which only prove syntax, not that a named import actually resolves',
      'smoke-test.sh insertion-anchor fix verified by actually running the insertion against a real copy of the file and confirming line ordering, not just re-reading the awk logic',
    ],
    smokeChecks: [
      'test -f src/components/congressional/ConFilterBar.jsx',
      'grep -q ConFilterBar src/components/congressional/TradeFeedTab.jsx',
      'grep -q ConFilterBar src/components/congressional/MemberProfilesTab.jsx',
      'grep -q ConFilterBar src/components/congressional/CommitteeViewTab.jsx',
      'grep -q INACTIVE_CONGRESS_MEMBERS backend/routes/congressMembers.js',
      'grep -q resolveCutoff backend/routes/congressTrades.js',
      'grep -q windowCutoff backend/routes/congressMembers.js',
      'grep -q WINDOW_OPTIONS src/components/congressional/ConFilterBar.jsx',
      'grep -q showWindow src/components/congressional/MemberProfilesTab.jsx',
    ],
    files: [
      'backend/routes/congressTrades.js', 'backend/routes/congressInsights.js', 'backend/routes/congressMembers.js',
      'src/components/congressional/ConFilterBar.jsx', 'src/components/congressional/TradeFeedTab.jsx',
      'src/components/congressional/MemberProfilesTab.jsx', 'src/components/congressional/InsightsTab.jsx',
      'src/pages/CongressMemberPage.jsx',
      'scripts/smoke-test.sh', 'src/data/sessions.js', 'tasks/apply-session-template.sh',
    ],
    vendorDeps: [],
  },

];  // end ACTIVE_SESSIONS
